import React, { useEffect, useLayoutEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  Button,
  Pagination,
  Snackbar,
  Alert,
  FormControl,
  Checkbox,
  Radio,
  Select,
  MenuItem,
  Chip,
  Stack,
  Tooltip,
  Autocomplete,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Skeleton,
  IconButton,
  Menu,
  Avatar,
  Popover,
  InputAdornment,
  InputLabel,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  RadioGroup,
  Slider,
} from '@mui/material';
import { FileDownload, MoreVert, Psychology, Refresh as RefreshIcon, ChevronLeft, ChevronRight, CalendarMonth, Close, AccessTime, Login, Logout, Timeline, Schedule, TrendingUp, Person, EventAvailable, EventBusy, UploadFile, CameraAlt, PhotoCamera, CloudUpload, ArrowUpward, ArrowDownward, ArrowDropDown, Add as AddIcon, Remove as RemoveIcon, RestartAlt as RestartAltIcon, Block as BlockIcon, ErrorOutline as ErrorOutlineIcon, Search, AssignmentInd as AssignmentIndIcon, HowToReg as HowToRegIcon, Badge as BadgeIcon, FilterList, SmartToy } from '@mui/icons-material';
import { Zoom, Fade, Slide, Grow, useMediaQuery, useTheme } from '@mui/material';
import PageHeader from './PageHeader';
import ClearAllFiltersButton from './ClearAllFiltersButton';
import {
  pageFilterActionButtonSx,
  pageFilterAutocompleteSx,
  pageFilterDatePickerSx,
  pageFilterFormControlSx,
  pageFiltersActionsSx,
  pageFiltersControlsSx,
  pageFiltersStackSx,
  pageFiltersToolbarSx,
} from '../utils/pageFilterStyles';
import {
  createTodayDateRange,
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
  isDefaultDateTimeRange,
} from '../utils/filterDefaults';
import AuditFindPersonDialogs from './AuditFindPersonDialogs';
import AuditDialogs from './AuditDialogs';
import { AuditRecordingPlayButton, isAuditRecordingReady } from './AuditRecordingPlayButton';
import { AttendanceGridLayout } from './AuditAttendanceGridLayout';
import { DateRangePicker, Calendar } from 'react-date-range';
import { format, addDays, startOfDay, endOfDay, isSameDay } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import { getAuditLogs, getAuditImageBlob, exportAuditLogs, getAttendanceSummary, exportAttendanceLogs, getUniqueAuditNames, addAuditImageToEmployee, getPersonFaces, getFaceImage, getAttendanceImageBlob, matchAuditImage, refreshTopMatchers, getLateEntryTime, fetchHrmsEmployees, getCompanyPickerConfig, markAuditAttendance, markAttendance, startUnknownMatchJob, getUnknownMatchJob, formatUnknownMatchLogsForExport, findPersonCombined, unmatchToMatch, deleteAttendanceLog, revokeAttendanceByAudit, startVisionReviewJob, getVisionReviewJob, startVisionReviewBatchJob, getVisionReviewBatchJob, getCameraOptions, getPersonSuggestions, getPersonByPersonId } from '../utils/api';
import { hydratePossibleMatchLogFromDetail, hydrateTimingDialogFromDetail } from '../utils/auditTimingDialog';
import { formatDateWithSeconds, formatDate, formatDateOnly } from '../utils/dateUtils';
import { filterPersonNameOptions, formatPersonNameOptionLabel, sanitizeNameSearchInput } from '../utils/nameSearchInput';
import { logShowsVendorBadge, logShowsVisitorBadge } from '../utils/employeeTypeUi';
import { keyframes } from '@mui/system';
import { EmployeeNameHyperlink } from './EmployeeNameHyperlink';
import { buildPersonLinkMeta } from '../utils/personDetailsNavigation';
import { getAttendanceMarkedByLabel, hasAttendanceMarkedBy, getAiReviewChipLabel, getAttendanceRevokedReopenHint, getVisionReviewFromLog, getVisionReviewExecutorInfo } from '../utils/attendanceDisplay';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { useAuth } from '../contexts/AuthContext';
import useAuditImage from '../hooks/useAuditImage';
import useAuditImagePrefetch from '../hooks/useAuditImagePrefetch';
import { peek as peekAuditImage, getOrFetch as fetchAuditImage } from '../services/auditImageCache';
import * as XLSX from 'xlsx';
import { CircularProgressIcon } from './CircularProgressIcon';
import { PersonSearch } from './PersonSearch';

const primary = '#40518A';
const UNKNOWN_MATCH_JOB_STORAGE_KEY = 'audit_unknown_match_job_id';
const searchingBorderGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(64, 81, 138, 0.20);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(64, 81, 138, 0.10);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(64, 81, 138, 0.20);
  }
`;

// Anonymous image for manual attendance and loading states
const ANONYMOUS_IMAGE = '/anonymous.webp';
const RECORDING_PLAY_TRIGGER_SELECTOR = '[data-recording-play-trigger="true"]';
const RECORDING_CLICK_GUARD_UNTIL_KEY = '__recordingClickGuardUntil';

const isRecordingPlayInteraction = (event) => {
  const target = event?.target;
  if (target && typeof target.closest === 'function' && target.closest(RECORDING_PLAY_TRIGGER_SELECTOR)) {
    return true;
  }
  if (typeof window !== 'undefined') {
    const ignoreUntil = Number(window[RECORDING_CLICK_GUARD_UNTIL_KEY] || 0);
    if (Number.isFinite(ignoreUntil) && ignoreUntil > Date.now()) return true;
  }
  return false;
};

// Shimmer animation keyframes
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Shimmer effect styles
const shimmerStyles = {
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: `${shimmer} 1.5s ease-in-out infinite`,
};

// Attendance Card Skeleton Component
export const AttendanceCardSkeleton = () => (
  <Card
    sx={{
      borderRadius: 3,
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      {/* Header with avatar and name */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            ...shimmerStyles,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ width: '60%', height: 20, borderRadius: 1, mb: 0.5, ...shimmerStyles }} />
          <Box sx={{ width: '40%', height: 14, borderRadius: 1, ...shimmerStyles }} />
        </Box>
      </Stack>

      {/* Status chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ width: 70, height: 24, borderRadius: 12, ...shimmerStyles }} />
        <Box sx={{ width: 80, height: 24, borderRadius: 12, ...shimmerStyles }} />
        <Box sx={{ width: 90, height: 24, borderRadius: 12, ...shimmerStyles }} />
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Recent Activity header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ width: 100, height: 12, borderRadius: 1, ...shimmerStyles }} />
        <Stack direction="row" spacing={0.5}>
          <Box sx={{ width: 28, height: 28, borderRadius: 1, ...shimmerStyles }} />
          <Box sx={{ width: 40, height: 28, borderRadius: 1, ...shimmerStyles }} />
          <Box sx={{ width: 28, height: 28, borderRadius: 1, ...shimmerStyles }} />
        </Stack>
      </Stack>

      {/* Activity tiles */}
      <Stack direction="row" spacing={1.2}>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ flex: 1 }}>
            <Box sx={{ width: '100%', height: 100, borderRadius: 2, mb: 1, ...shimmerStyles }} />
            <Box sx={{ width: '70%', height: 14, borderRadius: 1, mb: 0.5, ...shimmerStyles }} />
            <Box sx={{ width: '50%', height: 12, borderRadius: 1, ...shimmerStyles }} />
          </Box>
        ))}
      </Stack>
    </CardContent>
  </Card>
);

// Logs Tile Skeleton Component
const LogsTileSkeleton = () => (
  <Card
    sx={{
      width: '100%',
      borderRadius: 2,
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    {/* Image skeleton - aspect ratio container */}
    <Box sx={{ width: '100%', paddingTop: '100%', position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0, ...shimmerStyles }} />
    </Box>

    <CardContent sx={{ p: 1.5 }}>
      {/* Name and status */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Box sx={{ width: '50%', height: 18, borderRadius: 1, ...shimmerStyles }} />
        <Box sx={{ width: 60, height: 22, borderRadius: 11, ...shimmerStyles }} />
      </Stack>

      {/* Time and camera info */}
      <Box sx={{ width: '70%', height: 14, borderRadius: 1, mb: 0.5, ...shimmerStyles }} />
      <Box sx={{ width: '40%', height: 12, borderRadius: 1, ...shimmerStyles }} />
    </CardContent>
  </Card>
);

// Helper function to get default date range (last month)
const getDefaultDateRange = () => {
  const now = new Date();
  const oneMonthAgo = new Date(now);
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

  // Format for datetime-local input (YYYY-MM-DDTHH:mm)
  const formatForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return {
    start: formatForInput(oneMonthAgo),
    end: formatForInput(now)
  };
};

const mapDashboardFilterToStatus = (filterValue) => {
  const normalizedFilter = filterValue?.toLowerCase();
  if (normalizedFilter === 'match') return 'match';
  if (normalizedFilter === 'possible_match') return 'possible_match';
  if (normalizedFilter === 'unmatch' || normalizedFilter === 'unknown' || normalizedFilter === 'no_match') return 'unmatch';
  if (normalizedFilter === 'failed') return 'failed';
  if (normalizedFilter === 'validity_expired' || normalizedFilter === 'expired') return 'validity_expired';
  if (normalizedFilter === 'ai_reviewed') return 'ai_reviewed';
  return '';
};

const VALID_VISION_REVIEW_RESULTS = ['ai_matched', 'ai_not_matched', 'pending_review', 'manually_resolved', 'total_possible_matches'];

const VISION_REVIEW_RESULT_LABELS = {
  ai_matched: 'AI Matched',
  ai_not_matched: 'AI Not Matched',
  pending_review: 'Pending Review',
  manually_resolved: 'Manually Resolved',
  total_possible_matches: 'Total Possible Matches',
};

const visionReviewResultLabel = (value) => {
  const key = String(value || '').trim().toLowerCase();
  if (!key) return 'All';
  return VISION_REVIEW_RESULT_LABELS[key] || key;
};

const auditLogTypeParams = (value) => {
  const v = String(value || '').trim().toLowerCase();
  if (v === 'intrusion') return { is_intrusion: true, is_vendor: null, is_visitor: null };
  if (v === 'vendor') return { is_intrusion: false, is_vendor: true, is_visitor: false };
  if (v === 'visitor') return { is_intrusion: false, is_vendor: false, is_visitor: true };
  if (v === 'attendance') return { is_intrusion: false, is_vendor: false, is_visitor: false };
  return { is_intrusion: null, is_vendor: null, is_visitor: null };
};

const auditLogTypeLabel = (value) => {
  const v = String(value || '').trim().toLowerCase();
  if (v === 'intrusion') return 'Intrusion';
  if (v === 'vendor') return 'Vendor';
  if (v === 'visitor') return 'Visitor';
  if (v === 'attendance') return 'Employee';
  return 'All';
};

const STACK_WINDOW_MS = 45000;
const DEFAULT_ADVANCED_TOTAL_TIME_MS = 200;
const MAX_STACK_PREVIEW = 3;
const OTHER_COMPANY_OPTION_VALUE = '__OTHER_COMPANY__';
const NULL_COMPANY_FILTER_VALUE = '__NULL_COMPANY__';

const getLogTimestampMs = (log) => {
  const ts = log.captured_at || log.created_at;
  if (!ts) return Number.NaN;
  const parsed = Date.parse(ts);
  return Number.isNaN(parsed) ? Number.NaN : parsed;
};

/** Local calendar day for a log (notification deep-link date picker sync). */
const getLogEventDate = (log) => {
  const tsMs = getLogTimestampMs(log);
  if (Number.isNaN(tsMs)) return null;
  return startOfDay(new Date(tsMs));
};

const isAttendanceSent = (value) =>
  value === true || value === 1 || value === '1' || value === 'true';

/** Show manual mark icon only for matched buckets where attendance was not sent (not Possible Match). */
const shouldShowBucketManualMarkIcon = (topLog, bucketLogs = []) => {
  if (!topLog?.audit_id) return false;
  if (topLog.manual_attendance) return false;
  const metadata = getLogRequestMetadata(topLog);
  if (metadata?.manual_attendance) return false;

  if (topLog.confidence_level === 'possible_match') return false;

  const isMatchedTile =
    Boolean(topLog.success) && topLog.confidence_level !== 'possible_match';
  if (!isMatchedTile) return false;

  const bucketHasAttendance =
    (Array.isArray(bucketLogs) ? bucketLogs : []).some((entry) =>
      isAttendanceSent(entry?.send_attendance)
    ) || isAttendanceSent(topLog?.send_attendance);

  return !bucketHasAttendance;
};

const resolveBucketManualMarkPersonDefaults = (topLog) => {
  if (!topLog) return { personId: '', personName: '', employeeCode: '' };
  const recognizedId = topLog.recognized_person_id
    ? String(topLog.recognized_person_id).trim()
    : '';
  if (recognizedId) {
    return {
      personId: recognizedId,
      personName: topLog.recognized_name || '',
      employeeCode: topLog.employee_code || '',
    };
  }
  if (topLog.confidence_level === 'possible_match') {
    const metadata = getLogRequestMetadata(topLog);
    const topCandidate = Array.isArray(metadata?.top_candidates)
      ? metadata.top_candidates[0]
      : null;
    if (topCandidate?.person_id) {
      return {
        personId: String(topCandidate.person_id).trim(),
        personName: topCandidate.name || '',
        employeeCode: topCandidate.employee_code || topLog.employee_code || '',
      };
    }
  }
  return { personId: '', personName: '', employeeCode: '' };
};

const getLogRequestMetadata = (log) => {
  if (!log) return {};
  const raw = log.request_metadata;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (e) {
      return {};
    }
  }
  return {};
};

const getPhysicalCameraLabel = (log) => {
  const detail = log?.camera_detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const fromDetail = String(detail.camera_label || '').trim();
    if (fromDetail) return fromDetail;
  }
  const metadata = getLogRequestMetadata(log);
  return String(metadata.camera_label || '').trim();
};

const getAttendanceRefFromLog = (log) => {
  const metadata = getLogRequestMetadata(log);
  const topLevelManualAttendance = log?.manual_attendance && typeof log.manual_attendance === 'object'
    ? log.manual_attendance
    : null;
  const manualAttendance = metadata.manual_attendance && typeof metadata.manual_attendance === 'object'
    ? metadata.manual_attendance
    : null;
  const attendanceMeta = metadata.attendance && typeof metadata.attendance === 'object'
    ? metadata.attendance
    : null;
  const attendanceRevocation = metadata.attendance_revocation && typeof metadata.attendance_revocation === 'object'
    ? metadata.attendance_revocation
    : null;

  const attendanceId =
    (topLevelManualAttendance && topLevelManualAttendance.attendance_id) ||
    (manualAttendance && manualAttendance.attendance_id) ||
    (attendanceMeta && attendanceMeta.attendance_id) ||
    log?.attendance_id ||
    metadata.attendance_id ||
    null;

  const attendanceActive = isAttendanceSent(log?.send_attendance);
  const revoked = !attendanceActive && Boolean(
    log?.revoked === true ||
    (attendanceMeta && attendanceMeta.revoked) ||
    (attendanceRevocation && attendanceRevocation.revoked) ||
    metadata.attendance_revoked
  );
  const revokedByName =
    (attendanceMeta && attendanceMeta.revoked_by_name) ||
    (attendanceRevocation && attendanceRevocation.revoked_by_name) ||
    metadata.attendance_revoked_by_name ||
    null;

  return {
    attendanceId: attendanceId ? String(attendanceId) : null,
    revoked,
    revokedByName: revokedByName ? String(revokedByName) : null,
  };
};

const resolveBucketAttendanceRef = (representativeLog, bucketLogs = []) => {
  const entries = (Array.isArray(bucketLogs) ? bucketLogs : []).filter(Boolean);
  if (representativeLog) entries.unshift(representativeLog);

  const hasAttendance = entries.some((item) => isAttendanceSent(item?.send_attendance));
  const revoked = !hasAttendance && entries.some((item) => getAttendanceRefFromLog(item).revoked);
  const prioritized = [...entries].sort((a, b) => Number(isAttendanceSent(b?.send_attendance)) - Number(isAttendanceSent(a?.send_attendance)));
  const linked = prioritized
    .map((item) => ({ item, ref: getAttendanceRefFromLog(item) }))
    .find(({ ref }) => Boolean(ref.attendanceId));

  return {
    attendanceId: linked?.ref?.attendanceId || null,
    sourceAuditId: linked?.item?.audit_id || representativeLog?.audit_id || null,
    hasAttendance,
    revoked,
  };
};

const getTrackBucketKey = (log) => {
  const trackId = log.track_id ? String(log.track_id) : 'track';
  const camera = (log.camera || 'unknown').toString().toLowerCase();
  const timestampMs = getLogTimestampMs(log);
  const bucketIndex = Number.isNaN(timestampMs) ? '0' : Math.floor(timestampMs / STACK_WINDOW_MS);
  return `${trackId}:${camera}:${bucketIndex}`;
};

const groupLogsByTrackBuckets = (logs) => {
  const sourceLogs = Array.isArray(logs) ? logs : [];
  const buckets = new Map();
  sourceLogs.forEach((log) => {
    const key = getTrackBucketKey(log);
    const bucket = buckets.get(key) || [];
    bucket.push(log);
    buckets.set(key, bucket);
  });
  return Array.from(buckets.entries())
    .map(([bucketKey, bucketLogs]) => ({
      bucketKey,
      logs: bucketLogs.sort((a, b) => {
        const aMs = getLogTimestampMs(a) || 0;
        const bMs = getLogTimestampMs(b) || 0;
        return bMs - aMs;
      }),
    }))
    .sort((a, b) => {
      const aMs = getLogTimestampMs(a.logs[0]) || 0;
      const bMs = getLogTimestampMs(b.logs[0]) || 0;
      return bMs - aMs;
    });
};

const getStackLogKey = (log) => {
  if (!log) return null;
  if (log.audit_id) return `audit:${String(log.audit_id)}`;
  const timestamp = log.captured_at || log.created_at || '';
  const camera = (log.camera || '').toString().toLowerCase();
  return `fallback:${String(log.track_id || 'track')}:${camera}:${String(timestamp)}`;
};

const buildPrioritizedStackImages = (bucketLogs, representativeLog, imageCache) => {
  const logs = Array.isArray(bucketLogs) ? bucketLogs : [];
  const orderedLogs = [];
  const seen = new Map();

  const addLog = (log) => {
    const key = getStackLogKey(log);
    if (!key) return;
    if (seen.has(key)) {
      const existingIndex = seen.get(key);
      orderedLogs[existingIndex] = {
        ...orderedLogs[existingIndex],
        ...log,
      };
      return;
    }
    seen.set(key, orderedLogs.length);
    orderedLogs.push(log);
  };

  addLog(representativeLog);
  logs.forEach(addLog);

  return orderedLogs.slice(0, MAX_STACK_PREVIEW).map((stackLog) => ({
    log: stackLog,
    image: stackLog.audit_id ? imageCache[stackLog.audit_id] : null,
  }));
};

const hydrateRepresentativeLog = (representativeLog, bucketLogs) => {
  if (!representativeLog || !Array.isArray(bucketLogs) || bucketLogs.length === 0) {
    return representativeLog;
  }

  const representativeKey = getStackLogKey(representativeLog);
  if (!representativeKey) return representativeLog;

  const matchingBucketLog = bucketLogs.find((bucketLog) => getStackLogKey(bucketLog) === representativeKey);
  if (!matchingBucketLog) return representativeLog;

  return {
    ...representativeLog,
    ...matchingBucketLog,
  };
};

/**
 * When a bucket contains multiple captured frames, attendance may be marked on a different
 * frame than the one chosen as the "representative" by the backend. In that case, we should
 * show the attendance-marked frame image on the tile (and as the top stack layer) so the
 * image matches the "Attendance" chip.
 */
const pickAttendanceMarkedLogForDisplay = (representativeLog, bucketLogs = []) => {
  const entries = [representativeLog, ...(Array.isArray(bucketLogs) ? bucketLogs : [])].filter(Boolean);
  if (!entries.length) return representativeLog || null;

  const scored = entries.map((item) => {
    const ref = getAttendanceRefFromLog(item);
    const ts = getLogTimestampMs(item);
    return {
      item,
      ts: Number.isNaN(ts) ? Number.NEGATIVE_INFINITY : ts,
      hasAttendanceId: Boolean(ref.attendanceId),
      hasAttendanceSent: isAttendanceSent(item?.send_attendance),
    };
  });

  const attendanceCandidates = scored.filter((s) => s.hasAttendanceId || s.hasAttendanceSent);
  if (!attendanceCandidates.length) return representativeLog || entries[0];

  // Prefer logs that are linked to an attendance id (works for revoked attendance too), then
  // ones that actually sent attendance, and finally the most recent timestamp.
  attendanceCandidates.sort((a, b) => {
    const byId = Number(b.hasAttendanceId) - Number(a.hasAttendanceId);
    if (byId) return byId;
    const bySent = Number(b.hasAttendanceSent) - Number(a.hasAttendanceSent);
    if (bySent) return bySent;
    return b.ts - a.ts;
  });

  return attendanceCandidates[0].item;
};

const Audit = ({ view, embedded = false }) => {
  const { confirm } = useConfirmDialog();
  const location = useLocation();
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeNotificationAuditId = useMemo(
    () => (new URLSearchParams(location.search).get('audit_id') || '').trim(),
    [location.search]
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  // Determine active view from explicit page wrapper, with pathname fallback for legacy routes.
  const resolvedView = useMemo(() => {
    if (view === 'logs' || view === 'present') {
      return view;
    }
    if (
      location.pathname === '/audit' ||
      location.pathname === '/logs/audit' ||
      location.pathname === '/in-out'
    ) {
      return 'logs';
    }
    return 'present';
  }, [view, location.pathname]);
  const isLogsView = resolvedView === 'logs';

  const parseDateParam = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const normalizeTimeParam = (value, fallback) => {
    if (!value || typeof value !== 'string' || !value.includes(':')) return fallback;
    const [hoursRaw, minutesRaw] = value.split(':');
    const hours = Number(hoursRaw);
    const minutes = Number(minutesRaw);
    if (
      Number.isNaN(hours) ||
      Number.isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return fallback;
    }
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const initialLogsName = sanitizeNameSearchInput(searchParams.get('logs_name') || '');
  const initialLogsCamera = ['in', 'out'].includes((searchParams.get('logs_camera') || '').toLowerCase())
    ? (searchParams.get('logs_camera') || '').toLowerCase()
    : '';
  const initialLogsStatus = ['match', 'possible_match', 'unmatch', 'failed', 'validity_expired', 'ai_reviewed'].includes((searchParams.get('logs_status') || '').toLowerCase())
    ? (searchParams.get('logs_status') || '').toLowerCase()
    : '';
  const initialVisionReviewResult = VALID_VISION_REVIEW_RESULTS.includes((searchParams.get('vision_review_result') || '').toLowerCase())
    ? (searchParams.get('vision_review_result') || '').toLowerCase()
    : '';
  const initialLogsType = ['attendance', 'vendor', 'visitor', 'intrusion'].includes((searchParams.get('logs_type') || '').toLowerCase())
    ? (searchParams.get('logs_type') || '').toLowerCase()
    : '';
  const initialLogsStartDate =
    parseDateParam(searchParams.get('logs_start_date')) ||
    parseDateParam(searchParams.get('start_date')) ||
    new Date();
  const initialLogsEndDate =
    parseDateParam(searchParams.get('logs_end_date')) ||
    parseDateParam(searchParams.get('end_date')) ||
    initialLogsStartDate;
  const initialLogsStartTime = normalizeTimeParam(searchParams.get('logs_start_time'), '00:00');
  const initialLogsEndTime = normalizeTimeParam(searchParams.get('logs_end_time'), '23:59');

  const initialAttendanceName = sanitizeNameSearchInput(searchParams.get('person_name') || '');
  const initialAttendanceAction = ['check_in', 'check_out'].includes((searchParams.get('action') || '').toLowerCase())
    ? (searchParams.get('action') || '').toLowerCase()
    : '';
  const initialAttendancePremises = ['in', 'out'].includes((searchParams.get('premises_filter') || '').toLowerCase())
    ? (searchParams.get('premises_filter') || '').toLowerCase()
    : '';
  const initialAttendanceLate = ['true', '1'].includes((searchParams.get('late_filter') || '').toLowerCase());
  const initialAttendanceSortOrder = ['asc', 'desc'].includes((searchParams.get('sort_order') || '').toLowerCase())
    ? (searchParams.get('sort_order') || '').toLowerCase()
    : 'desc';
  const initialAttendanceStartDate =
    parseDateParam(searchParams.get('start_date')) ||
    parseDateParam(searchParams.get('logs_start_date')) ||
    new Date();
  const initialAttendanceEndDate =
    parseDateParam(searchParams.get('end_date')) ||
    parseDateParam(searchParams.get('logs_end_date')) ||
    initialAttendanceStartDate;
  const initialAttendanceStartTime = normalizeTimeParam(searchParams.get('start_time'), '00:00');
  const initialAttendanceEndTime = normalizeTimeParam(searchParams.get('end_time'), '23:59');
  const [days, setDays] = useState(30); // Used for names autocomplete
  const [logs, setLogs] = useState([]);
  // Filter states for logs tab
  const [name, setName] = useState(initialLogsName);
  const [nameInput, setNameInput] = useState(initialLogsName);
  const [camera, setCamera] = useState(initialLogsCamera);
  const [cameraId, setCameraId] = useState('');
  const [assignedInOutCameras, setAssignedInOutCameras] = useState([]);
  const assignedInOutCamerasRequestRef = useRef(null);
  const assignedInOutCamerasLoadedRef = useRef(false);
  const [status, setStatus] = useState(initialLogsStatus);
  const [visionReviewResult, setVisionReviewResult] = useState(initialVisionReviewResult);
  const [logType, setLogType] = useState(initialLogsType); // '' = All, attendance/vendor/visitor = non-intrusion role filters, intrusion = is_intrusion=true
  const [greaterThanTotalTimeMs, setGreaterThanTotalTimeMs] = useState(null);
  const [advancedFiltersAnchorEl, setAdvancedFiltersAnchorEl] = useState(null);
  const [advancedTotalTimeInputMs, setAdvancedTotalTimeInputMs] = useState(String(DEFAULT_ADVANCED_TOTAL_TIME_MS));
  const advancedFiltersOpen = Boolean(advancedFiltersAnchorEl);
  const [pagination, setPagination] = useState({ limit: 100, offset: 0, current_page: 1, total: 0 });
  const [selectedPersonIdForSearch, setSelectedPersonIdForSearch] = useState('');

  // Ref to track the current view to detect changes
  const currentViewRef = useRef(null);


  // Date Range Picker state for Logs (default to today)
  const [logsDateRange, setLogsDateRange] = useState([
    {
      startDate: initialLogsStartDate,
      endDate: initialLogsEndDate,
      key: 'selection'
    }
  ]);
  const [logsDateDraftRange, setLogsDateDraftRange] = useState([
    {
      startDate: initialLogsStartDate,
      endDate: initialLogsEndDate,
      key: 'selection'
    }
  ]);
  const [logsDatePickerAnchor, setLogsDatePickerAnchor] = useState(null);
  const logsDatePickerOpen = Boolean(logsDatePickerAnchor);
  // Time filter state for Logs
  const [logsStartTime, setLogsStartTime] = useState(initialLogsStartTime);
  const [logsEndTime, setLogsEndTime] = useState(initialLogsEndTime);
  const [logsStartTimeDraft, setLogsStartTimeDraft] = useState(initialLogsStartTime);
  const [logsEndTimeDraft, setLogsEndTimeDraft] = useState(initialLogsEndTime);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [exportLoading, setExportLoading] = useState(false);
  const [auditExportFormat, setAuditExportFormat] = useState('csv');
  const [auditExportMenuAnchor, setAuditExportMenuAnchor] = useState(null);
  const auditExportMenuOpen = Boolean(auditExportMenuAnchor);
  const [exportAttendanceLoading, setExportAttendanceLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [revokingAuditIds, setRevokingAuditIds] = useState(new Set());

  const [timingDialog, setTimingDialog] = useState({ open: false, log: null, stack: [], detailLoading: false });
  const filtersInitializedRef = useRef(false);
  const logsNavFilterKeyRef = useRef('');
  const logsNavFilterAppliedRef = useRef(true);
  const pendingLogsNavFilterRef = useRef(null);
  const lastAutoLoadLogsRequestKeyRef = useRef('');
  const attendanceNavFilterKeyRef = useRef('');
  const attendanceNavFilterAppliedRef = useRef(true);
  const dateRangeApplyClickedRef = useRef(false);
  const clearAuditDeepLinkOnNextUrlSyncRef = useRef(false);
  /** Avoid double find-person API: auto-refresh effect must not run on transition into result mode (first search already called processImageForMatching). */
  const prevFindPersonResultModeRef = useRef(false);
  const inFlightLogsRequestRef = useRef(null);

  // Attendance state
  const [aPersonName, setAPersonName] = useState(initialAttendanceName);
  const [aAction, setAAction] = useState(initialAttendanceAction);
  const [aPremisesFilter, setAPremisesFilter] = useState(initialAttendancePremises); // '', 'in', 'out'
  const [aLateFilter, setALateFilter] = useState(initialAttendanceLate);
  const [aSortOrder, setASortOrder] = useState(initialAttendanceSortOrder);
  const [lateEntryTime, setLateEntryTime] = useState(null);
  const [aSummaries, setASummaries] = useState([]);
  const [aPagination, setAPagination] = useState({ limit: 100, offset: 0, current_page: 1, total: 0 });
  const [aLoading, setALoading] = useState(false);
  const [availableNames, setAvailableNames] = useState([]);
  const [autoCompleteOptionsLable, setAutoCompleteOptionsLable] = useState([]);

  const safeAvailableNames = Array.isArray(availableNames) ? availableNames : [];
  const [personThumbnails, setPersonThumbnails] = useState({});
  const personThumbnailsRef = useRef({});
  const thumbnailRequestsRef = useRef(new Set());
  const [attendanceSnapshots, setAttendanceSnapshots] = useState({});
  const attendanceSnapshotsRef = useRef({});
  const attendanceSnapshotRequestsRef = useRef(new Set());

  // Date Range Picker state for Attendance (default to today)
  const [aDateRange, setADateRange] = useState([
    {
      startDate: initialAttendanceStartDate,
      endDate: initialAttendanceEndDate,
      key: 'selection'
    }
  ]);
  const [aDatePickerAnchor, setADatePickerAnchor] = useState(null);
  const aDatePickerOpen = Boolean(aDatePickerAnchor);
  const [aStartTime, setAStartTime] = useState(initialAttendanceStartTime);
  const [aEndTime, setAEndTime] = useState(initialAttendanceEndTime);

  // Train Menu state
  const [trainMenuAnchor, setTrainMenuAnchor] = useState(null);
  const [trainAuditId, setTrainAuditId] = useState(null);
  const [presentDialogOpen, setPresentDialogOpen] = useState(false);
  const [bucketManualMarkDialog, setBucketManualMarkDialog] = useState({
    open: false,
    topLog: null,
    bucketLogs: [],
    stackImages: [],
  });
  const [bucketManualMarkRemark, setBucketManualMarkRemark] = useState('');
  const [bucketManualMarkPersonId, setBucketManualMarkPersonId] = useState('');
  const [bucketManualMarkPersonName, setBucketManualMarkPersonName] = useState('');
  const [bucketManualMarkEmployeeCode, setBucketManualMarkEmployeeCode] = useState('');
  const [bucketManualMarkSelectedAuditId, setBucketManualMarkSelectedAuditId] = useState('');
  const [bucketManualMarkSubmitting, setBucketManualMarkSubmitting] = useState(false);
  const [presentAuditId, setPresentAuditId] = useState(null);
  const [presentPersonId, setPresentPersonId] = useState('');
  const [presentName, setPresentName] = useState('');
  const [presentAction, setPresentAction] = useState('check_in');
  const [presentSubmitting, setPresentSubmitting] = useState(false);

  const [addToEmployeeDialog, setAddToEmployeeDialog] = useState(false);
  const [createProfileTypeDialogOpen, setCreateProfileTypeDialogOpen] = useState(false);
  const [createProfileTypeAuditId, setCreateProfileTypeAuditId] = useState(null);
  const [selectedAuditId, setSelectedAuditId] = useState('');
  const [selectedPersonId, setSelectedPersonId] = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [selectedEmployeeCode, setSelectedEmployeeCode] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [employees, setEmployees] = useState([]);
  const safeEmployees = Array.isArray(employees) ? employees : [];
  const sortedEmployees = useMemo(
    () => [...safeEmployees].sort((a, b) => (a?.name || '').localeCompare(b?.name || '')),
    [safeEmployees]
  );
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeeCompanyId, setEmployeeCompanyId] = useState('');
  const [companyOptions, setCompanyOptions] = useState([]);
  const companyOptionsLoadedRef = useRef(false);

  useEffect(() => {
    companyOptionsLoadedRef.current = false;
  }, [permissions?.feature_flags?.kivo]);
  const employeesCacheRef = useRef({});
  const employeesRequestRef = useRef(null);
  const [operationLoading, setOperationLoading] = useState(false);
  const vendorManagementEnabled = permissions?.feature_flags?.vendor_management === true;
  const visitorManagementEnabled = permissions?.feature_flags?.visitor_management === true;
  const visionReviewAvailable = permissions?.feature_flags?.vision_review === true;
  const visionReviewEnabled = permissions?.feature_flags?.vision_review_enabled === true;
  const showVisionReviewUI = visionReviewAvailable && visionReviewEnabled;

  // Image preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Matching state
  const [matchingLoading, setMatchingLoading] = useState(false);
  const [matchingAuditId, setMatchingAuditId] = useState(null);
  const [retryMatchDialogOpen, setRetryMatchDialogOpen] = useState(false);
  const [retryMatchAudit, setRetryMatchAudit] = useState(null);
  const [retryMatchThreshold, setRetryMatchThreshold] = useState('0.6');
  const [retryMatchStackImages, setRetryMatchStackImages] = useState([]);
  const [retryMatchSelectedAuditId, setRetryMatchSelectedAuditId] = useState(null);
  const [retryMatchImageLoading, setRetryMatchImageLoading] = useState(false);
  const [retryMatchResultData, setRetryMatchResultData] = useState(null);
  const [retryMatchResultLoading, setRetryMatchResultLoading] = useState(false);
  const [retryMatchAttendanceSubmitting, setRetryMatchAttendanceSubmitting] = useState(false);
  const retryMatchThresholdRef = useRef(0.6);
  const retryMatchContentRef = useRef(null);
  const retryMatchResultRef = useRef(null);
  const [uploadMatchLoading, setUploadMatchLoading] = useState(false);
  const [refreshingFindPerson, setRefreshingFindPerson] = useState(false);
  const [matchedPersonIdFilter, setMatchedPersonIdFilter] = useState(
    (searchParams.get('person_id') || '').trim()
  );
  const [matchedPersonName, setMatchedPersonName] = useState('');
  const [findPersonSimilarityThreshold, setFindPersonSimilarityThreshold] = useState('0.3');
  const [findPersonQueryImageBase64, setFindPersonQueryImageBase64] = useState('');
  const uploadMatchInputRef = useRef(null);
  const [unknownConfirmOpen, setUnknownConfirmOpen] = useState(false);
  const [unknownQueryImageBase64, setUnknownQueryImageBase64] = useState('');
  const [unknownMatchJobId, setUnknownMatchJobId] = useState('');
  const [unknownMatchRunning, setUnknownMatchRunning] = useState(false);
  const [unknownMatchProgress, setUnknownMatchProgress] = useState({ processed: 0, total: 0, progress_percent: 0 });
  const [unknownResultMode, setUnknownResultMode] = useState(false);
  const [findPersonResultMode, setFindPersonResultMode] = useState(false);
  const [findPersonSummary, setFindPersonSummary] = useState({
    known_count: 0,
    possible_match_count: 0,
    unknown_count: 0,
    total_count: 0,
  });
  const unknownMatchPollRef = useRef(null);

  // Checkbox selection state for ReMatch feature
  const [selectedLogs, setSelectedLogs] = useState(new Set());
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [selectedPossibleMatchLogs, setSelectedPossibleMatchLogs] = useState(new Set());
  const [possibleMatchSelectAllChecked, setPossibleMatchSelectAllChecked] = useState(false);
  const [visionReviewJobId, setVisionReviewJobId] = useState('');
  const [visionReviewRunning, setVisionReviewRunning] = useState(false);
  const [visionReviewLoading, setVisionReviewLoading] = useState(false);
  const [visionReviewProgress, setVisionReviewProgress] = useState({ processed: 0, total: 0, progress_percent: 0 });
  const [visionReviewIsBatch, setVisionReviewIsBatch] = useState(false);
  const [refreshTopMatchersLoading, setRefreshTopMatchersLoading] = useState(false);
  const refreshTopMatchersInFlightRef = useRef(false);
  const [refreshTopMatchersConfirmDialog, setRefreshTopMatchersConfirmDialog] = useState({
    open: false,
    log: null,
    source: 'timeline',
  });
  const visionReviewPollRef = useRef(null);
  const visionReviewJobKindRef = useRef('sync');
  const loadLogsRef = useRef(null);
  const [rematchLoading, setRematchLoading] = useState(false);

  // Image source selection dialog state
  const [imageSourceDialogOpen, setImageSourceDialogOpen] = useState(false);

  // Debug: Monitor dialog state changes
  useEffect(() => {
    console.log('imageSourceDialogOpen changed to:', imageSourceDialogOpen);
  }, [imageSourceDialogOpen]);

  // Image preview state for find person (shown in main dialog)
  const [previewImageDataUrl, setPreviewImageDataUrl] = useState(null);

  // Camera state
  const [cameraStream, setCameraStream] = useState(null);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);

  // Manual Mark Attendance state
  const [markAttendanceDialog, setMarkAttendanceDialog] = useState(false);
  const [markAttendanceCompanyId, setMarkAttendanceCompanyId] = useState('');
  const [markAttendancePersonId, setMarkAttendancePersonId] = useState('');
  const [markAttendancePersonName, setMarkAttendancePersonName] = useState('');
  const [markAttendanceCheckIn, setMarkAttendanceCheckIn] = useState('');
  const [markAttendanceCheckOut, setMarkAttendanceCheckOut] = useState('');
  const [markAttendanceRemark, setMarkAttendanceRemark] = useState('');
  const [markAttendanceSubmitting, setMarkAttendanceSubmitting] = useState(false);
  const [revokeConfirmDialog, setRevokeConfirmDialog] = useState({
    open: false,
    topLog: null,
    bucketLogs: [],
    userName: '',
  });
  const [attendanceCalendarOpen, setAttendanceCalendarOpen] = useState(false);
  const [calendarType, setCalendarType] = useState('checkin'); // 'checkin' or 'checkout'
  const [selectedAttendanceDate, setSelectedAttendanceDate] = useState(new Date());
  const [attendanceExportDialogOpen, setAttendanceExportDialogOpen] = useState(false);
  const [attendanceExportFields, setAttendanceExportFields] = useState([]);
  const [attendanceExportSelectedFields, setAttendanceExportSelectedFields] = useState([]);
  const [attendanceExportPersonName, setAttendanceExportPersonName] = useState('');
  const [attendanceExportAction, setAttendanceExportAction] = useState('');
  const [attendanceExportPremisesFilter, setAttendanceExportPremisesFilter] = useState('');
  const [attendanceExportLateFilter, setAttendanceExportLateFilter] = useState(false);
  const [attendanceExportAllEmployeesPresence, setAttendanceExportAllEmployeesPresence] = useState(false);

  // Possible Match pop-up (click on orange Possible Match chip on card)
  const [possibleMatchModal, setPossibleMatchModal] = useState({ open: false, log: null, image: null });
  const [possibleMatchSelectedId, setPossibleMatchSelectedId] = useState(null);
  const [possibleMatchManualCompanyId, setPossibleMatchManualCompanyId] = useState('');
  const [possibleMatchCandidateImages, setPossibleMatchCandidateImages] = useState({});
  const [possibleMatchMarkSubmitting, setPossibleMatchMarkSubmitting] = useState(false);
  const possibleMatchTrainActiveRef = useRef(false);
  // Processing timeline dialog: possible match UI (same as Possible Match popup)
  const [timingDialogPossibleMatchSelectedId, setTimingDialogPossibleMatchSelectedId] = useState(null);
  const [timingDialogPossibleMatchManualCompanyId, setTimingDialogPossibleMatchManualCompanyId] = useState('');
  const [timingDialogPossibleMatchCandidateImages, setTimingDialogPossibleMatchCandidateImages] = useState({});
  const [timingDialogPossibleMatchMarkSubmitting, setTimingDialogPossibleMatchMarkSubmitting] = useState(false);
  // Confirmation before marking attendance from possible match (modal or timeline)
  const [confirmMarkAttendanceOpen, setConfirmMarkAttendanceOpen] = useState(false);
  const [confirmMarkAttendanceSource, setConfirmMarkAttendanceSource] = useState(null); // 'possible_match_modal' | 'timing_dialog' | 'bucket_manual_mark'
  const [confirmMarkAttendancePersonName, setConfirmMarkAttendancePersonName] = useState('');
  const [attendanceExportFileName, setAttendanceExportFileName] = useState('attendance_logs');
  const [attendanceExportDateRange, setAttendanceExportDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: 'selection' }
  ]);
  const [attendanceExportStartTime, setAttendanceExportStartTime] = useState('00:00');
  const [attendanceExportEndTime, setAttendanceExportEndTime] = useState('23:59');
  const [attendanceExportDatePickerAnchor, setAttendanceExportDatePickerAnchor] = useState(null);
  const attendanceExportDatePickerOpen = Boolean(attendanceExportDatePickerAnchor);

  // Find Person search icon audit logs modal state
  const [findPersonSearchIconAuditLogsModalOpen, setFindPersonSearchIconAuditLogsModalOpen] = useState(false);
  const [findPersonSearchIconAuditLogsLog, setFindPersonSearchIconAuditLogsLog] = useState(null);
  const [findPersonSearchIconAuditLogsStackImages, setFindPersonSearchIconAuditLogsStackImages] = useState([]);
  const [findPersonSearchIconAuditLogsSelectedImage, setFindPersonSearchIconAuditLogsSelectedImage] = useState(null);
  const [findPersonSearchIconAuditLogsSelectedAuditId, setFindPersonSearchIconAuditLogsSelectedAuditId] = useState(null);
  const [findPersonSearchIconAuditLogsThreshold, setFindPersonSearchIconAuditLogsThreshold] = useState('0.3');
  const [findPersonSearchIconAuditLogsLoading, setFindPersonSearchIconAuditLogsLoading] = useState(false);

  const buildAttendanceDateTimeRange = useCallback(() => {
    const startDate = aDateRange[0]?.startDate || new Date();
    const endDate = aDateRange[0]?.endDate || startDate;
    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    // Parse IST times (user input)
    const [istStartHour, istStartMinute] = parseTime(aStartTime, 0, 0);
    const [istEndHour, istEndMinute] = parseTime(aEndTime, 23, 59);

    // IST is UTC+5:30, so we need to subtract 5:30 from IST time to get UTC
    // Create date objects representing IST time, then convert to UTC
    const startDateIST = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      istStartHour,
      istStartMinute,
      0
    ));
    // Subtract 5 hours 30 minutes (IST offset) to convert to UTC
    const startDateTimeUTC = new Date(startDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    const endDateIST = new Date(Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      istEndHour,
      istEndMinute,
      59
    ));
    // Subtract 5 hours 30 minutes (IST offset) to convert to UTC
    const endDateTimeUTC = new Date(endDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    // Format as ISO string (yyyy-MM-ddTHH:mm format, backend will parse as UTC)
    return {
      formattedStart: startDateTimeUTC.toISOString().slice(0, 16), // Format: yyyy-MM-ddTHH:mm
      formattedEnd: endDateTimeUTC.toISOString().slice(0, 16), // Format: yyyy-MM-ddTHH:mm
    };
  }, [aDateRange, aStartTime, aEndTime]);

  const buildLogsDateTimeRange = useCallback((overrides = {}) => {
    const effectiveDateRange = Array.isArray(overrides.dateRange) && overrides.dateRange.length
      ? overrides.dateRange
      : logsDateRange;
    const effectiveStartTime = typeof overrides.startTime === 'string' ? overrides.startTime : logsStartTime;
    const effectiveEndTime = typeof overrides.endTime === 'string' ? overrides.endTime : logsEndTime;
    const startDate = effectiveDateRange[0]?.startDate || new Date();
    const endDate = effectiveDateRange[0]?.endDate || startDate;
    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    // Parse IST times (user input)
    const [istStartHour, istStartMinute] = parseTime(effectiveStartTime, 0, 0);
    const [istEndHour, istEndMinute] = parseTime(effectiveEndTime, 23, 59);

    // IST is UTC+5:30, so we need to subtract 5:30 from IST time to get UTC
    // Create date objects representing IST time, then convert to UTC
    const startDateIST = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      istStartHour,
      istStartMinute,
      0
    ));
    // Subtract 5 hours 30 minutes (IST offset) to convert to UTC
    const startDateTimeUTC = new Date(startDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    const endDateIST = new Date(Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      istEndHour,
      istEndMinute,
      59
    ));
    // Subtract 5 hours 30 minutes (IST offset) to convert to UTC
    const endDateTimeUTC = new Date(endDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    // Format as ISO string (yyyy-MM-ddTHH:mm format, backend will parse as UTC)
    return {
      formattedStart: startDateTimeUTC.toISOString().slice(0, 16), // Format: yyyy-MM-ddTHH:mm
      formattedEnd: endDateTimeUTC.toISOString().slice(0, 16), // Format: yyyy-MM-ddTHH:mm
    };
  }, [logsDateRange, logsStartTime, logsEndTime]);
  const show = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const clearNotificationAuditDeepLink = useCallback(() => {
    clearAuditDeepLinkOnNextUrlSyncRef.current = true;
    const params = new URLSearchParams(location.search);
    if (!params.get('audit_id')) return;
    params.delete('audit_id');
    params.delete('view');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true, state: location.state }
    );
  }, [location.pathname, location.search, location.state, navigate]);

  const applyNameFilterNow = useCallback((rawValue) => {
    const sanitizedValue = sanitizeNameSearchInput(rawValue || '');
    clearNotificationAuditDeepLink();
    setNameInput(sanitizedValue);
    setName(sanitizedValue);
  }, [clearNotificationAuditDeepLink]);

  useEffect(() => {
    setSearchParams((prevParams) => {
      const params = new URLSearchParams(prevParams);

      // Logs filters
      if (name) params.set('logs_name', name); else params.delete('logs_name');
      if (camera) params.set('logs_camera', camera); else params.delete('logs_camera');
      if (status) params.set('logs_status', status); else params.delete('logs_status');
      if (visionReviewResult) params.set('vision_review_result', visionReviewResult); else params.delete('vision_review_result');
      if (logType) params.set('logs_type', logType); else params.delete('logs_type');
      // Person filter selected via the PersonSearch suggestions in the logs view.
      // Kept in the URL like the other filters so it survives refreshes/back.
      if (isLogsView && matchedPersonIdFilter) params.set('person_id', matchedPersonIdFilter); else params.delete('person_id');
      params.delete('logs_start_date');
      params.delete('logs_end_date');
      // While deep-linked via audit_id (notification click), keep the URL clean — the fetch
      // uses audit_id directly, so date/time params are irrelevant and would be misleading.
      let hasAuditIdDeepLink = Boolean((params.get('audit_id') || '').trim());
      if (hasAuditIdDeepLink && clearAuditDeepLinkOnNextUrlSyncRef.current) {
        params.delete('audit_id');
        params.delete('view');
        hasAuditIdDeepLink = false;
        clearAuditDeepLinkOnNextUrlSyncRef.current = false;
      }
      if (hasAuditIdDeepLink) {
        params.delete('logs_start_time');
        params.delete('logs_end_time');
      } else {
        if (logsStartTime && logsStartTime !== '00:00') params.set('logs_start_time', logsStartTime); else params.delete('logs_start_time');
        if (logsEndTime && logsEndTime !== '23:59') params.set('logs_end_time', logsEndTime); else params.delete('logs_end_time');
      }

      // Attendance filters
      if (aPersonName) params.set('person_name', aPersonName); else params.delete('person_name');
      if (aAction) params.set('action', aAction); else params.delete('action');
      if (aPremisesFilter) params.set('premises_filter', aPremisesFilter); else params.delete('premises_filter');
      if (aLateFilter) params.set('late_filter', 'true'); else params.delete('late_filter');
      if (aSortOrder && aSortOrder !== 'desc') params.set('sort_order', aSortOrder); else params.delete('sort_order');
      const activeDateRange = isLogsView ? logsDateRange : aDateRange;
      if (hasAuditIdDeepLink) {
        params.delete('start_date');
        params.delete('end_date');
        params.delete('start_time');
        params.delete('end_time');
      } else {
        if (activeDateRange[0]?.startDate) {
          params.set('start_date', format(activeDateRange[0].startDate, 'yyyy-MM-dd'));
        } else {
          params.delete('start_date');
        }
        if (activeDateRange[0]?.endDate) {
          params.set('end_date', format(activeDateRange[0].endDate, 'yyyy-MM-dd'));
        } else {
          params.delete('end_date');
        }
        if (aStartTime && aStartTime !== '00:00') params.set('start_time', aStartTime); else params.delete('start_time');
        if (aEndTime && aEndTime !== '23:59') params.set('end_time', aEndTime); else params.delete('end_time');
      }

      return params.toString() !== prevParams.toString() ? params : prevParams;
    }, { replace: true });
  }, [
    aAction,
    aDateRange,
    aEndTime,
    aLateFilter,
    aPersonName,
    aPremisesFilter,
    aSortOrder,
    aStartTime,
    camera,
    isLogsView,
    logType,
    logsDateRange,
    logsEndTime,
    logsStartTime,
    matchedPersonIdFilter,
    name,
    status,
    visionReviewResult,
    setSearchParams,
  ]);

  const applySingleTileMatchUpdate = useCallback((auditId, result, thresholdOverride = null) => {
    if (!auditId || !result) return;

    const nextConfidenceLevel = result?.confidence_level || 'no_match';
    const hasRecognizedPerson = Boolean(result?.recognized_person_id);
    const isSuccessfulMatch =
      hasRecognizedPerson && !['no_match', 'no_face', 'error'].includes(String(nextConfidenceLevel || '').toLowerCase());

    const updatedFields = {
      recognized_name: result?.recognized_name || null,
      recognized_person_id: result?.recognized_person_id || null,
      recognized_face_id: result?.recognized_face_id || null,
      confidence_score: typeof result?.confidence_score === 'number' ? result.confidence_score : 0,
      confidence_level: nextConfidenceLevel,
      success: isSuccessfulMatch,
      unmatch_to_match_by: result?.unmatch_to_match_by || null,
    };
    if (Number.isFinite(Number(thresholdOverride))) {
      updatedFields.similarity_threshold = Math.max(0, Math.min(1, Number(thresholdOverride)));
    }

    setLogs((prev) =>
      Array.isArray(prev)
        ? prev.map((log) =>
          String(log?.audit_id || '') === String(auditId)
            ? { ...log, ...updatedFields }
            : log
        )
        : prev
    );

    setTimingDialog((prev) => {
      if (!prev?.log || String(prev.log?.audit_id || '') !== String(auditId)) return prev;
      return {
        ...prev,
        log: { ...prev.log, ...updatedFields },
        stack: Array.isArray(prev.stack)
          ? prev.stack.map((item) => {
            if (String(item?.log?.audit_id || '') !== String(auditId)) return item;
            return { ...item, log: { ...item.log, ...updatedFields } };
          })
          : prev.stack,
      };
    });

    setPossibleMatchModal((prev) => {
      if (!prev?.log || String(prev.log?.audit_id || '') !== String(auditId)) return prev;
      return { ...prev, log: { ...prev.log, ...updatedFields } };
    });
  }, []);

  const markLogAttendanceRevoked = useCallback((entry, attendanceId, revokedByName = null) => {
    if (!entry || typeof entry !== 'object') return entry;
    const metadata = { ...getLogRequestMetadata(entry) };
    const attendanceMeta = metadata.attendance && typeof metadata.attendance === 'object'
      ? { ...metadata.attendance }
      : {};
    const revocationTs = new Date().toISOString();
    attendanceMeta.attendance_id = attendanceId;
    attendanceMeta.revoked = true;
    attendanceMeta.revoked_at = revocationTs;
    attendanceMeta.revoked_by_name = revokedByName || attendanceMeta.revoked_by_name || null;
    metadata.attendance = attendanceMeta;
    metadata.attendance_revocation = {
      revoked: true,
      attendance_id: attendanceId,
      revoked_at: revocationTs,
      revoked_by_name: revokedByName || null,
    };
    const visionReview = metadata.vision_review && typeof metadata.vision_review === 'object'
      ? metadata.vision_review
      : {};
    const priorManual = metadata.manual_attendance && typeof metadata.manual_attendance === 'object'
      ? { ...metadata.manual_attendance }
      : null;
    if (priorManual) {
      metadata.manual_attendance_revoked = {
        ...priorManual,
        revoked: true,
        revoked_at: revocationTs,
        revoked_by_name: revokedByName || null,
      };
      delete metadata.manual_attendance;
    }
    const canReReview = Boolean(visionReview.reviewed);
    metadata.attendance_reopen = {
      can_re_review: canReReview,
      can_re_mark: true,
      revoked_at: revocationTs,
      message: `Attendance revoked — you may mark attendance again${canReReview ? ' or re-run AI matching.' : '.'}`,
    };
    const next = {
      ...entry,
      send_attendance: false,
      revoked: true,
      request_metadata: metadata,
    };
    if (
      entry.confidence_level === 'manual'
      && (visionReview.reviewed || priorManual?.marked_by_ai)
    ) {
      next.confidence_level = 'possible_match';
    }
    return next;
  }, []);

  const openRevokeConfirmDialog = useCallback((topLog, bucketLogs = []) => {
    const ref = resolveBucketAttendanceRef(topLog, bucketLogs);
    const entries = [topLog, ...(Array.isArray(bucketLogs) ? bucketLogs : [])].filter(Boolean);
    const sourceLog = entries.find((entry) => String(entry?.audit_id || '') === String(ref.sourceAuditId || '')) || topLog;
    const userName = sourceLog?.recognized_name || topLog?.recognized_name || 'Unknown';

    setRevokeConfirmDialog({
      open: true,
      topLog,
      bucketLogs: Array.isArray(bucketLogs) ? bucketLogs : [],
      userName,
    });
  }, []);

  const closeRevokeConfirmDialog = useCallback(() => {
    setRevokeConfirmDialog({
      open: false,
      topLog: null,
      bucketLogs: [],
      userName: '',
    });
  }, []);

  const handleRevokeAttendance = useCallback(async (topLog, bucketLogs = []) => {
    const ref = resolveBucketAttendanceRef(topLog, bucketLogs);
    const fallbackAuditId = ref.sourceAuditId || topLog?.audit_id || null;
    if (!ref.attendanceId && !fallbackAuditId) {
      show('Attendance record is not linked on this audit tile', 'warning');
      return;
    }
    if (ref.revoked) {
      show('Attendance is already revoked for this tile', 'info');
      return;
    }

    const loadingKey = fallbackAuditId || ref.attendanceId;
    setRevokingAuditIds((prev) => {
      const next = new Set(prev);
      next.add(loadingKey);
      return next;
    });

    try {
      let response = null;
      let resolvedAttendanceId = ref.attendanceId || null;
      if (resolvedAttendanceId) {
        try {
          response = await deleteAttendanceLog(resolvedAttendanceId, fallbackAuditId || '');
        } catch (deleteErr) {
          const msg = String(deleteErr?.message || '').toLowerCase();
          const shouldTryFallback =
            Boolean(fallbackAuditId) &&
            (msg.includes('attendance log not found') || msg.includes('404'));
          if (!shouldTryFallback) {
            throw deleteErr;
          }
          response = await revokeAttendanceByAudit(fallbackAuditId);
          resolvedAttendanceId = response?.attendance_id || resolvedAttendanceId;
        }
      } else {
        response = await revokeAttendanceByAudit(fallbackAuditId);
        resolvedAttendanceId = response?.attendance_id || null;
      }
      if (response?.success === false) {
        show(response?.detail || response?.message || 'Failed to revoke attendance', 'error');
        return;
      }
      const revokedByName = response?.updated_by_name || response?.revoked_by_name || null;
      const resolvedSourceAuditId = response?.source_audit_id || fallbackAuditId || null;
      const resolvedSourceTrackId = response?.source_track_id || topLog?.track_id || null;
      const resolvedSourcePersonId = response?.source_person_id || topLog?.recognized_person_id || null;
      if (!resolvedAttendanceId) {
        show('Attendance revoked but attendance id not returned', 'warning');
        return;
      }

      setLogs((prev) => (
        Array.isArray(prev)
          ? prev.map((bucketTopLog) => {
            const bucketEntries = Array.isArray(bucketTopLog?.bucket_logs) ? bucketTopLog.bucket_logs : [];
            const updatedBucketLogs = bucketEntries.map((entry) => {
              const entryRef = getAttendanceRefFromLog(entry);
              const sameAttendanceId = entryRef.attendanceId && entryRef.attendanceId === resolvedAttendanceId;
              const sameAuditFallback = !entryRef.attendanceId && resolvedSourceAuditId && String(entry?.audit_id || '') === String(resolvedSourceAuditId);
              const sameTrackPerson =
                resolvedSourceTrackId &&
                String(entry?.track_id || '') === String(resolvedSourceTrackId) &&
                (!resolvedSourcePersonId || String(entry?.recognized_person_id || '') === String(resolvedSourcePersonId));
              if (sameAttendanceId || sameAuditFallback || sameTrackPerson) {
                return markLogAttendanceRevoked(entry, resolvedAttendanceId, revokedByName);
              }
              return entry;
            });

            const topIsSameTrackPerson =
              resolvedSourceTrackId &&
              String(bucketTopLog?.track_id || '') === String(resolvedSourceTrackId) &&
              (!resolvedSourcePersonId || String(bucketTopLog?.recognized_person_id || '') === String(resolvedSourcePersonId));
            const updatedTop = bucketTopLog?.audit_id === (resolvedSourceAuditId || topLog?.audit_id || '') || topIsSameTrackPerson
              ? markLogAttendanceRevoked(bucketTopLog, resolvedAttendanceId, revokedByName)
              : bucketTopLog;

            if (Array.isArray(bucketTopLog?.bucket_logs)) {
              return {
                ...updatedTop,
                bucket_logs: updatedBucketLogs,
              };
            }
            return updatedTop;
          })
          : prev
      ));

      setTimingDialog((prev) => {
        if (!prev?.log) return prev;
        const currentRef = getAttendanceRefFromLog(prev.log);
        const updatedLog =
          (currentRef.attendanceId === resolvedAttendanceId) ||
            (!currentRef.attendanceId && resolvedSourceAuditId && String(prev.log?.audit_id || '') === String(resolvedSourceAuditId)) ||
            (resolvedSourceTrackId &&
              String(prev.log?.track_id || '') === String(resolvedSourceTrackId) &&
              (!resolvedSourcePersonId || String(prev.log?.recognized_person_id || '') === String(resolvedSourcePersonId)))
            ? markLogAttendanceRevoked(prev.log, resolvedAttendanceId, revokedByName)
            : prev.log;
        const updatedStack = Array.isArray(prev.stack)
          ? prev.stack.map((item) => {
            if (!item?.log) return item;
            const itemRef = getAttendanceRefFromLog(item.log);
            const sameAttendanceId = itemRef.attendanceId === resolvedAttendanceId;
            const sameAuditFallback = !itemRef.attendanceId && resolvedSourceAuditId && String(item.log?.audit_id || '') === String(resolvedSourceAuditId);
            const sameTrackPerson =
              resolvedSourceTrackId &&
              String(item.log?.track_id || '') === String(resolvedSourceTrackId) &&
              (!resolvedSourcePersonId || String(item.log?.recognized_person_id || '') === String(resolvedSourcePersonId));
            if (!sameAttendanceId && !sameAuditFallback && !sameTrackPerson) return item;
            return {
              ...item,
              log: markLogAttendanceRevoked(item.log, resolvedAttendanceId, revokedByName),
            };
          })
          : prev.stack;
        return {
          ...prev,
          log: updatedLog,
          stack: updatedStack,
        };
      });

      show('Attendance revoked — you can mark attendance again or re-run AI matching.', 'success');
      if (typeof loadLogsRef.current === 'function') {
        loadLogsRef.current();
      }
      return true;
    } catch (e) {
      show(e?.message || 'Failed to revoke attendance', 'error');
      return false;
    } finally {
      setRevokingAuditIds((prev) => {
        const next = new Set(prev);
        next.delete(loadingKey);
        return next;
      });
    }
  }, [markLogAttendanceRevoked, show]);

  const confirmRevokeAttendance = useCallback(async () => {
    const topLog = revokeConfirmDialog?.topLog;
    const bucketLogs = revokeConfirmDialog?.bucketLogs || [];
    if (!topLog) {
      closeRevokeConfirmDialog();
      return;
    }
    const succeeded = await handleRevokeAttendance(topLog, bucketLogs);
    closeRevokeConfirmDialog();
    if (succeeded) {
      setTimingDialog({ open: false, log: null, stack: [], detailLoading: false });
    }
  }, [closeRevokeConfirmDialog, handleRevokeAttendance, revokeConfirmDialog]);

  const handleMatchAudit = async (auditId, thresholdOverride = null) => {
    try {
      setMatchingLoading(true);
      setMatchingAuditId(auditId);
      const result = await matchAuditImage(auditId, thresholdOverride);

      if (result.success) {
        show(`Successfully matched: ${result.recognized_name || 'No match found'}`, 'success');
        // Update only the matched tile locally (avoid full page/list reload)
        applySingleTileMatchUpdate(auditId, result, thresholdOverride);
      } else {
        show(result.message || 'Failed to match image', 'error');
      }
    } catch (e) {
      show(e.message || 'Failed to match image', 'error');
    } finally {
      setMatchingLoading(false);
      setMatchingAuditId(null);
    }
  };

  const handleOpenRetryMatchDialog = async (event, log, image, stackImages = []) => {
    if (event) event.stopPropagation();
    if (!log?.audit_id) return;
    try {
      const logThreshold = Number(log?.similarity_threshold);
      const fallbackThreshold = Number(findPersonSimilarityThreshold);
      const defaultThreshold = Number.isFinite(logThreshold)
        ? Math.max(0, Math.min(1, logThreshold))
        : Number.isFinite(fallbackThreshold)
          ? Math.max(0, Math.min(1, fallbackThreshold))
          : 0.6;
      setRetryMatchThreshold(String(defaultThreshold));
      retryMatchThresholdRef.current = defaultThreshold;

      // Open dialog immediately with available in-memory images; fetch missing images in background.
      const existingImages = [];
      const missingItems = [];
      if (image) {
        existingImages.push({ log, image, isMain: true, uniqueId: log.audit_id || `main-${Date.now()}` });
      } else if (log.audit_id) {
        missingItems.push({ log, isMain: true, uniqueId: log.audit_id });
      }

      if (Array.isArray(stackImages) && stackImages.length > 0) {
        stackImages.forEach((stackItem, stackIdx) => {
          if (!stackItem?.log?.audit_id || stackItem.log.audit_id === log.audit_id) return;
          if (stackItem.image) {
            existingImages.push({
              log: stackItem.log,
              image: stackItem.image,
              isMain: false,
              uniqueId: stackItem.log.audit_id || `stack-${Date.now()}-${stackIdx}`,
            });
          } else {
            missingItems.push({
              log: stackItem.log,
              isMain: false,
              uniqueId: stackItem.log.audit_id || `stack-${Date.now()}-${stackIdx}`,
            });
          }
        });
      }

      setRetryMatchDialogOpen(true);
      setRetryMatchResultData(null);
      setRetryMatchResultLoading(false);
      setRetryMatchStackImages(existingImages);
      const firstExisting = existingImages[0];
      setRetryMatchSelectedAuditId(firstExisting?.uniqueId || null);
      setRetryMatchAudit(firstExisting?.log || log);

      if (!missingItems.length) {
        if (!existingImages.length) {
          show('No image available for retry match', 'warning');
        }
        return;
      }

      setRetryMatchImageLoading(true);
      const blobToDataUrl = (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

      const fetched = await Promise.all(
        missingItems.map(async (item) => {
          try {
            const blob = await getAuditImageBlob(item.log.audit_id);
            const dataUrl = await blobToDataUrl(blob);
            return { ...item, image: dataUrl };
          } catch (err) {
            console.error(`Error loading retry image ${item.log.audit_id}:`, err);
            return null;
          }
        })
      );

      const fetchedImages = fetched.filter(Boolean);
      setRetryMatchStackImages((prev) => {
        const map = new Map();
        (prev || []).forEach((it) => map.set(String(it?.log?.audit_id || it?.uniqueId || ''), it));
        fetchedImages.forEach((it) => map.set(String(it?.log?.audit_id || it?.uniqueId || ''), it));
        return Array.from(map.values());
      });
      if (!firstExisting && fetchedImages.length > 0) {
        setRetryMatchSelectedAuditId(fetchedImages[0].uniqueId);
        setRetryMatchAudit(fetchedImages[0].log);
      }
    } catch (error) {
      console.error('Error preparing retry match dialog:', error);
      show('Failed to open retry match', 'error');
    } finally {
      setRetryMatchImageLoading(false);
    }
  };

  const handleRetryMatchDialogClose = () => {
    if (matchingLoading || retryMatchAttendanceSubmitting || retryMatchResultLoading) return;
    setRetryMatchDialogOpen(false);
    setRetryMatchAudit(null);
    setRetryMatchThreshold('0.6');
    retryMatchThresholdRef.current = 0.6;
    setRetryMatchStackImages([]);
    setRetryMatchSelectedAuditId(null);
    setRetryMatchImageLoading(false);
    setRetryMatchResultData(null);
    setRetryMatchResultLoading(false);
  };

  const getPersonProfileImage = async (personId) => {
    const pid = String(personId || '').trim();
    if (!pid) return null;
    try {
      const faces = await getPersonFaces(pid);
      const faceList = Array.isArray(faces) ? faces : [];
      const activeFaces = faceList.filter((f) => !f.deleted_at);
      const sortByPoseDesc = (a, b) => Number(b?.pose_quality || 0) - Number(a?.pose_quality || 0);
      const activeFrontFaces = activeFaces
        .filter((f) => String(f?.face_angle || '').toLowerCase() === 'front')
        .sort(sortByPoseDesc);
      const fallbackFrontFaces = faceList
        .filter((f) => String(f?.face_angle || '').toLowerCase() === 'front')
        .sort(sortByPoseDesc);
      const preferredFace =
        activeFrontFaces[0] ||
        fallbackFrontFaces[0] ||
        activeFaces.sort(sortByPoseDesc)[0] ||
        faceList.sort(sortByPoseDesc)[0];
      if (!preferredFace?.face_id) return null;
      const blob = await getFaceImage(preferredFace.face_id);
      return await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
    } catch (_) {
      return null;
    }
  };

  const handleRetryMatchContinue = async () => {
    const parsed = Number(retryMatchThresholdRef.current ?? retryMatchThreshold);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
      show('Similarity threshold must be between 0.0 and 1.0', 'error');
      return;
    }
    setRetryMatchThreshold(String(parsed));
    if (Array.isArray(retryMatchStackImages) && retryMatchStackImages.length > 1 && !retryMatchSelectedAuditId) {
      show('Please select an image for retry match', 'warning');
      return;
    }
    if (Array.isArray(retryMatchStackImages) && retryMatchStackImages.length > 0 && retryMatchSelectedAuditId) {
      const selected = retryMatchStackImages.find((item) => String(item.uniqueId) === String(retryMatchSelectedAuditId));
      if (selected?.log?.audit_id) {
        setRetryMatchAudit(selected.log);
      }
    }

    const selected = Array.isArray(retryMatchStackImages) && retryMatchStackImages.length > 0
      ? retryMatchStackImages.find((item) => String(item.uniqueId || '') === String(retryMatchSelectedAuditId || '')) || retryMatchStackImages[0]
      : null;
    const selectedAudit = selected?.log || retryMatchAudit;
    if (!selectedAudit?.audit_id) return;

    try {
      setRetryMatchResultLoading(true);
      setMatchingLoading(true);
      setMatchingAuditId(selectedAudit.audit_id);

      // Preview only: do not update DB until user clicks Update
      const result = await matchAuditImage(selectedAudit.audit_id, parsed, false);
      if (!result?.success) {
        show(result?.message || 'Failed to match image', 'error');
        return;
      }

      const employeeImage = await getPersonProfileImage(result.recognized_person_id);
      setRetryMatchResultData({
        auditLog: selectedAudit,
        threshold: parsed,
        auditImage: selected?.image || (selectedAudit.audit_id ? peekAuditImage(selectedAudit.audit_id) : null),
        employeeImage,
        result,
      });
      show(`Preview ready: ${result.recognized_name || 'No match found'}`, 'info');
    } catch (e) {
      show(e.message || 'Failed to match image', 'error');
    } finally {
      setMatchingLoading(false);
      setMatchingAuditId(null);
      setRetryMatchResultLoading(false);
    }
  };

  const handleRetryMatchApply = async () => {
    if (!retryMatchResultData?.auditLog?.audit_id) {
      show('Match details are missing', 'error');
      return;
    }
    try {
      setRetryMatchAttendanceSubmitting(true);
      const log = retryMatchResultData.auditLog;
      const threshold = Number(retryMatchResultData?.threshold);
      const commitRes = await matchAuditImage(
        log.audit_id,
        Number.isFinite(threshold) ? threshold : null,
        true,
        retryMatchResultData?.result || null
      );
      if (!commitRes?.success) {
        show(commitRes?.message || 'Failed to apply match', 'error');
        return;
      }
      applySingleTileMatchUpdate(log.audit_id, commitRes, Number.isFinite(threshold) ? threshold : null);
      show('Match updated successfully', 'success');
      handleRetryMatchDialogClose();
    } catch (e) {
      show(e.message || 'Failed to apply match', 'error');
    } finally {
      setRetryMatchAttendanceSubmitting(false);
    }
  };

  useEffect(() => {
    if (!retryMatchDialogOpen || retryMatchResultLoading || !retryMatchResultData) return;
    const scrollTarget = retryMatchResultRef.current;
    const scrollContainer = retryMatchContentRef.current;
    if (scrollTarget?.scrollIntoView) {
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' });
    }
  }, [retryMatchDialogOpen, retryMatchResultLoading, retryMatchResultData]);

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });

  const handleUploadMatchClick = () => {
    if (!uploadMatchLoading && !logsLoading && !unknownMatchRunning) {
      setImageSourceDialogOpen(true);
    }
  };

  const handleCloseImageSourceDialog = () => {
    setImageSourceDialogOpen(false);
    // Stop camera if it's running
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(track => track.stop());
      cameraStreamRef.current = null;
      setCameraStream(null);
      setIsCameraOn(false);
    }
    // Clear preview image
    setPreviewImageDataUrl(null);
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user' // Front camera
        },
        audio: false
      });

      cameraStreamRef.current = mediaStream;
      setCameraStream(mediaStream);
      setIsCameraOn(true);

      // Wait a bit for video element to be ready
      setTimeout(() => {
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      console.error('Error accessing camera:', error);
      show('Unable to access camera. Please check permissions.', 'error');
      setIsCameraOn(false);
    }
  };

  const stopCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(track => track.stop());
      cameraStreamRef.current = null;
      setCameraStream(null);
      setIsCameraOn(false);

      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = null;
      }
    }
  };

  const capturePhotoFromCamera = async () => {
    if (!cameraVideoRef.current) return;

    try {
      const video = cameraVideoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      // Convert canvas to blob then to data URL
      canvas.toBlob(async (blob) => {
        if (!blob) {
          show('Failed to capture photo', 'error');
          return;
        }

        const dataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });

        // Stop camera
        stopCamera();

        // Show captured image in the same dialog
        setPreviewImageDataUrl(dataUrl);
      }, 'image/jpeg', 0.95);
    } catch (error) {
      console.error('Error capturing photo:', error);
      show('Failed to capture photo', 'error');
    }
  };

  const normalizeImageSourceForFindPerson = async (imageSource) => {
    const source = typeof imageSource === 'string' ? imageSource : '';
    if (!source) {
      throw new Error('No image selected for find person');
    }
    if (source.startsWith('data:image/')) {
      return source;
    }
    if (source.startsWith('blob:')) {
      const response = await fetch(source);
      if (!response.ok) {
        throw new Error('Failed to read selected image');
      }
      const blob = await response.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      return dataUrl;
    }
    if (/^[A-Za-z0-9+/=]+$/.test(source)) {
      return source;
    }
    throw new Error('Invalid image source for find person');
  };

  const processImageForMatching = async (
    dataUrl,
    {
      isRefresh = false,
      limit = null,
      offset = null,
      similarityThreshold: overrideThreshold = null,
      dateRange = null,
      startTime = null,
      endTime = null,
    } = {}
  ) => {
    try {
      if (isRefresh) {
        setRefreshingFindPerson(true);
        // Clear old logs immediately when refreshing to avoid showing stale data
        setLogs([]);
      } else {
        setUploadMatchLoading(true);
      }
      // Use override threshold if provided, otherwise use state value
      const thresholdValue = overrideThreshold !== null ? overrideThreshold : findPersonSimilarityThreshold;
      const parsedThreshold = Number(thresholdValue);
      const similarityThreshold = Number.isFinite(parsedThreshold)
        ? Math.max(0, Math.min(1, parsedThreshold))
        : undefined;
      const rangeOverrides = {};
      if (Array.isArray(dateRange) && dateRange.length) rangeOverrides.dateRange = dateRange;
      if (typeof startTime === 'string') rangeOverrides.startTime = startTime;
      if (typeof endTime === 'string') rangeOverrides.endTime = endTime;
      const { formattedStart, formattedEnd } = buildLogsDateTimeRange(rangeOverrides);
      const normalizedImageBase64 = await normalizeImageSourceForFindPerson(dataUrl);

      // Use provided limit/offset or current pagination values
      // When refreshing, default to offset 0 to start from first page
      const currentLimit = limit !== null ? limit : pagination.limit;
      const currentOffset = offset !== null ? offset : (isRefresh ? 0 : 0);

      const matchResponse = await findPersonCombined({
        image_base64: normalizedImageBase64,
        start_time: formattedStart,
        end_time: formattedEnd,
        name: name.trim(),
        camera,
        similarity_threshold: similarityThreshold,
        limit: currentLimit,
        offset: currentOffset,
      });

      const combinedLogs = Array.isArray(matchResponse?.combined_matches)
        ? matchResponse.combined_matches
        : [];
      const knownCount = Number(matchResponse?.known_count || 0);
      const possibleMatchCount = Number(matchResponse?.possible_match_count || 0);
      const unknownCount = Number(matchResponse?.unknown_count || 0);
      const totalCount = Number(matchResponse?.total_count || combinedLogs.length);
      const matchedPersonId = String(matchResponse?.recognized_person_id || '').trim();
      const matchedPersonNameValue = String(matchResponse?.recognized_name || '').trim();

      // Extract pagination from response if available
      const responsePagination = matchResponse?.pagination;
      const paginationTotal = responsePagination?.total ?? totalCount;
      const paginationLimit = responsePagination?.limit ?? currentLimit;
      const paginationOffset = responsePagination?.offset ?? currentOffset;
      const paginationCurrentPage = responsePagination?.current_page ?? Math.floor(paginationOffset / paginationLimit) + 1;

      setLogs(combinedLogs);
      setPagination((prev) => ({
        ...prev,
        limit: paginationLimit,
        offset: paginationOffset,
        current_page: paginationCurrentPage,
        total: paginationTotal,
      }));
      setMatchedPersonIdFilter(matchedPersonId);
      setMatchedPersonName(matchedPersonNameValue);
      setFindPersonSummary({
        known_count: knownCount,
        possible_match_count: possibleMatchCount,
        unknown_count: unknownCount,
        total_count: totalCount,
      });
      setFindPersonResultMode(true);
      setUnknownResultMode(false);
      setFindPersonQueryImageBase64(normalizedImageBase64);

      if (!isRefresh) {
        if (matchedPersonId) {
          show(
            `Found ${totalCount} result(s): matched ${knownCount}, possible match ${possibleMatchCount}, unmatched ${unknownCount} for ${matchedPersonNameValue || matchedPersonId}`,
            'success'
          );
        } else {
          show(
            `No known person match. Found ${unknownCount} result(s) in unmatched logs`,
            'info'
          );
        }
      }
    } catch (e) {
      const msg = e.message || 'Failed to match image';
      if (msg.toLowerCase().includes('multiple faces detected')) {
        clearUploadedMatchFilter();
        show(msg, 'warning');
      } else if (msg.toLowerCase().includes('no face detected')) {
        clearUploadedMatchFilter();
        show(msg, 'warning');
      } else {
        show(msg, 'error');
      }
    } finally {
      if (isRefresh) {
        setRefreshingFindPerson(false);
      } else {
        setUploadMatchLoading(false);
      }
    }
  };

  const clearUploadedMatchFilter = () => {
    setMatchedPersonIdFilter('');
    setMatchedPersonName('');
    setFindPersonResultMode(false);
    setFindPersonSummary({
      known_count: 0,
      possible_match_count: 0,
      unknown_count: 0,
      total_count: 0,
    });
    setFindPersonQueryImageBase64('');
    setSelectedLogs(new Set());
    setSelectAllChecked(false);
  };

  const hasActiveLogsFilters = useMemo(() => (
    !!(name?.trim()) ||
    !!status ||
    !!camera ||
    !!cameraId ||
    !!logType ||
    !!visionReviewResult ||
    greaterThanTotalTimeMs != null ||
    !!activeNotificationAuditId ||
    findPersonResultMode ||
    !isDefaultDateTimeRange(logsDateRange, logsStartTime, logsEndTime)
  ), [
    activeNotificationAuditId,
    camera,
    cameraId,
    findPersonResultMode,
    greaterThanTotalTimeMs,
    logType,
    logsDateRange,
    logsEndTime,
    logsStartTime,
    name,
    status,
    visionReviewResult,
  ]);

  const handleOpenAdvancedFilters = useCallback((event) => {
    setAdvancedTotalTimeInputMs(
      greaterThanTotalTimeMs != null ? String(greaterThanTotalTimeMs) : String(DEFAULT_ADVANCED_TOTAL_TIME_MS)
    );
    setAdvancedFiltersAnchorEl(event.currentTarget);
  }, [greaterThanTotalTimeMs]);

  const handleCloseAdvancedFilters = useCallback(() => {
    setAdvancedFiltersAnchorEl(null);
  }, []);

  const handleApplyAdvancedTotalTimeFilter = useCallback(() => {
    const parsed = Number(String(advancedTotalTimeInputMs || '').trim());
    if (!Number.isFinite(parsed) || parsed <= 0 || !Number.isInteger(parsed)) {
      show('Enter a whole number of milliseconds greater than 0', 'warning');
      return;
    }
    setGreaterThanTotalTimeMs(Math.trunc(parsed));
    setAdvancedFiltersAnchorEl(null);
    setPagination((prev) => ({ ...prev, offset: 0, current_page: 1 }));
  }, [show, advancedTotalTimeInputMs]);

  const handleClearAdvancedTotalTimeFilter = useCallback(() => {
    setGreaterThanTotalTimeMs(null);
    setAdvancedTotalTimeInputMs(String(DEFAULT_ADVANCED_TOTAL_TIME_MS));
    setAdvancedFiltersAnchorEl(null);
    setPagination((prev) => ({ ...prev, offset: 0, current_page: 1 }));
  }, []);

  const handleClearAllLogsFilters = useCallback(() => {
    clearNotificationAuditDeepLink();
    if (findPersonResultMode) {
      clearUploadedMatchFilter();
    }
    setMatchedPersonIdFilter('');
    setName('');
    setNameInput('');
    setCamera('');
    setCameraId('');
    setStatus('');
    setVisionReviewResult('');
    setLogType('');
    setGreaterThanTotalTimeMs(null);
    setAdvancedTotalTimeInputMs(String(DEFAULT_ADVANCED_TOTAL_TIME_MS));
    setAdvancedFiltersAnchorEl(null);
    const todayRange = createTodayDateRange();
    setLogsDateRange(todayRange);
    setLogsDateDraftRange(todayRange);
    setLogsStartTime(DEFAULT_START_TIME);
    setLogsEndTime(DEFAULT_END_TIME);
    setLogsStartTimeDraft(DEFAULT_START_TIME);
    setLogsEndTimeDraft(DEFAULT_END_TIME);
  }, [clearNotificationAuditDeepLink, findPersonResultMode]);

  const handleLogCheckboxChange = (auditId, checked) => {
    setSelectedLogs(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(auditId);
      } else {
        newSet.delete(auditId);
      }
      // Update selectAllChecked based on whether all unmatched visible logs are selected
      const unmatchedAuditIds = new Set(
        logs
          .filter(log => {
            const id = log.audit_id;
            if (!id) return false;
            return log.confidence_level === 'no_match' ||
              !log.success ||
              !log.recognized_person_id;
          })
          .map(log => log.audit_id)
          .filter(Boolean)
      );
      setSelectAllChecked(
        unmatchedAuditIds.size > 0 &&
        Array.from(unmatchedAuditIds).every(id => newSet.has(id))
      );
      return newSet;
    });
  };

  const handleSelectAllChange = (checked) => {
    if (checked) {
      // Select only unmatched visible logs
      const unmatchedAuditIds = new Set(
        logs
          .filter(log => {
            const auditId = log.audit_id;
            if (!auditId) return false;
            // Check if log is unmatched
            return log.confidence_level === 'no_match' ||
              !log.success ||
              !log.recognized_person_id;
          })
          .map(log => log.audit_id)
          .filter(Boolean)
      );
      setSelectedLogs(unmatchedAuditIds);
      setSelectAllChecked(unmatchedAuditIds.size > 0);
    } else {
      setSelectedLogs(new Set());
      setSelectAllChecked(false);
    }
  };

  // Sync selectAllChecked when logs change - only check unmatched logs
  useEffect(() => {
    if (findPersonResultMode && logs.length > 0) {
      const unmatchedAuditIds = new Set(
        logs
          .filter(log => {
            const id = log.audit_id;
            if (!id) return false;
            return log.confidence_level === 'no_match' ||
              !log.success ||
              !log.recognized_person_id;
          })
          .map(log => log.audit_id)
          .filter(Boolean)
      );
      const allUnmatchedSelected =
        unmatchedAuditIds.size > 0 &&
        Array.from(unmatchedAuditIds).every(id => selectedLogs.has(id));
      setSelectAllChecked(allUnmatchedSelected);
    } else {
      setSelectAllChecked(false);
    }
  }, [logs, selectedLogs, findPersonResultMode]);

  const handleReMatch = async () => {
    if (selectedLogs.size === 0 || !matchedPersonIdFilter) {
      return;
    }

    try {
      setRematchLoading(true);
      const auditIdsArray = Array.from(selectedLogs);
      const similarityThreshold = parseFloat(findPersonSimilarityThreshold) || 0.3;

      const response = await unmatchToMatch(
        auditIdsArray,
        matchedPersonIdFilter,
        matchedPersonName,
        similarityThreshold
      );

      if (response.success) {
        show(`Successfully matched ${response.updated_count} log(s)`, 'success');
        // Clear selection
        setSelectedLogs(new Set());
        setSelectAllChecked(false);
        // Refresh the logs
        await handleRefreshFindPersonResults(true);
      } else {
        show(response.message || 'Failed to match logs', 'error');
      }
    } catch (error) {
      console.error('Error matching logs:', error);
      show(error.message || 'Failed to match logs', 'error');
    } finally {
      setRematchLoading(false);
    }
  };

  const clearUnknownResultView = () => {
    setUnknownResultMode(false);
    setFindPersonResultMode(false);
    setUnknownMatchJobId('');
    setUnknownMatchRunning(false);
    setUnknownMatchProgress({ processed: 0, total: 0, progress_percent: 0 });
    try {
      sessionStorage.removeItem(UNKNOWN_MATCH_JOB_STORAGE_KEY);
    } catch (e) {
      // no-op
    }
    if (unknownMatchPollRef.current) {
      clearInterval(unknownMatchPollRef.current);
      unknownMatchPollRef.current = null;
    }
  };

  const stopUnknownPolling = useCallback(() => {
    if (unknownMatchPollRef.current) {
      clearInterval(unknownMatchPollRef.current);
      unknownMatchPollRef.current = null;
    }
  }, []);

  const handleUnknownJobStatus = useCallback((statusRes) => {
    setUnknownMatchProgress({
      processed: statusRes?.processed || 0,
      total: statusRes?.total || 0,
      progress_percent: statusRes?.progress_percent || 0,
    });

    if (statusRes?.status === 'completed') {
      stopUnknownPolling();
      setUnknownMatchRunning(false);
      const matchedLogs = Array.isArray(statusRes?.matches) ? statusRes.matches : [];
      setLogs(matchedLogs);
      setPagination((prev) => ({
        ...prev,
        offset: 0,
        current_page: 1,
        total: matchedLogs.length,
      }));
      setUnknownResultMode(true);
      return 'completed';
    }

    if (statusRes?.status === 'failed') {
      stopUnknownPolling();
      setUnknownMatchRunning(false);
      try {
        sessionStorage.removeItem(UNKNOWN_MATCH_JOB_STORAGE_KEY);
      } catch (e) {
        // no-op
      }
      return 'failed';
    }

    setUnknownMatchRunning(true);
    return 'running';
  }, [stopUnknownPolling]);

  const startUnknownPolling = useCallback((jobId, { silent = false } = {}) => {
    if (!jobId) return;
    stopUnknownPolling();
    unknownMatchPollRef.current = setInterval(async () => {
      try {
        const statusRes = await getUnknownMatchJob(jobId);
        const state = handleUnknownJobStatus(statusRes);
        if (state === 'completed' && !silent) {
          const matchedCount = Array.isArray(statusRes?.matches) ? statusRes.matches.length : 0;
          show(`Found ${matchedCount} matched log(s) from unmatched records`, 'success');
        } else if (state === 'failed') {
          show(statusRes?.error || 'Background match job failed', 'error');
        }
      } catch (pollErr) {
        stopUnknownPolling();
        setUnknownMatchRunning(false);
        setUnknownMatchJobId('');
        try {
          sessionStorage.removeItem(UNKNOWN_MATCH_JOB_STORAGE_KEY);
        } catch (e) {
          // no-op
        }
        if (!silent) {
          show(pollErr.message || 'Failed to poll unknown match job', 'error');
        }
      }
    }, 1500);
  }, [handleUnknownJobStatus, show, stopUnknownPolling]);

  const stopVisionReviewPolling = useCallback(() => {
    if (visionReviewPollRef.current) {
      clearInterval(visionReviewPollRef.current);
      visionReviewPollRef.current = null;
    }
  }, []);

  const getVisionReviewFilters = useCallback(() => {
    const startDate = logsDateRange[0].startDate;
    const endDate = logsDateRange[0].endDate;
    return {
      start_time: format(startOfDay(startDate), "yyyy-MM-dd'T'HH:mm"),
      end_time: format(endOfDay(endDate), "yyyy-MM-dd'T'HH:mm"),
      camera: camera || undefined,
      camera_id: cameraId || undefined,
      name: name.trim() || undefined,
    };
  }, [logsDateRange, camera, cameraId, name]);

  const summarizeVisionReviewResults = useCallback((statusRes) => {
    const results = Array.isArray(statusRes?.results) ? statusRes.results : [];
    const completed = results.filter((r) => r?.status === 'completed').length;
    const failed = results.filter((r) => r?.status === 'failed').length;
    const skipped = results.filter((r) => r?.status === 'skipped').length;
    return { completed, failed, skipped, total: results.length };
  }, []);

  const handleVisionReviewBatchJobStatus = useCallback((statusRes) => {
    const total = statusRes?.total || 0;
    const processed = statusRes?.processed || 0;
    setVisionReviewProgress({
      processed,
      total,
      progress_percent: total > 0 ? Math.round((processed / total) * 100) : 0,
    });
    if (statusRes?.status === 'completed') {
      stopVisionReviewPolling();
      setVisionReviewRunning(false);
      setVisionReviewJobId('');
      setVisionReviewIsBatch(false);
      if (typeof loadLogsRef.current === 'function') {
        loadLogsRef.current();
      }
      return 'completed';
    }
    if (statusRes?.status === 'failed') {
      stopVisionReviewPolling();
      setVisionReviewRunning(false);
      setVisionReviewJobId('');
      setVisionReviewIsBatch(false);
      return 'failed';
    }
    setVisionReviewRunning(true);
    return 'running';
  }, [stopVisionReviewPolling]);

  const handleVisionReviewJobStatus = useCallback((statusRes) => {
    setVisionReviewProgress({
      processed: statusRes?.processed || 0,
      total: statusRes?.total || 0,
      progress_percent: statusRes?.progress_percent || 0,
    });
    if (statusRes?.status === 'completed') {
      stopVisionReviewPolling();
      setVisionReviewRunning(false);
      setVisionReviewJobId('');
      setVisionReviewIsBatch(false);
      if (typeof loadLogsRef.current === 'function') {
        loadLogsRef.current();
      }
      return 'completed';
    }
    if (statusRes?.status === 'failed') {
      stopVisionReviewPolling();
      setVisionReviewRunning(false);
      setVisionReviewJobId('');
      setVisionReviewIsBatch(false);
      return 'failed';
    }
    setVisionReviewRunning(true);
    return 'running';
  }, [stopVisionReviewPolling]);

  const startVisionReviewPolling = useCallback((jobId, { silent = false, kind = 'sync' } = {}) => {
    if (!jobId) return;
    stopVisionReviewPolling();
    visionReviewJobKindRef.current = kind;
    const pollMs = kind === 'batch' ? 5000 : 1500;
    visionReviewPollRef.current = setInterval(async () => {
      try {
        const isBatch = visionReviewJobKindRef.current === 'batch';
        const statusRes = isBatch
          ? await getVisionReviewBatchJob(jobId)
          : await getVisionReviewJob(jobId);
        const state = isBatch
          ? handleVisionReviewBatchJobStatus(statusRes)
          : handleVisionReviewJobStatus(statusRes);
        if (state === 'completed' && !silent) {
          const { completed, failed, skipped, total } = summarizeVisionReviewResults(statusRes);
          if (failed > 0 && completed === 0) {
            const firstErr = (statusRes?.results || []).find((r) => r?.status === 'failed')?.error
              || statusRes?.error;
            show(firstErr || `AI Matching failed for ${failed} audit(s)`, 'error');
          } else if (failed > 0) {
            show(`AI Matching: ${completed} succeeded, ${failed} failed${skipped ? `, ${skipped} skipped` : ''}`, 'warning');
          } else {
            const autoMarked = (statusRes?.results || []).filter(
              (r) => r?.vision_review?.auto_attendance?.success
            ).length;
            const prefix = isBatch ? 'AI batch matching' : 'AI Matching';
            if (autoMarked > 0) {
              show(`${prefix} completed — attendance auto-marked for ${autoMarked} audit(s)`, 'success');
            } else {
              show(`${prefix} completed for ${total || completed} audit(s)`, 'success');
            }
          }
        } else if (state === 'failed') {
          show(statusRes?.error || 'AI Matching job failed', 'error');
        }
      } catch (pollErr) {
        stopVisionReviewPolling();
        setVisionReviewRunning(false);
        setVisionReviewJobId('');
        setVisionReviewIsBatch(false);
        if (!silent) {
          show(pollErr.message || 'Failed to poll AI Matching job', 'error');
        }
      }
    }, pollMs);
  }, [
    handleVisionReviewJobStatus,
    handleVisionReviewBatchJobStatus,
    show,
    stopVisionReviewPolling,
    summarizeVisionReviewResults,
  ]);

  const handlePossibleMatchCheckboxChange = useCallback((auditId, checked) => {
    setSelectedPossibleMatchLogs((prev) => {
      const next = new Set(prev);
      if (checked) next.add(auditId);
      else next.delete(auditId);
      const visibleIds = logs
        .filter((log) => log.confidence_level === 'possible_match' && log.audit_id)
        .map((log) => log.audit_id);
      setPossibleMatchSelectAllChecked(
        visibleIds.length > 0 && visibleIds.every((id) => next.has(id))
      );
      return next;
    });
  }, [logs]);

  const handlePossibleMatchSelectAllChange = useCallback((checked) => {
    if (checked) {
      const ids = new Set(
        logs
          .filter((log) => log.confidence_level === 'possible_match' && log.audit_id)
          .map((log) => log.audit_id)
      );
      setSelectedPossibleMatchLogs(ids);
      setPossibleMatchSelectAllChecked(ids.size > 0);
    } else {
      setSelectedPossibleMatchLogs(new Set());
      setPossibleMatchSelectAllChecked(false);
    }
  }, [logs]);

  const handleStartBulkVisionReview = useCallback(async ({ selectAllFiltered = false, force = false } = {}) => {
    if (visionReviewRunning) return;
    const auditIds = Array.from(selectedPossibleMatchLogs);
    const useBatch = selectAllFiltered || auditIds.length >= 2;

    try {
      setVisionReviewRunning(true);
      setVisionReviewIsBatch(useBatch);
      setVisionReviewProgress({ processed: 0, total: useBatch ? auditIds.length : 1, progress_percent: 0 });

      if (useBatch) {
        const payload = selectAllFiltered
          ? { select_all_filtered: true, force, ...getVisionReviewFilters() }
          : { audit_ids: auditIds, force };
        const started = await startVisionReviewBatchJob(payload);
        const jobId = started?.batch_job_id;
        if (!jobId) {
          throw new Error(started?.detail || started?.message || 'Failed to start AI Matching batch');
        }
        setVisionReviewJobId(jobId);
        startVisionReviewPolling(jobId, { kind: 'batch' });
      } else {
        const started = await startVisionReviewJob({ audit_ids: auditIds, force });
        const jobId = started?.job_id;
        if (!jobId) {
          throw new Error(started?.detail || started?.message || 'Failed to start AI Matching');
        }
        setVisionReviewJobId(jobId);
        startVisionReviewPolling(jobId, { kind: 'sync' });
      }
    } catch (err) {
      setVisionReviewRunning(false);
      setVisionReviewJobId('');
      setVisionReviewIsBatch(false);
      show(err.message || 'Failed to start AI Matching', 'error');
    }
  }, [
    visionReviewRunning,
    getVisionReviewFilters,
    selectedPossibleMatchLogs,
    startVisionReviewPolling,
    show,
  ]);

  const handleVisionReviewForLog = useCallback(async (log, { source = 'modal', force = false } = {}) => {
    const auditId = log?.audit_id;
    if (!auditId || visionReviewRunning || visionReviewLoading) return;
    try {
      setVisionReviewLoading(true);
      const started = await startVisionReviewJob({ audit_ids: [auditId], force });
      const jobId = started?.job_id;
      if (!jobId) {
        throw new Error(started?.detail || started?.message || 'Failed to start AI Matching');
      }

      const statusRes = await new Promise((resolve, reject) => {
        const poll = async () => {
          try {
            const res = await getVisionReviewJob(jobId);
            if (res?.status === 'completed') {
              resolve(res);
              return;
            }
            if (res?.status === 'failed') {
              reject(new Error(res?.error || 'AI Matching failed'));
              return;
            }
            setTimeout(poll, 1500);
          } catch (err) {
            reject(err);
          }
        };
        poll();
      });

      const result = (statusRes?.results || []).find((item) => item.audit_id === auditId);
      const visionReview = result?.vision_review;
      if (visionReview) {
        const mergeVisionReview = (prev) => ({
          ...prev,
          log: prev.log
            ? {
              ...prev.log,
              request_metadata: {
                ...(prev.log.request_metadata || {}),
                vision_review: visionReview,
              },
            }
            : prev.log,
        });
        if (source === 'timeline') {
          setTimingDialog((prev) => mergeVisionReview(prev));
        } else {
          setPossibleMatchModal((prev) => mergeVisionReview(prev));
        }
      }
      if (typeof loadLogsRef.current === 'function') {
        loadLogsRef.current();
      }
      if (result?.error) {
        show(result.error, 'error');
      } else if (result?.status === 'failed') {
        show(result?.vision_review?.error || 'AI Matching failed', 'error');
      } else if (visionReview?.auto_attendance?.success) {
        show(
          `AI Matching completed — attendance marked for ${visionReview.auto_attendance.person_name || 'employee'}`,
          'success'
        );
      } else if (visionReview?.auto_attendance?.error) {
        show(`AI Matching done but auto attendance failed: ${visionReview.auto_attendance.error}`, 'warning');
      } else {
        show('AI Matching completed', 'success');
      }
    } catch (err) {
      show(err.message || 'AI Matching failed', 'error');
    } finally {
      setVisionReviewLoading(false);
    }
  }, [visionReviewRunning, visionReviewLoading, show]);

  const applyRefreshedTopMatchersToLog = useCallback((log, topCandidates, visionReview) => {
    if (!log) return log;
    const nextMetadata = {
      ...(log.request_metadata || {}),
      top_candidates: Array.isArray(topCandidates) ? topCandidates : (log.request_metadata?.top_candidates || []),
    };
    if (visionReview && typeof visionReview === 'object') {
      nextMetadata.vision_review = visionReview;
    }
    return {
      ...log,
      request_metadata: nextMetadata,
    };
  }, []);

  const openRefreshTopMatchersConfirmDialog = useCallback((log, { source = 'timeline' } = {}) => {
    if (!log?.audit_id || refreshTopMatchersLoading || refreshTopMatchersInFlightRef.current) return;
    setRefreshTopMatchersConfirmDialog({
      open: true,
      log,
      source,
    });
  }, [refreshTopMatchersLoading]);

  const closeRefreshTopMatchersConfirmDialog = useCallback(() => {
    setRefreshTopMatchersConfirmDialog({
      open: false,
      log: null,
      source: 'timeline',
    });
  }, []);

  const handleRefreshTopMatchers = useCallback(async (log, { source = 'timeline' } = {}) => {
    const auditId = log?.audit_id;
    if (!auditId || refreshTopMatchersInFlightRef.current) return;
    refreshTopMatchersInFlightRef.current = true;
    setRefreshTopMatchersLoading(true);
    try {
      const result = await refreshTopMatchers(auditId);
      if (!result?.success) {
        throw new Error(result?.detail || result?.message || 'Failed to refresh top matchers');
      }
      const nextCandidates = Array.isArray(result.top_candidates) ? result.top_candidates : [];
      const nextVision = result.vision_review || null;

      const patchLog = (entry) => {
        if (!entry || String(entry.audit_id) !== String(auditId)) return entry;
        return applyRefreshedTopMatchersToLog(entry, nextCandidates, nextVision);
      };

      if (source === 'timeline' || source === 'modal') {
        if (source === 'timeline') {
          setTimingDialog((prev) => (
            prev?.log && String(prev.log.audit_id) === String(auditId)
              ? { ...prev, log: applyRefreshedTopMatchersToLog(prev.log, nextCandidates, nextVision) }
              : prev
          ));
        } else {
          setPossibleMatchModal((prev) => (
            prev?.log && String(prev.log.audit_id) === String(auditId)
              ? { ...prev, log: applyRefreshedTopMatchersToLog(prev.log, nextCandidates, nextVision) }
              : prev
          ));
        }
      }

      setLogs((prev) => (Array.isArray(prev) ? prev.map(patchLog) : prev));

      const firstPid = nextCandidates[0]?.person_id ? String(nextCandidates[0].person_id).trim() : null;
      if (source === 'timeline') {
        setTimingDialogPossibleMatchSelectedId(firstPid || null);
        setTimingDialogPossibleMatchCandidateImages({});
      } else if (source === 'modal') {
        setPossibleMatchSelectedId(firstPid || null);
        setPossibleMatchCandidateImages({});
      }

      if (result.improved) {
        show(result.message || 'Top matchers updated', 'success');
      } else {
        show(result.message || 'No better matches were found.', 'info');
      }
    } catch (err) {
      show(err?.message || err?.detail || 'Failed to refresh top matchers', 'error');
    } finally {
      refreshTopMatchersInFlightRef.current = false;
      setRefreshTopMatchersLoading(false);
    }
  }, [applyRefreshedTopMatchersToLog, show]);

  const confirmRefreshTopMatchers = useCallback(async () => {
    const log = refreshTopMatchersConfirmDialog?.log;
    const source = refreshTopMatchersConfirmDialog?.source || 'timeline';
    if (!log?.audit_id) {
      closeRefreshTopMatchersConfirmDialog();
      return;
    }
    closeRefreshTopMatchersConfirmDialog();
    await handleRefreshTopMatchers(log, { source });
  }, [closeRefreshTopMatchersConfirmDialog, handleRefreshTopMatchers, refreshTopMatchersConfirmDialog]);

  useEffect(() => () => stopVisionReviewPolling(), [stopVisionReviewPolling]);

  const handleStartUnknownSearch = useCallback(async () => {
    if (!unknownQueryImageBase64) return;
    try {
      setUnknownConfirmOpen(false);
      setUnknownResultMode(false);
      setUnknownMatchRunning(true);
      setUnknownMatchProgress({ processed: 0, total: 0, progress_percent: 0 });

      const startDate = logsDateRange[0].startDate;
      const endDate = logsDateRange[0].endDate;
      const formattedStart = format(startOfDay(startDate), "yyyy-MM-dd'T'HH:mm");
      const formattedEnd = format(endOfDay(endDate), "yyyy-MM-dd'T'HH:mm");

      const started = await startUnknownMatchJob({
        image_base64: unknownQueryImageBase64,
        start_time: formattedStart,
        end_time: formattedEnd,
        name: name.trim(),
        camera,
        similarity_threshold: Number.isFinite(Number(findPersonSimilarityThreshold))
          ? Math.max(0, Math.min(1, Number(findPersonSimilarityThreshold)))
          : undefined,
        include_known_matches: true,
      });
      const jobId = started?.job_id;
      if (!jobId) {
        throw new Error('Failed to start background match job');
      }
      setUnknownMatchJobId(jobId);
      try {
        sessionStorage.setItem(UNKNOWN_MATCH_JOB_STORAGE_KEY, jobId);
      } catch (e) {
        // no-op
      }
      show('Started background search in unmatched logs', 'info');
      startUnknownPolling(jobId);
    } catch (err) {
      setUnknownMatchRunning(false);
      show(err.message || 'Failed to start unknown match search', 'error');
    }
  }, [unknownQueryImageBase64, logsDateRange, name, camera, findPersonSimilarityThreshold, show, startUnknownPolling]);

  const handleUploadMatchChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type || !file.type.startsWith('image/')) {
      show('Please upload a valid image file', 'error');
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(file);
      // Show uploaded image in the same dialog
      setPreviewImageDataUrl(dataUrl);
    } catch (e) {
      show(e.message || 'Failed to read uploaded image', 'error');
    }
  };

  const handleFindPersonFromPreview = async () => {
    if (!previewImageDataUrl) return;
    setImageSourceDialogOpen(false);
    await processImageForMatching(previewImageDataUrl);
    // Clear preview after processing
    setPreviewImageDataUrl(null);
  };

  const handleRefreshFindPersonResults = async () => {
    if (!findPersonQueryImageBase64) {
      show('No previous image found for refresh', 'warning');
      return;
    }
    // Reset to first page when refreshing (especially when threshold changes)
    await processImageForMatching(findPersonQueryImageBase64, { isRefresh: true, limit: pagination.limit, offset: 0 });
  };

  // Handler to open Find Person search icon audit logs modal
  const handleOpenFindPersonSearchIconAuditLogs = async (log, image, stackImages = []) => {
    if (!log || !log.audit_id) return;

    try {
      setFindPersonSearchIconAuditLogsLog(log);
      setFindPersonSearchIconAuditLogsModalOpen(true);
      setFindPersonSearchIconAuditLogsSelectedImage(null);
      setFindPersonSearchIconAuditLogsSelectedAuditId(null);

      // Prepare stack images - include the main log image and all stack images
      const allImages = [];

      // Add main log image
      if (image) {
        allImages.push({ log, image, isMain: true, uniqueId: log.audit_id || `main-${Date.now()}` });
      } else if (log.audit_id) {
        // Fetch main image if not available
        try {
          const blob = await getAuditImageBlob(log.audit_id);
          const dataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          allImages.push({ log, image: dataUrl, isMain: true, uniqueId: log.audit_id });
        } catch (error) {
          console.error('Error loading main image:', error);
        }
      }

      // Add stack images
      if (Array.isArray(stackImages) && stackImages.length > 0) {
        for (let stackIdx = 0; stackIdx < stackImages.length; stackIdx++) {
          const stackItem = stackImages[stackIdx];
          if (stackItem.log && stackItem.log.audit_id && stackItem.log.audit_id !== log.audit_id) {
            let stackImage = stackItem.image;
            if (!stackImage && stackItem.log.audit_id) {
              try {
                const blob = await getAuditImageBlob(stackItem.log.audit_id);
                const dataUrl = await new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result);
                  reader.readAsDataURL(blob);
                });
                stackImage = dataUrl;
              } catch (error) {
                console.error(`Error loading stack image for ${stackItem.log.audit_id}:`, error);
                continue;
              }
            }
            if (stackImage) {
              allImages.push({
                log: stackItem.log,
                image: stackImage,
                isMain: false,
                uniqueId: stackItem.log.audit_id || `stack-${Date.now()}-${stackIdx}`
              });
            }
          }
        }
      }

      setFindPersonSearchIconAuditLogsStackImages(allImages);

      // Auto-select first image if available
      if (allImages.length > 0) {
        const firstItem = allImages[0];
        setFindPersonSearchIconAuditLogsSelectedImage(firstItem.image);
        setFindPersonSearchIconAuditLogsSelectedAuditId(firstItem.uniqueId);
      }
    } catch (error) {
      console.error('Error opening Find Person search audit logs:', error);
      show('Failed to open search', 'error');
    }
  };

  // Handler to close Find Person search icon audit logs modal
  const handleCloseFindPersonSearchIconAuditLogs = () => {
    setFindPersonSearchIconAuditLogsModalOpen(false);
    setFindPersonSearchIconAuditLogsLog(null);
    setFindPersonSearchIconAuditLogsStackImages([]);
    setFindPersonSearchIconAuditLogsSelectedImage(null);
    setFindPersonSearchIconAuditLogsSelectedAuditId(null);
  };

  // Handler to search audit logs based on selected image - triggers Find Person search
  const handleSearchFindPersonSearchIconAuditLogs = async () => {
    if (!findPersonSearchIconAuditLogsSelectedImage) {
      show('Please select an image to search', 'warning');
      return;
    }

    try {
      setFindPersonSearchIconAuditLogsLoading(true);

      // Store the selected image and threshold before closing modal
      const selectedImage = findPersonSearchIconAuditLogsSelectedImage;
      const threshold = findPersonSearchIconAuditLogsThreshold;

      // Set the similarity threshold for UI display
      setFindPersonSimilarityThreshold(threshold);

      // Close the modal immediately
      handleCloseFindPersonSearchIconAuditLogs();

      // Trigger Find Person search using the selected image with the threshold
      // This will display results in the main view exactly like the regular Find Person button
      await processImageForMatching(selectedImage, {
        isRefresh: false,
        similarityThreshold: threshold
      });

    } catch (error) {
      console.error('Error searching audit logs:', error);
      show(error.message || 'Failed to search audit logs', 'error');
    } finally {
      // Always set loading to false after search completes
      setFindPersonSearchIconAuditLogsLoading(false);
    }
  };


  const handleResetImageSource = () => {
    // Stop camera if running
    stopCamera();
    // Clear preview image
    setPreviewImageDataUrl(null);
  };
  const timingDialogResultMeta = getResultMeta(timingDialog.log);
  const timingDialogStack = timingDialog.stack || [];
  const fallbackDialogFrame = timingDialog.log
    ? {
      log: timingDialog.log,
      image: timingDialog.log.audit_id ? peekAuditImage(timingDialog.log.audit_id) : null,
    }
    : null;
  const modalFrames = timingDialogStack.length
    ? timingDialogStack
    : fallbackDialogFrame
      ? [fallbackDialogFrame]
      : [];
  const timingDialogBucketLogs = modalFrames
    .map((frame) => frame?.log)
    .filter(Boolean);
  const timingDialogAttendanceRef = timingDialog.log
    ? resolveBucketAttendanceRef(timingDialog.log, timingDialogBucketLogs)
    : { attendanceId: null, sourceAuditId: null, hasAttendance: false, revoked: false };
  const timingDialogIsMatchedTile = Boolean(timingDialog.log?.success) && timingDialog.log?.confidence_level !== 'possible_match';
  const canTimingDialogRevokeAttendance =
    timingDialogIsMatchedTile &&
    timingDialogAttendanceRef.hasAttendance &&
    !timingDialogAttendanceRef.revoked;
  const timingDialogRevokeLoading = Boolean(
    [timingDialogAttendanceRef.sourceAuditId, timingDialog.log?.audit_id, timingDialogAttendanceRef.attendanceId]
      .filter(Boolean)
      .some((key) => revokingAuditIds.has(key))
  );
  // Check if any frame in the track has send_attendance=True
  const hasTimingDialogTrackAttendance = modalFrames.some(frame => frame.log?.send_attendance === true) || timingDialog.log?.send_attendance === true;

  const timingDialogPrefetchIds = useMemo(() => {
    if (!timingDialog.open) return [];
    const ids = [];
    if (timingDialog.log?.audit_id) ids.push(timingDialog.log.audit_id);
    (timingDialog.stack || []).forEach((frame) => {
      if (frame?.log?.audit_id) ids.push(frame.log.audit_id);
    });
    return ids;
  }, [timingDialog.open, timingDialog.log, timingDialog.stack]);
  useAuditImagePrefetch(timingDialogPrefetchIds, timingDialog.open);

  useEffect(() => () => {
    stopUnknownPolling();
  }, [stopUnknownPolling]);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(track => track.stop());
        cameraStreamRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const savedJobId = (() => {
      try {
        return sessionStorage.getItem(UNKNOWN_MATCH_JOB_STORAGE_KEY) || '';
      } catch (e) {
        return '';
      }
    })();
    if (!savedJobId) return;

    setUnknownMatchJobId(savedJobId);
    setUnknownMatchRunning(true);
    show('Resuming previous background search...', 'info');
    startUnknownPolling(savedJobId, { silent: true });
  }, [show, startUnknownPolling]);
  useEffect(() => {
    personThumbnailsRef.current = personThumbnails;
  }, [personThumbnails]);
  useEffect(() => {
    attendanceSnapshotsRef.current = attendanceSnapshots;
  }, [attendanceSnapshots]);

  useEffect(() => {
    if (!timingDialog.open || !timingDialog.log) return;
    const log = timingDialog.log;
    const capturedTs = log.capturedTs || getCapturedTimestamp(log);
    const apiSubmitTs = log.apiSubmitTs || getApiSubmitTimestamp(log);
    const requestSentTs = log.requestSentTs || getRequestSentTimestamp(log);
    const matchingStartedTs = getMatchingStartedTimestamp(log);
    const apiCompletedTs = getApiCompletedTimestamp(log);
    const toPlain = (ts) => (ts ? { iso: ts.iso || '', ms: ts.ms ?? null } : null);
    const startedMs = log?.matching_started_at_ms ?? log?.request_metadata?.matching_started_at_ms ?? log?.recognize_started_at_ms ?? log?.request_metadata?.recognize_started_at_ms ?? null;
    const completedMs = log?.api_completed_at_ms ?? log?.request_metadata?.api_completed_at_ms ?? null;
    // eslint-disable-next-line no-console
    console.log('[Audit] Timing dialog timestamps', {
      audit_id: log.audit_id,
      captured: toPlain(capturedTs),
      api_submit: toPlain(apiSubmitTs),
      matching_queued: toPlain(requestSentTs),
      matching_started: toPlain(matchingStartedTs),
      matching_completed: toPlain(apiCompletedTs),
      matching_started_at_ms: startedMs,
      api_completed_at_ms: completedMs,
      matching_started_to_completed_ms: getMatchingStartToCompletedDeltaMs(startedMs, completedMs),
    });
    // eslint-disable-next-line no-console
    console.log(
      '[Audit] Matching started → completed',
      `${startedMs} → ${completedMs}`,
      'Δ',
      getMatchingStartToCompletedDeltaMs(startedMs, completedMs)
    );
  }, [timingDialog]);

  const doesAttendanceFilterMatchState = useCallback((attendanceFilterValue) => {
    if (!attendanceFilterValue) return true;
    if (attendanceFilterValue === 'total_in') return aAction === 'check_in' && !aPremisesFilter && !aLateFilter;
    if (attendanceFilterValue === 'total_out') return aAction === 'check_out' && !aPremisesFilter && !aLateFilter;
    if (attendanceFilterValue === 'in_premises') return !aAction && aPremisesFilter === 'in' && !aLateFilter;
    if (attendanceFilterValue === 'out_premises') return !aAction && aPremisesFilter === 'out' && !aLateFilter;
    if (attendanceFilterValue === 'late_entries') return !aAction && !aPremisesFilter && aLateFilter;
    return true;
  }, [aAction, aPremisesFilter, aLateFilter]);

  // Handle navigation from PieChartCard - apply filter from URL params or location state
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const urlFilter = query.get('filter');
    const urlDate = query.get('date');
    const stateFilter = location.state?.filter;
    const stateDate = location.state?.selectedDate;
    const attendanceFilter = location.state?.attendanceFilter;
    const logsNavFilterKey =
      isLogsView && (urlFilter || urlDate || stateFilter || stateDate)
        ? `${urlFilter || ''}|${urlDate || ''}|${stateFilter || ''}|${stateDate || ''}|${location.state?.timestamp || ''}|${location.key || ''}`
        : '';
    const attendanceNavKey = attendanceFilter
      ? `${attendanceFilter}|${location.state?.timestamp || ''}|${location.key || ''}`
      : '';

    console.log('Navigation handler triggered:', {
      urlFilter,
      urlDate,
      stateFilter,
      stateDate,
      isLogsView
    });

    // Only apply filters when on logs view
    if (isLogsView) {
      if (logsNavFilterKey) {
        if (logsNavFilterKeyRef.current !== logsNavFilterKey) {
          logsNavFilterKeyRef.current = logsNavFilterKey;
          logsNavFilterAppliedRef.current = false;
        }
      } else {
        logsNavFilterKeyRef.current = '';
        logsNavFilterAppliedRef.current = true;
        pendingLogsNavFilterRef.current = null;
      }

      // Apply dashboard logs filter only once per navigation intent.
      if (logsNavFilterKey && !logsNavFilterAppliedRef.current) {
        const filterValue = urlFilter || stateFilter;
        const dateValue = urlDate || stateDate;
        const nextStatus = filterValue ? mapDashboardFilterToStatus(filterValue) : null;
        let nextDate = null;
        if (dateValue) {
          try {
            const parsedDate = new Date(dateValue);
            if (!isNaN(parsedDate.getTime())) {
              nextDate = format(parsedDate, 'yyyy-MM-dd');
            }
          } catch (error) {
            console.error('Failed to parse date from URL:', error);
          }
        }
        pendingLogsNavFilterRef.current = {
          key: logsNavFilterKey,
          status: nextStatus,
          date: nextDate
        };

        // Set status filter from URL params or location state
        if (urlFilter || stateFilter) {
          console.log('Setting status to:', nextStatus);
          setStatus(nextStatus);
        }

        // Set date range from URL params or location state
        if (urlDate || stateDate) {
          console.log('Setting date range to:', dateValue);
          try {
            const parsedDate = new Date(dateValue);
            if (!isNaN(parsedDate.getTime())) {
              // Set both start and end date to the same date for single day view
              setLogsDateRange([{
                startDate: parsedDate,
                endDate: parsedDate,
                key: 'selection'
              }]);
            }
          } catch (error) {
            console.error('Failed to parse date from URL:', error);
          }
        }

        // Consume pie-chart navigation params so manual filter changes are not overridden.
        if (urlFilter || urlDate || stateFilter || stateDate) {
          const nextQuery = new URLSearchParams(location.search);
          nextQuery.delete('filter');
          nextQuery.delete('date');
          nextQuery.delete('t');

          const nextState = { ...(location.state || {}) };
          delete nextState.filter;
          delete nextState.selectedDate;

          navigate(
            {
              pathname: location.pathname,
              search: nextQuery.toString() ? `?${nextQuery.toString()}` : '',
            },
            { replace: true, state: nextState }
          );
        }
      }

    } else {
      // Reset the flag when leaving logs view
      filtersInitializedRef.current = false;
      logsNavFilterKeyRef.current = '';
      logsNavFilterAppliedRef.current = true;
      pendingLogsNavFilterRef.current = null;
    }

    // Set attendance filters (for Attendance tab)
    if (attendanceFilter) {
      if (attendanceNavFilterKeyRef.current !== attendanceNavKey) {
        attendanceNavFilterKeyRef.current = attendanceNavKey;
        attendanceNavFilterAppliedRef.current = false;
      }

      // Apply dashboard attendance filter only once per navigation intent.
      if (!attendanceNavFilterAppliedRef.current) {
        if (attendanceFilter === 'total_in') {
          setAAction('check_in');
          setAPremisesFilter('');
          setALateFilter(false);
        } else if (attendanceFilter === 'total_out') {
          setAAction('check_out');
          setAPremisesFilter('');
          setALateFilter(false);
        } else if (attendanceFilter === 'in_premises') {
          setAAction('');
          setAPremisesFilter('in');
          setALateFilter(false);
        } else if (attendanceFilter === 'out_premises') {
          setAAction('');
          setAPremisesFilter('out');
          setALateFilter(false);
        } else if (attendanceFilter === 'late_entries') {
          setAAction('');
          setAPremisesFilter('');
          setALateFilter(true);
        } else {
          attendanceNavFilterAppliedRef.current = true;
        }
      }
    } else if (resolvedView === 'present') {
      attendanceNavFilterKeyRef.current = '';
      attendanceNavFilterAppliedRef.current = true;
    }
  }, [location.search, navigate, location.state, location.key, location.pathname, isLogsView, resolvedView]);

  useEffect(() => {
    if (isLogsView) return;
    const attendanceFilter = location.state?.attendanceFilter;
    if (!attendanceFilter) {
      attendanceNavFilterAppliedRef.current = true;
      return;
    }
    if (doesAttendanceFilterMatchState(attendanceFilter)) {
      attendanceNavFilterAppliedRef.current = true;
    }
  }, [isLogsView, location.state?.attendanceFilter, location.state?.timestamp, doesAttendanceFilterMatchState]);

  useEffect(() => {
    if (!isLogsView) return;
    const pendingFilter = pendingLogsNavFilterRef.current;
    if (!pendingFilter) {
      logsNavFilterAppliedRef.current = true;
      return;
    }
    const selectedDate = logsDateRange?.[0]?.startDate;
    const selectedDateKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
    const statusMatches = pendingFilter.status == null || status === pendingFilter.status;
    const dateMatches = pendingFilter.date == null || selectedDateKey === pendingFilter.date;
    if (statusMatches && dateMatches) {
      logsNavFilterAppliedRef.current = true;
      pendingLogsNavFilterRef.current = null;
    }
  }, [isLogsView, status, logsDateRange]);

  const filteredAttendanceSummaries = useMemo(() => {
    if (!aPremisesFilter) return aSummaries;
    return aSummaries.filter((summary) => {
      const events = summary.recent_events || [];
      if (events.length === 0) return false;
      const sortedEvents = [...events].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const lastEvent = sortedEvents[0];
      if (aPremisesFilter === 'in') return lastEvent.action === 'check_in';
      if (aPremisesFilter === 'out') return lastEvent.action === 'check_out';
      return true;
    });
  }, [aSummaries, aPremisesFilter]);

  const csvEscapeValue = (value) => {
    const stringValue = String(value ?? '');
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const formatExportValue = (field, value) => {
    if (value === null || value === undefined) return '';
    if (field === 'date') return String(value).trim();
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) {
      return value.map((item) => formatExportValue(field, item)).join(' | ');
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }

    const raw = String(value).trim();
    if (!raw) return '';
    if (field?.includes('timestamp') || field?.includes('date') || field?.includes('time') || field === 'created_at') {
      const parsed = new Date(raw);
      if (!Number.isNaN(parsed.getTime())) return formatDateWithSeconds(raw);
    }
    if (raw === 'true' || raw === 'false') return raw === 'true' ? 'Yes' : 'No';
    if ((raw.startsWith('{') && raw.endsWith('}')) || (raw.startsWith('[') && raw.endsWith(']'))) {
      try {
        const parsedJson = JSON.parse(raw);
        return formatExportValue(field, parsedJson);
      } catch {
        return raw;
      }
    }
    return raw;
  };

  // Convert JSON data to CSV format; export headers even if no rows
  const convertJsonToCsv = (data, fieldsOverride = null, headersOverride = null) => {
    const fields = fieldsOverride || data?.fields;
    const headers = headersOverride || data?.headers;
    if (!data || !fields || !headers || fields.length !== headers.length) {
      throw new Error('Invalid export payload');
    }

    const logs = data.logs || [];

    // Create CSV header row using the provided headers
    const csvRows = [];
    csvRows.push(headers.map((header) => csvEscapeValue(header)).join(','));

    // Create CSV data rows (may be empty)
    for (const log of logs) {
      const values = fields.map(field => {
        const value = formatExportValue(field, log?.[field]);
        return csvEscapeValue(value);
      });
      csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
  };

  const downloadAuditXlsxFile = (data, fileName, timestamp) => {
    const fields = data?.fields;
    const headers = data?.headers;
    if (!data || !Array.isArray(fields) || !Array.isArray(headers) || fields.length !== headers.length || !fields.length) {
      throw new Error('Invalid XLSX export payload');
    }
    const logs = Array.isArray(data?.logs) ? data.logs : [];
    const worksheetRows = [
      headers,
      ...logs.map((log) => fields.map((field) => formatExportValue(field, log?.[field]))),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Audit Logs');
    const safeFileName = (fileName || 'audit_logs').trim() || 'audit_logs';
    const safeTimestamp = String(timestamp || '').trim() || new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    XLSX.writeFile(workbook, `${safeFileName}_${safeTimestamp}.xlsx`);
  };

  const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  const blobToObjectUrl = (blob) => window.URL.createObjectURL(blob);
  const revokeObjectUrlIfNeeded = (value) => {
    if (typeof value === 'string' && value.startsWith('blob:')) {
      try {
        window.URL.revokeObjectURL(value);
      } catch (_) {
        // no-op cleanup best effort
      }
    }
  };

  const handleExport = async (formatType = auditExportFormat) => {
    try {
      setExportLoading(true);
      const normalizedFormat = String(formatType || 'csv').toLowerCase() === 'xlsx' ? 'xlsx' : 'csv';
      setAuditExportFormat(normalizedFormat);

      let jsonData;

      // If in custom search mode, export the logs currently displayed
      if ((unknownResultMode || findPersonResultMode) && logs.length > 0) {
        jsonData = await formatUnknownMatchLogsForExport(logs);
        // Format timestamps for custom search mode (backend doesn't format these)
        jsonData.logs.forEach(log => {
          if (log.created_at) {
            log.created_at = formatDateWithSeconds(log.created_at);
          }
        });
      } else {
        // Normal export with filters - backend already formats all datetime fields
        const { formattedStart, formattedEnd } = buildLogsDateTimeRange();
        const logTypeParams = auditLogTypeParams(logType);
        jsonData = await exportAuditLogs({
          start_time: formattedStart,
          end_time: formattedEnd,
          name: name.trim(),
          camera: camera,
          camera_id: cameraId,
          person_id: matchedPersonIdFilter,
          status: status,
          ...logTypeParams,
        });
        // Backend already formats all datetime fields, so no need to format again
      }

      // Create a readable timestamp for filename: YYYY-MM-DD_HH-MM-SS
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      const timestamp = `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
      const baseFileName = 'audit_logs';

      if (normalizedFormat === 'xlsx') {
        downloadAuditXlsxFile(jsonData, baseFileName, timestamp);
      } else {
        // Convert JSON to CSV
        const csvContent = convertJsonToCsv(jsonData);
        // Create blob and download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${baseFileName}_${timestamp}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      const recordCount = Number(jsonData?.total_records || jsonData?.logs?.length || 0);
      show(`Audit logs exported successfully as ${normalizedFormat.toUpperCase()} (${recordCount} records)`, 'success');
    } catch (e) {
      console.error('Export error:', e);
      show('Failed to export audit logs', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  const openAuditExportMenu = (event) => {
    setAuditExportMenuAnchor(event.currentTarget);
  };

  const closeAuditExportMenu = () => {
    setAuditExportMenuAnchor(null);
  };

  const handleChooseAuditExportFormat = async (formatType) => {
    closeAuditExportMenu();
    const normalizedFormat = String(formatType || 'csv').toUpperCase() === 'XLSX' ? 'XLSX' : 'CSV';
    const approved = await confirm({
      title: 'Confirm Export',
      message: `Are you sure you want to export in/out logs as ${normalizedFormat}?`,
      confirmLabel: `Export ${normalizedFormat}`,
      danger: false,
    });
    if (!approved) return;
    handleExport(formatType);
  };

  const fetchAttendanceExportMetadata = useCallback(async () => {
    const metadata = await exportAttendanceLogs({
      meta_only: true,
    });
    const allFields = Array.isArray(metadata?.all_fields)
      ? metadata.all_fields
      : (metadata?.fields || []).map((field, idx) => ({
        field,
        label: metadata?.headers?.[idx] || field,
        enabled: true,
      }));
    if (!allFields.length) {
      throw new Error('No export fields available');
    }
    return allFields;
  }, []);

  const handleOpenAttendanceExportDialog = async () => {
    try {
      setExportAttendanceLoading(true);
      const allFields = await fetchAttendanceExportMetadata();
      const defaultSelection = allFields
        .filter((f) => f.enabled !== false && f.field !== 'presence_time')
        .map((f) => f.field);
      setAttendanceExportFields(allFields);
      // Set initial selection in config order (allFields is already in config order from backend)
      const initialSelection = defaultSelection.length ? defaultSelection : allFields
        .filter((f) => f.field !== 'presence_time')
        .map((f) => f.field);
      setAttendanceExportSelectedFields(initialSelection);
      setAttendanceExportPersonName(aPersonName);
      setAttendanceExportAction(aAction);
      setAttendanceExportPremisesFilter(aPremisesFilter);
      setAttendanceExportLateFilter(aLateFilter);
      setAttendanceExportAllEmployeesPresence(false);
      setAttendanceExportFileName('attendance_logs');
      const startDate = aDateRange[0]?.startDate || new Date();
      const endDate = aDateRange[0]?.endDate || startDate;
      setAttendanceExportDateRange([{ startDate, endDate, key: 'selection' }]);
      setAttendanceExportStartTime(aStartTime || '00:00');
      setAttendanceExportEndTime(aEndTime || '23:59');
      setAttendanceExportDatePickerAnchor(null);
      setAttendanceExportDialogOpen(true);
    } catch (e) {
      console.error('Failed to prepare attendance export:', e);
      show(e.message || 'Failed to load export fields', 'error');
    } finally {
      setExportAttendanceLoading(false);
    }
  };

  const moveSelectedField = (fromIndex, direction) => {
    setAttendanceExportSelectedFields((prev) => {
      const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= prev.length || toIndex >= prev.length) {
        return prev;
      }
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const toggleAttendanceExportField = (fieldName, checked) => {
    setAttendanceExportSelectedFields((prev) => {
      if (checked) {
        if (prev.includes(fieldName)) return prev;
        return [...prev, fieldName];
      }
      return prev.filter((item) => item !== fieldName);
    });
  };

  const buildExportDateTimeRange = () => {
    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    const selection = attendanceExportDateRange[0] || { startDate: new Date(), endDate: new Date() };
    const startDate = selection.startDate || new Date();
    const endDate = selection.endDate || startDate;

    const [startHour, startMinute] = parseTime(attendanceExportStartTime, 0, 0);
    const [endHour, endMinute] = parseTime(attendanceExportEndTime, 23, 59);

    const localStart = new Date(startDate);
    localStart.setHours(startHour, startMinute, 0, 0);
    const localEnd = new Date(endDate);
    localEnd.setHours(endHour, endMinute, 59, 0);

    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    if (startOnly > todayOnly || endOnly > todayOnly) {
      return { error: 'Future date cannot be selected' };
    }
    if (localStart > localEnd) {
      return { error: 'Start date/time cannot be after end date/time' };
    }

    // Same conversion logic as attendance tab filter: treat selected values as IST and convert to UTC.
    const startDateIST = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      startHour,
      startMinute,
      0
    ));
    const startDateTimeUTC = new Date(startDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    const endDateIST = new Date(Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      endHour,
      endMinute,
      59
    ));
    const endDateTimeUTC = new Date(endDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    return {
      formattedStart: startDateTimeUTC.toISOString().slice(0, 16),
      formattedEnd: endDateTimeUTC.toISOString().slice(0, 16),
    };
  };

  const handleExportAttendance = async () => {
    try {
      setExportAttendanceLoading(true);
      const visibleFields = attendanceExportFields.filter((field) => field.enabled !== false);
      const fieldByName = new Map(visibleFields.map((field) => [field.field, field]));
      const orderedSelectedFields = attendanceExportSelectedFields.filter((field) => fieldByName.has(field));
      let exportSelectedFields = [...orderedSelectedFields];
      if (attendanceExportAllEmployeesPresence) {
        if (!exportSelectedFields.includes('date') && fieldByName.has('date')) {
          exportSelectedFields.unshift('date');
        }
        if (!exportSelectedFields.includes('presence_time') && fieldByName.has('presence_time')) {
          // Insert presence_time in the correct position according to config order (after person_name)
          // But respect user's manual ordering for other fields
          const personNameIndex = exportSelectedFields.indexOf('person_name');
          if (personNameIndex >= 0) {
            // Insert after person_name
            exportSelectedFields.splice(personNameIndex + 1, 0, 'presence_time');
          } else {
            // If person_name is not present, find the first field that comes after presence_time in config
            const fieldsAfterPresenceTime = ['company_id', 'branch_id', 'check_in_count', 'check_in_timestamps', 'first_check_in', 'check_out_count', 'check_out_timestamps', 'last_check_out'];
            let insertIndex = exportSelectedFields.length;
            for (let i = 0; i < exportSelectedFields.length; i++) {
              if (fieldsAfterPresenceTime.includes(exportSelectedFields[i])) {
                insertIndex = i;
                break;
              }
            }
            exportSelectedFields.splice(insertIndex, 0, 'presence_time');
          }
        } else if (exportSelectedFields.includes('presence_time')) {
          // If presence_time is already selected, ensure it's in the correct position
          const currentIndex = exportSelectedFields.indexOf('presence_time');
          const personNameIndex = exportSelectedFields.indexOf('person_name');
          if (personNameIndex >= 0 && currentIndex !== personNameIndex + 1) {
            // Remove from current position and insert after person_name
            exportSelectedFields.splice(currentIndex, 1);
            exportSelectedFields.splice(personNameIndex + 1, 0, 'presence_time');
          }
        }
      }

      if (!exportSelectedFields.length) {
        show('Select at least one field to export', 'error');
        return;
      }
      const range = buildExportDateTimeRange();
      if (range?.error) {
        show(range.error, 'error');
        return;
      }
      const { formattedStart, formattedEnd } = range;
      const jsonData = await exportAttendanceLogs({
        person_name: attendanceExportAllEmployeesPresence ? '' : attendanceExportPersonName.trim(),
        action: attendanceExportAction,
        start_time: formattedStart,
        end_time: formattedEnd,
        premises_filter: attendanceExportPremisesFilter,
        late_filter: attendanceExportLateFilter,
        is_intrusion: false, // Only export regular employees (non-intrusion)
        selected_fields: exportSelectedFields,
        include_all_employees_presence: attendanceExportAllEmployeesPresence,
      });

      if (!jsonData?.fields || !jsonData?.headers || jsonData.fields.length !== jsonData.headers.length) {
        console.error('Export attendance: field/header mismatch', jsonData);
        show('Failed to export attendance logs (invalid export config)', 'error');
        return;
      }
      const isEmpty = !jsonData.logs || jsonData.logs.length === 0;
      if (isEmpty) {
        console.warn('Export attendance: no logs returned, exporting headers only', jsonData);
      }

      // Use the sorted field order for CSV export (already sorted above according to config)
      const selectedFieldDefs = exportSelectedFields
        .map((field) => fieldByName.get(field))
        .filter(Boolean);
      const exportFields = selectedFieldDefs.map((field) => field.field);
      const exportHeaders = selectedFieldDefs.map((field) => field.label || field.field);
      // Convert JSON to CSV
      const csvContent = convertJsonToCsv(jsonData, exportFields, exportHeaders);

      // Create blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const safeFileName = (attendanceExportFileName || 'attendance_logs')
        .trim()
        .replace(/[^a-zA-Z0-9_-]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '') || 'attendance_logs';
      link.download = `${safeFileName}_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setAttendanceExportDialogOpen(false);
      show(`Attendance logs exported successfully (${jsonData.total_records || 0} records)`, 'success');
    } catch (e) {
      console.error('Export attendance error:', e);
      show('Failed to export attendance logs', 'error');
    } finally {
      setExportAttendanceLoading(false);
    }
  };

  const attendanceExportVisibleFields = useMemo(
    () => attendanceExportFields.filter((field) => field.enabled !== false),
    [attendanceExportFields]
  );

  const loadAssignedInOutCameraOptions = useCallback(async () => {
    try {
      const res = await getCameraOptions({ usage: 'attendance' });
      const items = Array.isArray(res?.cameras) ? res.cameras : [];
      const byId = new Map();
      items.forEach((cam) => {
        if (cam?.is_active === false) return;
        const type = String(cam?.camera_type || '').trim().toUpperCase();
        if (type !== 'IN' && type !== 'OUT') return;
        const id = String(cam?.camera_id || cam?.id || '').trim();
        if (!id || byId.has(id)) return;
        byId.set(id, {
          id,
          label: String(cam?.label || cam?.camera_label || id).trim() || id,
          camera_type: type,
        });
      });
      const next = Array.from(byId.values()).sort((a, b) => {
        if (a.camera_type !== b.camera_type) return a.camera_type === 'IN' ? -1 : 1;
        return a.label.localeCompare(b.label);
      });
      setAssignedInOutCameras(next);
      return next;
    } catch (err) {
      console.error('Failed to load assigned IN/OUT cameras:', err);
      setAssignedInOutCameras([]);
      return [];
    }
  }, []);

  const ensureAssignedInOutCamerasLoaded = useCallback(() => {
    if (assignedInOutCamerasLoadedRef.current) {
      return Promise.resolve();
    }
    if (assignedInOutCamerasRequestRef.current) {
      return assignedInOutCamerasRequestRef.current;
    }
    assignedInOutCamerasRequestRef.current = loadAssignedInOutCameraOptions()
      .finally(() => {
        assignedInOutCamerasLoadedRef.current = true;
        assignedInOutCamerasRequestRef.current = null;
      });
    return assignedInOutCamerasRequestRef.current;
  }, [loadAssignedInOutCameraOptions]);

  const buildLogsRequestKey = useCallback((limit, offset, rangeOverrides = {}) => {
    const { formattedStart, formattedEnd } = buildLogsDateTimeRange(rangeOverrides);
    const logTypeParams = auditLogTypeParams(logType);
    return JSON.stringify({
      start_time: formattedStart,
      end_time: formattedEnd,
      name: name.trim(),
      camera,
      cameraId,
      person_id: matchedPersonIdFilter,
      status,
      logType,
      visionReviewResult,
      greaterThanTotalTimeMs,
      ...logTypeParams,
      limit,
      offset
    });
  }, [buildLogsDateTimeRange, logType, name, camera, cameraId, matchedPersonIdFilter, status, visionReviewResult, greaterThanTotalTimeMs]);

  const loadLogs = useCallback(async (limit, offset, rangeOverrides = {}) => {
    const query = new URLSearchParams(location.search);
    const notifAuditId = query.get('audit_id') || '';
    const ignoreAuditId = Boolean(rangeOverrides?.ignoreAuditId);

    // When redirected from a notification bell click, use direct audit_id lookup —
    // bypass time-range math entirely for an exact 1-record match.
    if (notifAuditId && !ignoreAuditId) {
      const requestKey = `audit_id:${notifAuditId}`;
      if (inFlightLogsRequestRef.current === requestKey) return;
      inFlightLogsRequestRef.current = requestKey;
      try {
        setLogsLoading(true);
        const res = await getAuditLogs({ audit_id: notifAuditId, limit, offset });
        const arr = Array.isArray(res?.logs) ? res.logs
          : Array.isArray(res) ? res
            : Array.isArray(res?.data) ? res.data
              : Array.isArray(res?.results) ? res.results
                : [];
        const total = res.pagination?.total || res.total || 0;
        setLogs(arr);
        setPagination({ limit, offset, current_page: Math.floor(offset / limit) + 1, total });

        // Sync date picker to the record's event day (notification deep-link has no date in URL).
        const matchedLog = arr.find((log) => String(log?.audit_id || '') === String(notifAuditId)) || arr[0];
        const eventDate = matchedLog ? getLogEventDate(matchedLog) : null;
        if (eventDate) {
          const nextRange = [{ startDate: eventDate, endDate: eventDate, key: 'selection' }];
          setLogsDateRange(nextRange);
          setLogsDateDraftRange(nextRange);
        }
      } catch (e) {
        console.error('Failed to load audit log by audit_id:', e);
        show('Failed to load audit log', 'error');
      } finally {
        setLogsLoading(false);
        if (inFlightLogsRequestRef.current === `audit_id:${notifAuditId}`) {
          inFlightLogsRequestRef.current = null;
        }
      }
      return;
    }

    const { formattedStart, formattedEnd } = buildLogsDateTimeRange(rangeOverrides);
    const logTypeParams = auditLogTypeParams(logType);
    const requestKey = buildLogsRequestKey(limit, offset, rangeOverrides);
    if (inFlightLogsRequestRef.current === requestKey) {
      return;
    }
    inFlightLogsRequestRef.current = requestKey;
    try {
      setLogsLoading(true);

      console.log('Loading audit logs with filters:', {
        start_time: formattedStart,
        end_time: formattedEnd,
        name: name.trim(),
        camera: camera,
        camera_id: cameraId,
        person_id: matchedPersonIdFilter,
        status,
        vision_review_result: visionReviewResult,
        logType,
        greaterThanTotalTimeMs,
        ...logTypeParams,
        limit,
        offset
      });

      const res = await getAuditLogs({
        start_time: formattedStart,
        end_time: formattedEnd,
        name: name.trim(),
        camera: camera,
        camera_id: cameraId,
        person_id: matchedPersonIdFilter,
        status,
        vision_review_result: visionReviewResult,
        greater_than_total_time: greaterThanTotalTimeMs,
        ...logTypeParams,
        limit,
        offset
      });
      // eslint-disable-next-line no-console
      console.log('[Audit] Raw audit logs response (as received):', res);
      const arr = Array.isArray(res?.logs)
        ? res.logs
        : Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.results)
              ? res.results
              : [];
      const total = res.pagination?.total || res.total || 0; // Extract total from pagination metadata

      console.log('Audit logs loaded:', { total, count: arr.length });

      setLogs(arr);
      setPagination({ limit, offset, current_page: Math.floor(offset / limit) + 1, total: total });
    } catch (e) {
      console.error('Failed to load audit logs:', e);
      show('Failed to load audit logs', 'error');
    } finally {
      setLogsLoading(false);
      if (inFlightLogsRequestRef.current === requestKey) {
        inFlightLogsRequestRef.current = null;
      }
    }
  }, [buildLogsRequestKey, name, camera, cameraId, matchedPersonIdFilter, status, visionReviewResult, logType, greaterThanTotalTimeMs, buildLogsDateTimeRange, location.search, show]);

  useEffect(() => {
    loadLogsRef.current = () => loadLogs(pagination.limit, pagination.offset);
  }, [loadLogs, pagination.limit, pagination.offset]);
  // Load logs when on logs view (match or unmatch)
  useEffect(() => {
    console.log('Load logs useEffect triggered:', {
      isLogsView,
      name,
      camera,
      status,
      logType,
      pagination,
      filtersInitialized: filtersInitializedRef.current
    });

    if (isLogsView) {
      // Don't load logs if we're navigating with findPersonImage (will be handled by find person useEffect)
      if (location.state?.findPersonImage) {
        lastAutoLoadLogsRequestKeyRef.current = '';
        return;
      }

      if (unknownResultMode || findPersonResultMode) {
        lastAutoLoadLogsRequestKeyRef.current = '';
        return;
      }
      if (!logsNavFilterAppliedRef.current) {
        return;
      }
      // If we just navigated with URL filters, wait for them to be applied
      // The status/dateRange state updates will trigger this effect again
      const query = new URLSearchParams(location.search);
      const notificationAuditId = query.get('audit_id') || '';
      const urlFilter = query.get('filter');
      const urlDate = query.get('date');

      if ((urlFilter || urlDate) && filtersInitializedRef.current) {
        // We have URL params and they're being initialized, but state might not be updated yet
        // Check if the current state matches the URL params
        const normalizedUrlFilter = urlFilter?.toLowerCase();
        const dateMatches = !urlDate || format(logsDateRange[0].startDate, 'yyyy-MM-dd') === urlDate;
        const statusMatches = !urlFilter || status === normalizedUrlFilter;

        console.log('URL filter check:', { urlFilter, urlDate, status, dateMatches, statusMatches });

        if (!dateMatches || !statusMatches) {
          console.log('Waiting for filters to be applied from URL params...');
          return; // Skip this render, wait for state to update
        }
      }

      const autoRequestKey = notificationAuditId
        ? `audit_id:${notificationAuditId}`
        : buildLogsRequestKey(pagination.limit, 0);
      if (lastAutoLoadLogsRequestKeyRef.current === autoRequestKey) {
        return;
      }
      lastAutoLoadLogsRequestKeyRef.current = autoRequestKey;

      loadLogs(pagination.limit, 0);
    } else {
      lastAutoLoadLogsRequestKeyRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogsView, name, camera, cameraId, logsDateRange, status, visionReviewResult, logType, greaterThanTotalTimeMs, matchedPersonIdFilter, unknownResultMode, findPersonResultMode, location.search, location.state, pagination.limit, buildLogsRequestKey]);

  // Auto-trigger find person when navigating from employee page
  useEffect(() => {
    if (isLogsView && location.state?.findPersonImage) {
      const { findPersonImage, personId, personName, similarityThreshold } = location.state;

      // Clear old logs immediately before doing anything else
      setLogs([]);
      setPagination((prev) => ({
        ...prev,
        offset: 0,
        current_page: 1,
        total: 0,
      }));

      // Set find person result mode immediately to prevent loadLogs from running
      setFindPersonResultMode(true);
      setUnknownResultMode(false);

      // Clear the state to avoid re-triggering on re-renders
      navigate(location.pathname, { replace: true, state: {} });

      // Clear any existing find person state (but keep result mode true)
      setMatchedPersonIdFilter('');
      setMatchedPersonName('');
      setFindPersonSummary({
        known_count: 0,
        possible_match_count: 0,
        unknown_count: 0,
        total_count: 0,
      });

      // findPersonImage is already a full data URL from FileReader
      setFindPersonQueryImageBase64(findPersonImage);
      if (similarityThreshold !== undefined) {
        setFindPersonSimilarityThreshold(String(similarityThreshold));
      }
      if (personId) {
        setMatchedPersonIdFilter(personId);
      }
      if (personName) {
        setMatchedPersonName(personName);
      }

      // Set loading state
      setUploadMatchLoading(true);

      // Trigger the find person process
      processImageForMatching(findPersonImage, { isRefresh: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogsView, location.state]);

  // Auto-refresh Find Person results when similarity threshold or filters change (not on first entry into result mode)
  useEffect(() => {
    // Skip auto-refresh if Apply button was just clicked (it handles the refresh)
    if (dateRangeApplyClickedRef.current) {
      return;
    }
    const enteredResultMode =
      findPersonResultMode && !prevFindPersonResultModeRef.current;
    prevFindPersonResultModeRef.current = findPersonResultMode;
    if (enteredResultMode) {
      return;
    }
    // Only auto-refresh if we're in find person result mode and have an image
    if (findPersonResultMode && findPersonQueryImageBase64 && !uploadMatchLoading && !refreshingFindPerson) {
      // Use a small delay to debounce rapid changes (e.g., slider dragging)
      const timeoutId = setTimeout(() => {
        processImageForMatching(findPersonQueryImageBase64, { isRefresh: true, limit: pagination.limit, offset: 0 });
      }, 300); // 300ms debounce

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findPersonSimilarityThreshold, name, camera, findPersonResultMode, findPersonQueryImageBase64]);

  // Attendance loaders
  const loadAttendanceSummary = useCallback(async (limit, offset) => {
    try {
      setALoading(true);
      const { formattedStart, formattedEnd } = buildAttendanceDateTimeRange();

      const premisesForApi =
        aPremisesFilter ||
        (aAction === 'check_in' ? 'in' : aAction === 'check_out' ? 'out' : '');
      const res = await getAttendanceSummary({
        person_name: aPersonName.trim(),
        action: '',
        start_time: formattedStart,
        end_time: formattedEnd,
        limit,
        offset,
        premises_filter: premisesForApi,
        late_filter: aLateFilter,
        sort_order: aSortOrder,
        is_intrusion: false, // Only show regular employees (non-intrusion)
      });
      const arr = res.summaries || [];
      const total = res.pagination?.total || res.total || 0;
      setASummaries(arr);
      setAPagination({ limit, offset, current_page: Math.floor(offset / limit) + 1, total });
    } catch (e) {
      console.error(e);
      show('Failed to summarise attendance', 'error');
    } finally {
      setALoading(false);
    }
  }, [aPersonName, aAction, aPremisesFilter, aLateFilter, aSortOrder, buildAttendanceDateTimeRange]);

  useEffect(() => {
    if (!isLogsView) {
      // Skip API call if we have an attendanceFilter in location state that hasn't been applied yet
      const attendanceFilter = location.state?.attendanceFilter;
      if (attendanceFilter && !attendanceNavFilterAppliedRef.current) {
        if (attendanceFilter === 'late_entries' && !aLateFilter) {
          // Wait for the filter-setting useEffect to apply the filter first
          return;
        }
        if (attendanceFilter === 'total_in' && aAction !== 'check_in') {
          return;
        }
        if (attendanceFilter === 'total_out' && aAction !== 'check_out') {
          return;
        }
        if (attendanceFilter === 'in_premises' && aPremisesFilter !== 'in') {
          return;
        }
        if (attendanceFilter === 'out_premises' && aPremisesFilter !== 'out') {
          return;
        }
      }
      loadAttendanceSummary(aPagination.limit, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogsView, aPersonName, aAction, aPremisesFilter, aLateFilter, aSortOrder, location.state?.attendanceFilter]);

  useEffect(() => {
    if (!Array.isArray(aSummaries) || aSummaries.length === 0) {
      return;
    }
    const personIds = Array.from(new Set(aSummaries.map((summary) => summary?.person_id).filter(Boolean)));
    const pendingPersonIds = personIds.filter(
      (pid) => personThumbnailsRef.current[pid] === undefined && !thumbnailRequestsRef.current.has(pid)
    );
    if (!pendingPersonIds.length) return;

    pendingPersonIds.forEach((pid) => thumbnailRequestsRef.current.add(pid));
    let cancelled = false;
    const MAX_CONCURRENT_FETCHES = 4;
    const BATCH_COMMIT_SIZE = 6;
    let nextIndex = 0;
    let pendingUpdates = {};
    let pendingCount = 0;

    const flushPending = () => {
      if (!pendingCount || cancelled) return;
      const updates = pendingUpdates;
      pendingUpdates = {};
      pendingCount = 0;
      setPersonThumbnails((prev) => {
        let changed = false;
        const next = { ...prev };
        Object.entries(updates).forEach(([pid, value]) => {
          if (next[pid] !== undefined) {
            revokeObjectUrlIfNeeded(value);
            return;
          }
          next[pid] = value;
          changed = true;
        });
        return changed ? next : prev;
      });
    };

    const queueUpdate = (pid, value) => {
      pendingUpdates[pid] = value;
      pendingCount += 1;
      if (pendingCount >= BATCH_COMMIT_SIZE) {
        flushPending();
      }
    };

    const fetchOne = async (pid) => {
      try {
        const faces = await getPersonFaces(pid);
        const faceList = Array.isArray(faces) ? faces : [];
        const preferredFace = faceList.find((face) => !face.deleted_at) || faceList[0];
        if (!preferredFace) {
          queueUpdate(pid, null);
          return;
        }
        const blob = await getFaceImage(preferredFace.face_id);
        const objectUrl = blobToObjectUrl(blob);
        if (cancelled) {
          revokeObjectUrlIfNeeded(objectUrl);
          return;
        }
        queueUpdate(pid, objectUrl);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load attendance thumbnail:', err);
          queueUpdate(pid, null);
        }
      } finally {
        thumbnailRequestsRef.current.delete(pid);
      }
    };

    const worker = async () => {
      while (!cancelled) {
        const current = nextIndex;
        nextIndex += 1;
        if (current >= pendingPersonIds.length) break;
        await fetchOne(pendingPersonIds[current]);
      }
    };

    const workerCount = Math.min(MAX_CONCURRENT_FETCHES, pendingPersonIds.length);
    Promise.all(Array.from({ length: workerCount }, () => worker())).finally(() => {
      flushPending();
    });

    return () => {
      cancelled = true;
      pendingPersonIds.forEach((pid) => thumbnailRequestsRef.current.delete(pid));
      Object.values(pendingUpdates).forEach(revokeObjectUrlIfNeeded);
      pendingUpdates = {};
      pendingCount = 0;
    };
  }, [aSummaries]);
  useEffect(() => {
    if (!Array.isArray(aSummaries) || aSummaries.length === 0) {
      return;
    }
    const auditSnapshotIds = new Set();
    const attendanceImageIds = new Set();
    aSummaries.forEach((summary) => {
      (summary.recent_events || []).forEach((evt) => {
        if (evt.snapshot_audit_id) {
          auditSnapshotIds.add(evt.snapshot_audit_id);
        } else if (evt.audit_id) {
          auditSnapshotIds.add(evt.audit_id);
        }
        if (evt.image_available && evt.attendance_id) {
          attendanceImageIds.add(evt.attendance_id);
        }
      });
    });
    const tasks = [];
    auditSnapshotIds.forEach((auditId) => {
      if (!auditId) return;
      const cacheKey = String(auditId);
      if (
        attendanceSnapshotsRef.current[cacheKey] !== undefined ||
        attendanceSnapshotRequestsRef.current.has(cacheKey)
      ) {
        return;
      }
      tasks.push({ type: 'audit', id: auditId, cacheKey });
    });
    attendanceImageIds.forEach((attendanceId) => {
      if (!attendanceId) return;
      const cacheKey = `attendance:${attendanceId}`;
      if (
        attendanceSnapshotsRef.current[cacheKey] !== undefined ||
        attendanceSnapshotRequestsRef.current.has(cacheKey)
      ) {
        return;
      }
      tasks.push({ type: 'attendance', id: attendanceId, cacheKey });
    });
    if (!tasks.length) return;

    tasks.forEach((task) => attendanceSnapshotRequestsRef.current.add(task.cacheKey));
    let cancelled = false;
    const MAX_CONCURRENT_FETCHES = 6;
    const BATCH_COMMIT_SIZE = 8;
    let nextIndex = 0;
    let pendingUpdates = {};
    let pendingCount = 0;

    const flushPending = () => {
      if (!pendingCount || cancelled) return;
      const updates = pendingUpdates;
      pendingUpdates = {};
      pendingCount = 0;
      setAttendanceSnapshots((prev) => {
        let changed = false;
        const next = { ...prev };
        Object.entries(updates).forEach(([cacheKey, value]) => {
          if (next[cacheKey] !== undefined) {
            revokeObjectUrlIfNeeded(value);
            return;
          }
          next[cacheKey] = value;
          changed = true;
        });
        return changed ? next : prev;
      });
    };

    const queueUpdate = (cacheKey, value) => {
      pendingUpdates[cacheKey] = value;
      pendingCount += 1;
      if (pendingCount >= BATCH_COMMIT_SIZE) {
        flushPending();
      }
    };

    const fetchOne = async (task) => {
      try {
        const blob = task.type === 'audit'
          ? await getAuditImageBlob(task.id)
          : await getAttendanceImageBlob(task.id);
        const objectUrl = blobToObjectUrl(blob);
        if (cancelled) {
          revokeObjectUrlIfNeeded(objectUrl);
          return;
        }
        queueUpdate(task.cacheKey, objectUrl);
      } catch (err) {
        if (!cancelled) {
          if (task.type === 'audit') {
            console.error('Failed to load attendance snapshot:', err);
          } else {
            console.error('Failed to load attendance image:', err);
          }
          queueUpdate(task.cacheKey, null);
        }
      } finally {
        attendanceSnapshotRequestsRef.current.delete(task.cacheKey);
      }
    };

    const worker = async () => {
      while (!cancelled) {
        const current = nextIndex;
        nextIndex += 1;
        if (current >= tasks.length) break;
        await fetchOne(tasks[current]);
      }
    };

    const workerCount = Math.min(MAX_CONCURRENT_FETCHES, tasks.length);
    Promise.all(Array.from({ length: workerCount }, () => worker())).finally(() => {
      flushPending();
    });

    return () => {
      cancelled = true;
      tasks.forEach((task) => attendanceSnapshotRequestsRef.current.delete(task.cacheKey));
      Object.values(pendingUpdates).forEach(revokeObjectUrlIfNeeded);
      pendingUpdates = {};
      pendingCount = 0;
    };
  }, [aSummaries]);

  useEffect(() => {
    return () => {
      Object.values(attendanceSnapshotsRef.current || {}).forEach(revokeObjectUrlIfNeeded);
      Object.values(personThumbnailsRef.current || {}).forEach(revokeObjectUrlIfNeeded);
    };
  }, []);

  const normaliseMs = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const num = Number(value);
    if (Number.isNaN(num)) return null;
    return Math.trunc(num);
  };

  const msToIsoString = (value) => {
    const num = normaliseMs(value);
    if (num === null) return '';
    const date = new Date(num);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString();
  };

  const parseIsoToEpochMs = (value) => {
    if (!value && value !== 0) return Number.NaN;
    const text = String(value).trim();
    if (!text) return Number.NaN;
    const hasTimezone = /([+-]\d{2}:?\d{2}|Z)$/i.test(text);
    // If no timezone indicator, treat the timestamp as UTC by appending Z
    // This ensures database timestamps (stored as UTC) are parsed correctly
    const normalizedText = hasTimezone ? text : `${text}Z`;
    const parsed = Date.parse(normalizedText);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
    return Number.NaN;
  };

  const resolveTimestamp = (isoValue, msValue, fallbackIso) => {
    let resolvedIso = isoValue || '';
    let resolvedMs = normaliseMs(msValue);
    if (!resolvedIso) {
      const fromMs = msToIsoString(resolvedMs);
      if (fromMs) resolvedIso = fromMs;
    }
    if (!resolvedIso && fallbackIso) {
      resolvedIso = fallbackIso;
    }
    if ((resolvedMs === null || resolvedMs === undefined) && resolvedIso) {
      const parsed = parseIsoToEpochMs(resolvedIso);
      if (!Number.isNaN(parsed)) {
        resolvedMs = parsed;
      }
    }
    if (resolvedMs !== null && resolvedMs !== undefined) {
      const canonicalIso = msToIsoString(resolvedMs);
      if (canonicalIso) {
        resolvedIso = canonicalIso;
      }
    } else if (resolvedIso) {
      const parsed = parseIsoToEpochMs(resolvedIso);
      if (!Number.isNaN(parsed)) {
        resolvedMs = parsed;
        const canonicalIso = msToIsoString(resolvedMs);
        if (canonicalIso) {
          resolvedIso = canonicalIso;
        }
      }
    }
    return { iso: resolvedIso, ms: resolvedMs };
  };

  const getCapturedTimestamp = (log) => {
    const meta = log.request_metadata || {};
    return resolveTimestamp(
      log.captured_at || meta.captured_at,
      log.captured_at_ms ?? meta.captured_at_ms,
      log.created_at
    );
  };

  const getRequestSentTimestamp = (log) => {
    const meta = log.request_metadata || {};
    return resolveTimestamp(
      log.recognize_started_at ?? meta.recognize_started_at,
      log.recognize_started_at_ms ?? meta.recognize_started_at_ms ?? null,
      null
    );
  };

  const getApiSubmitTimestamp = (log) => {
    const meta = log.request_metadata || {};
    return resolveTimestamp(
      log.api_submit_at || meta.api_submit_at,
      log.api_submit_at_ms ?? meta.api_submit_at_ms,
      log.created_at
    );
  };

  const getApiReceivedTimestamp = (log) => {
    const meta = log.request_metadata || {};
    return resolveTimestamp(
      log.api_received_at ?? meta.api_received_at ?? null,
      log.api_received_at_ms ?? meta.api_received_at_ms ?? null,
      null
    );
  };

  const getApiCompletedTimestamp = (log) => {
    const meta = log.request_metadata || {};
    return resolveTimestamp(
      log.api_completed_at ?? meta.api_completed_at ?? null,
      log.api_completed_at_ms ?? meta.api_completed_at_ms ?? null,
      null
    );
  };

  const getMatchingStartedTimestamp = (log) => {
    const meta = log.request_metadata || {};
    const matchingStarted = resolveTimestamp(
      log.matching_started_at ?? meta.matching_started_at ?? null,
      log.matching_started_at_ms ?? meta.matching_started_at_ms ?? null,
      null
    );
    if (matchingStarted.iso || matchingStarted.ms != null) return matchingStarted;
    return getRequestSentTimestamp(log);
  };

  const getMatchingStartToCompletedDeltaMs = (matchingStartedAtMs, apiCompletedAtMs) => {
    if (matchingStartedAtMs == null || apiCompletedAtMs == null) return null;
    const started = Number(matchingStartedAtMs);
    const completed = Number(apiCompletedAtMs);
    if (Number.isNaN(started) || Number.isNaN(completed)) return null;
    if (completed < started) return null;
    return completed - started;
  };

  const openTimingDialog = async ({ log, stack = [] }) => {
    const auditId = log?.audit_id;
    setTimingDialog({ open: true, log, stack, detailLoading: Boolean(auditId) });
    setTimingDialogPossibleMatchSelectedId(null);
    setTimingDialogPossibleMatchManualCompanyId('');
    setTimingDialogPossibleMatchCandidateImages({});
    if (!auditId) return;

    try {
      const next = await hydrateTimingDialogFromDetail(log, stack);
      setTimingDialog((prev) => {
        if (!prev.open || String(prev.log?.audit_id || '') !== String(auditId)) {
          return prev;
        }
        return { open: true, ...next };
      });
    } catch (e) {
      console.error('Failed to load audit log detail:', e);
      setTimingDialog((prev) => (
        prev.open && String(prev.log?.audit_id || '') === String(auditId)
          ? { ...prev, detailLoading: false }
          : prev
      ));
    }
  };

  const closeTimingDialog = () => {
    setTimingDialog({ open: false, log: null, stack: [], detailLoading: false });
    setTimingDialogPossibleMatchSelectedId(null);
    setTimingDialogPossibleMatchManualCompanyId('');
    setTimingDialogPossibleMatchCandidateImages({});
  };

  const handlePageChange = useCallback((e, page) => {
    const newOffset = (page - 1) * pagination.limit;
    // If in find-person result mode, reload find-person results with pagination
    if (findPersonResultMode && findPersonQueryImageBase64) {
      processImageForMatching(findPersonQueryImageBase64, { isRefresh: true, limit: pagination.limit, offset: newOffset });
    } else {
      loadLogs(pagination.limit, newOffset);
    }
  }, [pagination.limit, loadLogs, findPersonResultMode, findPersonQueryImageBase64, processImageForMatching]);

  const handleAttendancePageChange = useCallback((e, page) => {
    const newOffset = (page - 1) * aPagination.limit;
    loadAttendanceSummary(aPagination.limit, newOffset);
  }, [aPagination.limit, loadAttendanceSummary]);

  const handleOpenLogsDatePicker = useCallback((event) => {
    const startDate = logsDateRange[0]?.startDate ? new Date(logsDateRange[0].startDate) : new Date();
    const endDate = logsDateRange[0]?.endDate ? new Date(logsDateRange[0].endDate) : startDate;
    setLogsDateDraftRange([{ startDate, endDate, key: 'selection' }]);
    setLogsStartTimeDraft(logsStartTime);
    setLogsEndTimeDraft(logsEndTime);
    setLogsDatePickerAnchor(event.currentTarget);
  }, [logsDateRange, logsStartTime, logsEndTime]);

  const handleApplyLogsDateFilters = useCallback(() => {
    dateRangeApplyClickedRef.current = true;
    clearAuditDeepLinkOnNextUrlSyncRef.current = true;
    const appliedDateRange = [{
      startDate: logsDateDraftRange[0]?.startDate || logsDateRange[0]?.startDate || new Date(),
      endDate: logsDateDraftRange[0]?.endDate || logsDateDraftRange[0]?.startDate || logsDateRange[0]?.endDate || new Date(),
      key: 'selection',
    }];
    const appliedStartTime = logsStartTimeDraft;
    const appliedEndTime = logsEndTimeDraft;

    const nextParams = new URLSearchParams(location.search);
    nextParams.delete('audit_id');
    nextParams.delete('view');
    nextParams.delete('start_time');
    nextParams.delete('end_time');
    nextParams.delete('logs_start_date');
    nextParams.delete('logs_end_date');
    nextParams.set('start_date', format(appliedDateRange[0].startDate, 'yyyy-MM-dd'));
    nextParams.set('end_date', format(appliedDateRange[0].endDate, 'yyyy-MM-dd'));
    if (appliedStartTime && appliedStartTime !== '00:00') {
      nextParams.set('logs_start_time', appliedStartTime);
    } else {
      nextParams.delete('logs_start_time');
    }
    if (appliedEndTime && appliedEndTime !== '23:59') {
      nextParams.set('logs_end_time', appliedEndTime);
    } else {
      nextParams.delete('logs_end_time');
    }
    navigate(
      {
        pathname: location.pathname,
        search: nextParams.toString() ? `?${nextParams.toString()}` : '',
      },
      { replace: true, state: location.state }
    );

    setLogsDateRange(appliedDateRange);
    setLogsStartTime(appliedStartTime);
    setLogsEndTime(appliedEndTime);
    setLogsDatePickerAnchor(null);

    // In find-person mode (including search-icon flow), Apply should refresh via find-person API.
    if (findPersonResultMode && findPersonQueryImageBase64) {
      processImageForMatching(findPersonQueryImageBase64, {
        isRefresh: true,
        limit: pagination.limit,
        offset: 0,
        dateRange: appliedDateRange,
        startTime: appliedStartTime,
        endTime: appliedEndTime,
      });
    } else {
      loadLogs(pagination.limit, 0, {
        dateRange: appliedDateRange,
        startTime: appliedStartTime,
        endTime: appliedEndTime,
        ignoreAuditId: true,
      });
    }

    window.setTimeout(() => {
      dateRangeApplyClickedRef.current = false;
    }, 0);
  }, [
    findPersonResultMode,
    findPersonQueryImageBase64,
    loadLogs,
    logsDateDraftRange,
    logsDateRange,
    logsEndTimeDraft,
    logsStartTimeDraft,
    location.pathname,
    location.search,
    location.state,
    navigate,
    pagination.limit,
    processImageForMatching,
  ]);

  const handleTrainClick = (event, auditId) => {
    setTrainMenuAnchor(event.currentTarget);
    setTrainAuditId(auditId);
  };

  const handleTrainClose = () => {
    if (possibleMatchTrainActiveRef.current) {
      closePossibleMatchModal();
      possibleMatchTrainActiveRef.current = false;
    }
    setTrainMenuAnchor(null);
    setTrainAuditId(null);
  };

  const normalizeEmployeeOption = useCallback((emp) => {
    const nested = (emp && typeof emp === 'object' && typeof emp.employee === 'object') ? emp.employee : {};
    const rawId = emp?.employee_id || emp?.id || nested?.employee_id || nested?.id || emp?.person_id || nested?.person_id;
    const person_id = rawId ? String(rawId) : '';
    const firstName = emp?.first_name || nested?.first_name || '';
    const lastName = emp?.last_name || nested?.last_name || '';
    const joinedName = `${firstName} ${lastName}`.trim();
    const name = emp?.full_name || nested?.full_name || emp?.name || nested?.name || joinedName || (emp?.employee_code ? `Employee ${emp.employee_code}` : '');
    const employee_code = emp?.employee_code || nested?.employee_code || '';
    const company_id = emp?.company_id || nested?.company_id || '';
    const branch_id = emp?.branch_id || nested?.branch_id || '';
    return person_id
      ? {
        person_id,
        name: name || `Employee ${person_id}`,
        employee_code,
        company_id,
        branch_id,
      }
      : null;
  }, []);

  const loadCompanyOptions = useCallback(async () => {
    if (companyOptionsLoadedRef.current) return;
    companyOptionsLoadedRef.current = true;
    try {
      const res = await getCompanyPickerConfig(permissions);
      const configs = res?.configs || (res ? [res] : []);
      if (configs.length) {
        setCompanyOptions(configs);
        const defaultId = res?.default_config_id;
        const defaultCfg = configs.find((c) => c.config_id === defaultId) || configs[0];
        if (!employeeCompanyId && defaultCfg?.company_id) {
          setEmployeeCompanyId(defaultCfg.company_id);
        }
        if (!markAttendanceCompanyId && defaultCfg?.company_id) {
          setMarkAttendanceCompanyId(defaultCfg.company_id);
        }
      }
    } catch (err) {
      console.debug('Could not load company configs for training', err);
    }
  }, [employeeCompanyId, markAttendanceCompanyId, permissions]);

  const isTimingDialogPossibleMatchOpen =
    timingDialog.open && timingDialog.log?.confidence_level === 'possible_match';

  const loadEmployees = useCallback(async (companyIdOverride = null) => {
    if (
      (markAttendanceDialog || possibleMatchModal.open || isTimingDialogPossibleMatchOpen) &&
      companyIdOverride === ''
    ) {
      setEmployees([]);
      setEmployeesLoading(false);
      return;
    }
    // Determine which company ID to use based on which dialog is open
    let targetCompanyId = companyIdOverride;
    if (targetCompanyId === null) {
      if (markAttendanceDialog) {
        targetCompanyId = markAttendanceCompanyId || (companyOptions[0]?.company_id || '');
      } else if (possibleMatchModal.open) {
        targetCompanyId =
          possibleMatchManualCompanyId ||
          (companyOptions.length === 1 ? companyOptions[0]?.company_id || '' : '');
      } else if (isTimingDialogPossibleMatchOpen) {
        targetCompanyId =
          timingDialogPossibleMatchManualCompanyId ||
          (companyOptions.length === 1 ? companyOptions[0]?.company_id || '' : '');
      } else {
        targetCompanyId = employeeCompanyId || (companyOptions[0]?.company_id || '');
      }
    }
    const isOtherSelection = targetCompanyId === OTHER_COMPANY_OPTION_VALUE;
    const requestedCompanyId = isOtherSelection ? NULL_COMPANY_FILTER_VALUE : (targetCompanyId || null);
    const cacheKey = isOtherSelection ? OTHER_COMPANY_OPTION_VALUE : (targetCompanyId || 'default');
    const cached = employeesCacheRef.current[cacheKey];
    if (cached && Array.isArray(cached)) {
      setEmployees(cached);
      setEmployeesLoading(false);
      return;
    }
    if (employeesRequestRef.current === cacheKey) {
      // Avoid duplicate in-flight fetches for same company
      return;
    }
    setEmployeesLoading(true);
    employeesRequestRef.current = cacheKey;
    try {
      const pageSize = 200;
      let page = 1;
      let combined = [];
      // Fetch all pages from HRMS so autocomplete can show every employee
      while (true) {
        let records = [];
        let totalPages = 1;
        if (isOtherSelection) {
          // Use persons/names endpoint for "Other" (employees with company_id IS NULL)
          if (page > 1) {
            records = [];
            totalPages = 1;
          } else {
            // Only get non-intrusion employee names (is_intrusion=False)
            totalPages = 1;
          }
        } else {
          const res = await fetchHrmsEmployees({ page, limit: pageSize, companyId: requestedCompanyId });
          records = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.employees)
              ? res.employees
              : Array.isArray(res?.records)
                ? res.records
                : Array.isArray(res?.list)
                  ? res.list
                  : Array.isArray(res?.items)
                    ? res.items
                    : [];
          const meta = res?.meta || res?.pagination || {};
          totalPages = parseInt(meta.count ?? meta.total_pages ?? 0, 10) || 1;
        }
        combined = combined.concat(records);
        if (!records.length || page >= totalPages) {
          break;
        }
        page += 1;
      }
      const unique = [];
      const seen = new Set();
      combined.forEach((emp) => {
        const normalized = normalizeEmployeeOption(emp);
        if (normalized && !seen.has(normalized.person_id)) {
          seen.add(normalized.person_id);
          unique.push(normalized);
        }
      });
      setEmployees(unique);
      employeesCacheRef.current[cacheKey] = unique;
    } catch (e) {
      console.error('Failed to load employees:', e);
      show('Failed to load employees from company records', 'error');
    } finally {
      setEmployeesLoading(false);
      employeesRequestRef.current = null;
    }
  }, [
    companyOptions,
    employeeCompanyId,
    markAttendanceCompanyId,
    possibleMatchManualCompanyId,
    timingDialogPossibleMatchManualCompanyId,
    markAttendanceDialog,
    possibleMatchModal.open,
    isTimingDialogPossibleMatchOpen,
    normalizeEmployeeOption,
    show,
  ]);

  // Load employees for autocomplete from company records instead of enrolled faces
  useEffect(() => {
    if (
      !addToEmployeeDialog &&
      !presentDialogOpen &&
      !markAttendanceDialog &&
      !possibleMatchModal.open &&
      !isTimingDialogPossibleMatchOpen
    ) {
      return;
    }
    let cancelled = false;
    const init = async () => {
      await loadCompanyOptions();
      if (cancelled) return;
      if (addToEmployeeDialog || presentDialogOpen || markAttendanceDialog) {
        await loadEmployees();
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [
    addToEmployeeDialog,
    presentDialogOpen,
    markAttendanceDialog,
    possibleMatchModal.open,
    isTimingDialogPossibleMatchOpen,
    loadCompanyOptions,
    loadEmployees,
  ]);

  // Reload employees whenever company selection changes while dialog is open
  useEffect(() => {
    if (
      !addToEmployeeDialog &&
      !presentDialogOpen &&
      !markAttendanceDialog &&
      !possibleMatchModal.open &&
      !isTimingDialogPossibleMatchOpen
    ) {
      return;
    }
    if (possibleMatchModal.open || isTimingDialogPossibleMatchOpen) {
      const manualCompanyId = possibleMatchModal.open
        ? possibleMatchManualCompanyId
        : timingDialogPossibleMatchManualCompanyId;
      const singleCompanyId = companyOptions.length === 1 ? companyOptions[0]?.company_id || '' : '';
      if (!manualCompanyId && !singleCompanyId) {
        return;
      }
    }
    loadEmployees();
  }, [
    employeeCompanyId,
    markAttendanceCompanyId,
    possibleMatchManualCompanyId,
    timingDialogPossibleMatchManualCompanyId,
    addToEmployeeDialog,
    presentDialogOpen,
    markAttendanceDialog,
    possibleMatchModal.open,
    isTimingDialogPossibleMatchOpen,
    loadEmployees,
  ]);

  const handleOpenAddToEmployee = () => {
    setSelectedAuditId(trainAuditId);
    setSelectedPersonId('');
    setSelectedEmployeeCode('');
    setSelectedCompanyId('');
    setSelectedBranchId('');
    handleTrainClose();
    setSelectedName('');
    setAddToEmployeeDialog(true);
  };

  const getTrainPrefillCaptureImage = useCallback(async (auditIdOverride = null) => {
    const resolvedAuditId = auditIdOverride || trainAuditId;
    if (!resolvedAuditId) return null;
    try {
      const blob = await getAuditImageBlob(resolvedAuditId);
      return await blobToDataUrl(blob);
    } catch (_) {
      return null;
    }
  }, [trainAuditId]);

  const navigateFromTrainToRegistration = useCallback(async (path, auditIdOverride = null) => {
    const resolvedAuditId = auditIdOverride || createProfileTypeAuditId || trainAuditId;
    const prefillCaptureImage = await getTrainPrefillCaptureImage(resolvedAuditId);
    handleTrainClose();
    setCreateProfileTypeDialogOpen(false);
    setCreateProfileTypeAuditId(null);
    navigate(path, {
      state: {
        prefillCaptureImage: prefillCaptureImage || null,
        source: 'audit_train',
        sourceAuditId: resolvedAuditId || null,
      },
    });
  }, [createProfileTypeAuditId, getTrainPrefillCaptureImage, handleTrainClose, navigate, trainAuditId]);

  const handleOpenCreateEmployeeProfile = useCallback(() => {
    navigateFromTrainToRegistration('/employees/register');
  }, [navigateFromTrainToRegistration]);

  const handleOpenCreateVendor = useCallback(() => {
    if (!vendorManagementEnabled) return;
    navigateFromTrainToRegistration('/vendors/register-vendor');
  }, [navigateFromTrainToRegistration, vendorManagementEnabled]);

  const handleOpenCreateIntrusion = useCallback(() => {
    navigateFromTrainToRegistration('/intrusion-profile/register-intrusion-profile');
  }, [navigateFromTrainToRegistration]);

  const handleOpenCreateVisitor = useCallback(() => {
    if (!visitorManagementEnabled) return;
    navigateFromTrainToRegistration('/visitors/register-visitor');
  }, [navigateFromTrainToRegistration, visitorManagementEnabled]);

  const handleOpenCreateEmployee = useCallback(() => {
    const selectedAuditId = trainAuditId || null;
    setCreateProfileTypeAuditId(selectedAuditId);
    handleTrainClose();
    setCreateProfileTypeDialogOpen(true);
  }, [handleTrainClose, trainAuditId]);

  useEffect(() => {
    if (!createProfileTypeDialogOpen) {
      setCreateProfileTypeAuditId(null);
    }
  }, [createProfileTypeDialogOpen]);

  const handleOpenPresent = (log) => {
    const cameraMode = (log?.camera || '').toString().toLowerCase();
    const inferredAction = cameraMode === 'out' ? 'check_out' : 'check_in';
    setPresentAuditId(log.audit_id);
    setPresentPersonId(log.recognized_person_id || '');
    setPresentName(log.recognized_name || '');
    setPresentAction(inferredAction);
    setPresentDialogOpen(true);
  };

  const handleClosePresentDialog = () => {
    setPresentDialogOpen(false);
    setPresentAuditId(null);
    setPresentPersonId('');
    setPresentName('');
    setPresentAction('check_in');
  };

  const handleOpenBucketManualMark = useCallback((topLog, bucketLogs = [], stackImages = []) => {
    if (!topLog?.audit_id) return;
    const defaults = resolveBucketManualMarkPersonDefaults(topLog);
    setBucketManualMarkDialog({
      open: true,
      topLog,
      bucketLogs: Array.isArray(bucketLogs) ? bucketLogs : [],
      stackImages: Array.isArray(stackImages) ? stackImages : [],
    });
    setBucketManualMarkRemark('');
    setBucketManualMarkPersonId(defaults.personId);
    setBucketManualMarkPersonName(defaults.personName);
    setBucketManualMarkEmployeeCode(defaults.employeeCode);
    setBucketManualMarkSelectedAuditId(String(topLog.audit_id));
  }, []);

  const handleCloseBucketManualMark = useCallback(() => {
    setBucketManualMarkDialog({ open: false, topLog: null, bucketLogs: [], stackImages: [] });
    setBucketManualMarkRemark('');
    setBucketManualMarkPersonId('');
    setBucketManualMarkPersonName('');
    setBucketManualMarkEmployeeCode('');
    setBucketManualMarkSelectedAuditId('');
  }, []);

  const handleBucketManualMarkRequestConfirm = () => {
    const topLog = bucketManualMarkDialog.topLog;
    const stackFrames = Array.isArray(bucketManualMarkDialog.stackImages) && bucketManualMarkDialog.stackImages.length > 0
      ? bucketManualMarkDialog.stackImages
      : [{ log: topLog, image: null }];
    const selectedAuditId = bucketManualMarkSelectedAuditId || topLog?.audit_id;
    const selectedFrame = stackFrames.find(
      (frame) => String(frame?.log?.audit_id || '') === String(selectedAuditId)
    );
    const selectedLog = selectedFrame?.log || topLog;
    if (!selectedLog?.audit_id) {
      show('Please select a capture', 'error');
      return;
    }
    if (!bucketManualMarkPersonId) {
      show('Employee is required', 'error');
      return;
    }
    if (!bucketManualMarkRemark.trim()) {
      show('Please provide a remark for manual attendance', 'error');
      return;
    }
    const personName =
      bucketManualMarkPersonName ||
      topLog?.recognized_name ||
      'this employee';
    openConfirmMarkAttendance('bucket_manual_mark', personName);
  };

  const handleBucketManualMarkSubmit = async () => {
    const topLog = bucketManualMarkDialog.topLog;
    const stackFrames = Array.isArray(bucketManualMarkDialog.stackImages) && bucketManualMarkDialog.stackImages.length > 0
      ? bucketManualMarkDialog.stackImages
      : [{ log: topLog, image: null }];
    const selectedAuditId = bucketManualMarkSelectedAuditId || topLog?.audit_id;
    const selectedFrame = stackFrames.find(
      (frame) => String(frame?.log?.audit_id || '') === String(selectedAuditId)
    );
    const selectedLog = selectedFrame?.log || topLog;
    if (!selectedLog?.audit_id) {
      show('Please select a capture', 'error');
      return;
    }
    if (!bucketManualMarkPersonId) {
      show('Employee is required', 'error');
      return;
    }
    if (!bucketManualMarkRemark.trim()) {
      show('Please provide a remark for manual attendance', 'error');
      return;
    }
    const cameraMode = (selectedLog.camera || '').toString().toLowerCase();
    const action = cameraMode === 'out' ? 'check_out' : 'check_in';
    const capturedTs = getCapturedTimestamp(selectedLog);
    const timestamp = capturedTs?.iso || selectedLog.created_at || '';
    let formattedRemark = bucketManualMarkRemark.trim();
    if (formattedRemark && !formattedRemark.endsWith('.')) {
      formattedRemark = `${formattedRemark}.`;
    }
    const selected = employees.find((e) => e.person_id === bucketManualMarkPersonId);
    try {
      setBucketManualMarkSubmitting(true);
      const response = await markAttendance({
        person_id: bucketManualMarkPersonId,
        person_name: bucketManualMarkPersonName || selected?.name || selectedLog.recognized_name || '',
        employee_code: bucketManualMarkEmployeeCode || selected?.employee_code || '',
        action,
        timestamp,
        audit_id: selectedLog.audit_id,
        remark: formattedRemark,
        is_possible_match: selectedLog.confidence_level === 'possible_match',
      });
      if (response?.attendance_id) {
        show('Attendance marked successfully', 'success');
        handleCloseBucketManualMark();
        loadLogs(pagination.limit, pagination.offset);
      } else {
        show(response?.detail || response?.message || 'Failed to mark attendance', 'error');
      }
    } catch (e) {
      show(e.message || 'Failed to mark attendance', 'error');
    } finally {
      setBucketManualMarkSubmitting(false);
    }
  };

  const handleOpenMarkAttendance = () => {
    // Clear all values when opening the dialog
    const now = new Date();
    setMarkAttendanceCompanyId('');
    setMarkAttendancePersonId('');
    setMarkAttendancePersonName('');
    setMarkAttendanceCheckIn('');
    setMarkAttendanceCheckOut('');
    setSelectedAttendanceDate(now);
    setAttendanceCalendarOpen(false);
    setCalendarType('checkin');
    setMarkAttendanceDialog(true);
  };

  const handleCloseMarkAttendance = () => {
    // Clear all values when closing the dialog
    setMarkAttendanceDialog(false);
    setMarkAttendanceCompanyId('');
    setMarkAttendancePersonId('');
    setMarkAttendancePersonName('');
    setMarkAttendanceCheckIn('');
    setMarkAttendanceCheckOut('');
    setMarkAttendanceRemark('');
    setAttendanceCalendarOpen(false);
  };

  const openPossibleMatchModal = async (log, image) => {
    setPossibleMatchModal({ open: true, log, image });
    setPossibleMatchSelectedId(null);
    setPossibleMatchManualCompanyId('');
    setPossibleMatchCandidateImages({});
    const auditId = log?.audit_id;
    if (!auditId) return;
    try {
      const hydrated = await hydratePossibleMatchLogFromDetail(log);
      setPossibleMatchModal((prev) => {
        if (!prev.open || String(prev.log?.audit_id || '') !== String(auditId)) return prev;
        return { ...prev, log: hydrated };
      });
      setLogs((prev) => (
        Array.isArray(prev)
          ? prev.map((entry) => (
            String(entry?.audit_id || '') === String(auditId)
              ? {
                ...entry,
                request_metadata: {
                  ...(entry.request_metadata || {}),
                  ...(hydrated.request_metadata || {}),
                },
              }
              : entry
          ))
          : prev
      ));
    } catch (e) {
      console.error('Failed to load possible match detail:', e);
    }
  };

  const closePossibleMatchModal = () => {
    setPossibleMatchModal({ open: false, log: null, image: null });
    setPossibleMatchSelectedId(null);
    setPossibleMatchManualCompanyId('');
    setPossibleMatchCandidateImages({});
  };

  // Load face images for top candidates when Possible Match modal opens
  const possibleMatchTopCandidateIdsKey = useMemo(() => {
    const candidates = possibleMatchModal.log?.request_metadata?.top_candidates;
    if (!Array.isArray(candidates) || candidates.length === 0) return '';
    return candidates
      .slice(0, 3)
      .map((c) => String(c?.person_id || '').trim())
      .filter(Boolean)
      .join('|');
  }, [possibleMatchModal.log?.request_metadata?.top_candidates]);

  useEffect(() => {
    if (!possibleMatchModal.open || !possibleMatchModal.log?.request_metadata?.top_candidates) return;
    const candidates = possibleMatchModal.log.request_metadata.top_candidates;
    const blobToDataUrl = (blob) => new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
    let cancelled = false;
    (async () => {
      const next = {};
      for (const c of candidates) {
        const pid = c.person_id ? String(c.person_id).trim() : null;
        if (!pid || cancelled) continue;
        try {
          const faces = await getPersonFaces(pid);
          const faceList = Array.isArray(faces) ? faces : [];
          const activeFaces = faceList.filter((f) => !f.deleted_at);
          const sortByPoseDesc = (a, b) => Number(b?.pose_quality || 0) - Number(a?.pose_quality || 0);
          const activeFrontFaces = activeFaces
            .filter((f) => String(f?.face_angle || '').toLowerCase() === 'front')
            .sort(sortByPoseDesc);
          const fallbackFrontFaces = faceList
            .filter((f) => String(f?.face_angle || '').toLowerCase() === 'front')
            .sort(sortByPoseDesc);
          const preferredFace =
            activeFrontFaces[0] ||
            fallbackFrontFaces[0] ||
            activeFaces.sort(sortByPoseDesc)[0] ||
            faceList.sort(sortByPoseDesc)[0];
          if (preferredFace?.face_id && !cancelled) {
            const blob = await getFaceImage(preferredFace.face_id);
            const dataUrl = await blobToDataUrl(blob);
            next[pid] = dataUrl;
          }
        } catch (_) { /* ignore */ }
      }
      if (!cancelled) setPossibleMatchCandidateImages((prev) => ({ ...prev, ...next }));
    })();
    return () => { cancelled = true; };
  }, [possibleMatchModal.open, possibleMatchModal.log?.audit_id, possibleMatchTopCandidateIdsKey]);

  const timingDialogTopCandidateIdsKey = useMemo(() => {
    const candidates = timingDialog.log?.request_metadata?.top_candidates;
    if (!Array.isArray(candidates) || candidates.length === 0) return '';
    return candidates
      .slice(0, 3)
      .map((c) => String(c?.person_id || '').trim())
      .filter(Boolean)
      .join('|');
  }, [timingDialog.log?.request_metadata?.top_candidates]);

  // Load face images for top candidates when Processing timeline opens with a possible_match log
  useEffect(() => {
    if (!timingDialog.open || !timingDialog.log || timingDialog.log.confidence_level !== 'possible_match') return;
    const candidates = timingDialog.log.request_metadata?.top_candidates;
    if (!Array.isArray(candidates) || candidates.length === 0) return;
    const blobToDataUrl = (blob) => new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
    let cancelled = false;
    (async () => {
      const next = {};
      for (const c of candidates) {
        const pid = c.person_id ? String(c.person_id).trim() : null;
        if (!pid || cancelled) continue;
        try {
          const faces = await getPersonFaces(pid);
          const faceList = Array.isArray(faces) ? faces : [];
          const activeFaces = faceList.filter((f) => !f.deleted_at);
          const sortByPoseDesc = (a, b) => Number(b?.pose_quality || 0) - Number(a?.pose_quality || 0);
          const activeFrontFaces = activeFaces
            .filter((f) => String(f?.face_angle || '').toLowerCase() === 'front')
            .sort(sortByPoseDesc);
          const fallbackFrontFaces = faceList
            .filter((f) => String(f?.face_angle || '').toLowerCase() === 'front')
            .sort(sortByPoseDesc);
          const preferredFace =
            activeFrontFaces[0] ||
            fallbackFrontFaces[0] ||
            activeFaces.sort(sortByPoseDesc)[0] ||
            faceList.sort(sortByPoseDesc)[0];
          if (preferredFace?.face_id && !cancelled) {
            const blob = await getFaceImage(preferredFace.face_id);
            const dataUrl = await blobToDataUrl(blob);
            next[pid] = dataUrl;
          }
        } catch (_) { /* ignore */ }
      }
      if (!cancelled) setTimingDialogPossibleMatchCandidateImages((prev) => ({ ...prev, ...next }));
    })();
    return () => { cancelled = true; };
  }, [timingDialog.open, timingDialog.log?.audit_id, timingDialog.log?.confidence_level, timingDialogTopCandidateIdsKey]);

  const openConfirmMarkAttendance = (source, personName) => {
    setConfirmMarkAttendanceSource(source);
    setConfirmMarkAttendancePersonName(personName || 'this person');
    setConfirmMarkAttendanceOpen(true);
  };

  const closeConfirmMarkAttendance = () => {
    setConfirmMarkAttendanceOpen(false);
    setConfirmMarkAttendanceSource(null);
    setConfirmMarkAttendancePersonName('');
  };

  const handleConfirmMarkAttendanceSubmit = async () => {
    if (confirmMarkAttendanceSource === 'possible_match_modal') {
      closeConfirmMarkAttendance();
      await handlePossibleMatchMarkAttendance();
    } else if (confirmMarkAttendanceSource === 'timing_dialog') {
      closeConfirmMarkAttendance();
      await handleTimingDialogPossibleMatchMarkAttendance();
    } else if (confirmMarkAttendanceSource === 'bucket_manual_mark') {
      closeConfirmMarkAttendance();
      await handleBucketManualMarkSubmit();
    }
  };

  const handleTimingDialogPossibleMatchMarkAttendance = async () => {
    const log = timingDialog.log;
    if (!log?.audit_id || !timingDialogPossibleMatchSelectedId) {
      show('Please select a person to mark attendance', 'error');
      return;
    }
    const cameraMode = (log.camera || '').toString().toLowerCase();
    const action = cameraMode === 'out' ? 'check_out' : 'check_in';
    const selected = employees.find((e) => e.person_id === timingDialogPossibleMatchSelectedId);
    const personName = selected?.name || log.request_metadata?.top_candidates?.find((c) => String(c.person_id) === String(timingDialogPossibleMatchSelectedId))?.name || '';
    try {
      setTimingDialogPossibleMatchMarkSubmitting(true);
      const response = await markAttendance({
        person_id: timingDialogPossibleMatchSelectedId,
        person_name: personName,
        employee_code: selected?.employee_code || '',
        action,
        timestamp: log?.created_at || '',
        audit_id: log.audit_id,
        is_possible_match: true,
      });
      if (response?.attendance_id) {
        show('Attendance marked successfully (applied to entire track)', 'success');
        closeTimingDialog();
        loadLogs(pagination.limit, pagination.offset);
      } else {
        show(response?.detail || 'Failed to mark attendance', 'error');
      }
    } catch (e) {
      show(e.message || 'Failed to mark attendance', 'error');
    } finally {
      setTimingDialogPossibleMatchMarkSubmitting(false);
    }
  };

  const handlePossibleMatchMarkAttendance = async () => {
    const { log } = possibleMatchModal;
    if (!log?.audit_id || !possibleMatchSelectedId) {
      show('Please select a person to mark attendance', 'error');
      return;
    }
    const cameraMode = (log.camera || '').toString().toLowerCase();
    const action = cameraMode === 'out' ? 'check_out' : 'check_in';
    const selected = employees.find((e) => e.person_id === possibleMatchSelectedId);
    const personName = selected?.name || log.request_metadata?.top_candidates?.find((c) => String(c.person_id) === String(possibleMatchSelectedId))?.name || '';
    try {
      setPossibleMatchMarkSubmitting(true);
      const response = await markAttendance({
        person_id: possibleMatchSelectedId,
        person_name: personName,
        employee_code: selected?.employee_code || '',
        action,
        timestamp: log?.created_at || '',
        audit_id: log.audit_id,
        is_possible_match: true,
      });
      if (response?.attendance_id) {
        show('Attendance marked successfully', 'success');
        closePossibleMatchModal();
        loadLogs(pagination.limit, pagination.offset);
      } else {
        show(response?.detail || 'Failed to mark attendance', 'error');
      }
    } catch (e) {
      show(e.message || 'Failed to mark attendance', 'error');
    } finally {
      setPossibleMatchMarkSubmitting(false);
    }
  };

  const handlePresentSubmit = async () => {
    if (!presentAuditId || !presentPersonId) {
      show('Please select an employee', 'error');
      return;
    }
    try {
      setPresentSubmitting(true);
      const selected = employees.find((e) => e.person_id === presentPersonId);
      const response = await markAuditAttendance({
        audit_id: presentAuditId,
        person_id: presentPersonId,
        person_name: presentName || selected?.name || '',
        employee_code: selected?.employee_code || '',
        action: presentAction,
      });
      if (response?.attendance) {
        show('Attendance marked successfully', 'success');
        handleClosePresentDialog();
        loadLogs(pagination.limit, pagination.offset);
      } else {
        show(response?.detail || 'Failed to mark attendance', 'error');
      }
    } catch (e) {
      show(e.message || 'Failed to mark attendance', 'error');
    } finally {
      setPresentSubmitting(false);
    }
  };

  const handleMarkAttendanceSubmit = async () => {
    if (!markAttendancePersonId || (!markAttendanceCheckIn && !markAttendanceCheckOut)) {
      show('Please select an employee and provide at least check-in or check-out time', 'error');
      return;
    }
    if (!markAttendanceRemark.trim()) {
      show('Please provide a remark for manual attendance', 'error');
      return;
    }
    try {
      setMarkAttendanceSubmitting(true);

      // Format remark: ensure it ends with a full stop
      let formattedRemark = markAttendanceRemark.trim();
      if (formattedRemark && !formattedRemark.endsWith('.')) {
        formattedRemark = formattedRemark + '.';
      }

      const selected = employees.find((e) => e.person_id === markAttendancePersonId);

      // Convert local time to UTC for storage
      const checkInUTC = markAttendanceCheckIn ? new Date(markAttendanceCheckIn).toISOString() : null;
      const checkOutUTC = markAttendanceCheckOut ? new Date(markAttendanceCheckOut).toISOString() : null;

      // Mark check-in if provided
      if (checkInUTC) {
        const checkInResult = await markAttendance({
          person_id: markAttendancePersonId,
          action: 'check_in',
          person_name: markAttendancePersonName || selected?.name || '',
          employee_code: selected?.employee_code || '',
          timestamp: checkInUTC,
          remark: formattedRemark
        });

        if (!checkInResult.attendance_id && !checkInResult.success) {
          show(checkInResult.message || checkInResult.detail || 'Failed to mark check-in', 'error');
          setMarkAttendanceSubmitting(false);
          return;
        }
      }

      // Mark check-out if provided
      if (checkOutUTC) {
        const checkOutResult = await markAttendance({
          person_id: markAttendancePersonId,
          action: 'check_out',
          person_name: markAttendancePersonName || selected?.name || '',
          employee_code: selected?.employee_code || '',
          timestamp: checkOutUTC,
          remark: formattedRemark
        });

        if (!checkOutResult.attendance_id && !checkOutResult.success) {
          const errorPrefix = checkInUTC ? 'Check-in marked but check-out failed: ' : 'Failed to mark check-out: ';
          show(errorPrefix + (checkOutResult.message || checkOutResult.detail || 'Unknown error'), checkInUTC ? 'warning' : 'error');
          if (!checkInUTC) {
            setMarkAttendanceSubmitting(false);
            return;
          }
        } else {
          show(`${checkInUTC ? 'Attendance' : 'Check-out'} marked successfully for ${markAttendancePersonName || selected?.name}`, 'success');
        }
      } else if (checkInUTC) {
        show(`Check-in marked successfully for ${markAttendancePersonName || selected?.name}`, 'success');
      }

      // Close dialog and reload attendance data
      handleCloseMarkAttendance();
      loadAttendanceSummary(aPagination.limit, aPagination.offset);
    } catch (e) {
      console.error('Mark attendance error:', e);
      show(e.message || 'Failed to mark attendance', 'error');
    } finally {
      setMarkAttendanceSubmitting(false);
    }
  };

  const handleAddToEmployee = async () => {
    if (!selectedPersonId || !selectedName) {
      show('Please select an employee', 'error');
      return;
    }
    try {
      setOperationLoading(true);
      await addAuditImageToEmployee(selectedAuditId, selectedPersonId, selectedName, selectedEmployeeCode, selectedCompanyId, selectedBranchId);
      show('Successfully added image to employee', 'success');
      setAddToEmployeeDialog(false);
      loadLogs(pagination.limit, pagination.offset);
    } catch (e) {
      show(e.message || 'Failed to add image to employee', 'error');
    } finally {
      setOperationLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewUrl(null);
  };

  const groupLogsByDate = (logs) => {
    const sourceLogs = Array.isArray(logs) ? logs : [];
    const groups = {};
    sourceLogs.forEach(log => {
      const ts = log.captured_at || log.created_at;
      const date = new Date(ts);
      const dateKey = date.toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric'
      });

      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(log);
    });
    return groups;
  };
  const auditPersonSearchQueryParams = useMemo(() => ({
    offset: 0,
    include_deleted: 'false',
   
  }), []);
  const formatEnrolledPersonNameOption = useCallback((option) => {
    return formatPersonNameOptionLabel(option, { includeCode: true });
  }, []);
  const filterEnrolledPersonNameOptions = useCallback((options, state) => {
    return filterPersonNameOptions(options, state);
  }, []);

  return (
    <Container maxWidth="2200" disableGutters sx={{ px: 0 }}>
      {!embedded ? (
        <PageHeader
          title={isLogsView ? "In/Out" : "Present"}
          subtitle={isLogsView ? "View and filter recognition logs" : "Track employee attendance"}
          icon={null}
        />
      ) : null}

      {
        // Logs view content
        <Box>
          {/* Filters Section */}
          <Box sx={pageFiltersToolbarSx}>
            <Stack {...pageFiltersStackSx}>
              <Box sx={pageFiltersControlsSx}>
                <PersonSearch
                  queryParams={auditPersonSearchQueryParams}
                  onSelect={(person) => {
                    setMatchedPersonIdFilter(person.person_id)
                  }}
                  onQueryChange={() => {
                    // Typing/clearing the name field drops the selected person_id
                    // filter so results fall back to a plain name search.
                    setMatchedPersonIdFilter('')
                    setMatchedPersonName('')
                  }}
                />


                {activeNotificationAuditId ? (
                  <Chip
                    label={`Audit ID: ${activeNotificationAuditId}`}
                    onDelete={clearNotificationAuditDeepLink}
                    variant="outlined"
                    color="primary"
                    sx={{
                      maxWidth: { xs: '100%', sm: 360 },
                      height: 38,
                      borderColor: '#40518A',
                      color: '#40518A',
                      bgcolor: 'rgba(64, 81, 138, 0.06)',
                      '& .MuiChip-label': {
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                ) : null}

                {greaterThanTotalTimeMs != null ? (
                  <Chip
                    label={`Total Time > ${greaterThanTotalTimeMs} ms`}
                    onDelete={handleClearAdvancedTotalTimeFilter}
                    variant="outlined"
                    color="primary"
                    sx={{
                      maxWidth: { xs: '100%', sm: 280 },
                      height: 38,
                    }}
                  />
                ) : null}

                <FormControl
                  size="small"
                  sx={pageFilterFormControlSx(150)}
                >
                  <InputLabel id="status-label" shrink>
                    Select Status
                  </InputLabel>

                  <Select
                    labelId="status-label"
                    value={status}
                    label="Select Status"
                    onChange={(e) => {
                      clearNotificationAuditDeepLink();
                      setStatus(e.target.value);
                    }}
                    displayEmpty
                    renderValue={(v) => {
                      if (!v) return 'All';
                      const statusMap = {
                        'match': 'Match',
                        'possible_match': 'Possible Match',
                        'unmatch': 'Unmatch',
                        'failed': 'Failed',
                        'validity_expired': 'Validity Expired',
                        'ai_reviewed': 'AI Matched',
                      };
                      return statusMap[v] || v.charAt(0).toUpperCase() + v.slice(1);
                    }}
                  >
                    <MenuItem value="">
                      All
                    </MenuItem>
                    <MenuItem value="match">Match</MenuItem>
                    <MenuItem value="possible_match">Possible Match</MenuItem>
                    <MenuItem value="unmatch">Unmatch</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                    <MenuItem value="validity_expired">Validity Expired</MenuItem>
                    {showVisionReviewUI && (
                      <MenuItem value="ai_reviewed">AI Matched</MenuItem>
                    )}
                  </Select>
                </FormControl>

                {showVisionReviewUI && (
                  <FormControl
                    size="small"
                    sx={pageFilterFormControlSx(190)}
                  >
                    <InputLabel id="vision-review-result-label" shrink>
                      AI Matching Result
                    </InputLabel>

                    <Select
                      labelId="vision-review-result-label"
                      value={visionReviewResult}
                      label="AI Matching Result"
                      onChange={(e) => {
                        clearNotificationAuditDeepLink();
                        setVisionReviewResult(e.target.value);
                      }}
                      displayEmpty
                      renderValue={visionReviewResultLabel}
                    >
                      <MenuItem value="">
                        All
                      </MenuItem>
                      <MenuItem value="total_possible_matches">Total Possible Matches</MenuItem>
                      <MenuItem value="ai_matched">AI Matched</MenuItem>
                      <MenuItem value="ai_not_matched">AI Not Matched</MenuItem>
                      <MenuItem value="pending_review">Pending Review</MenuItem>
                      <MenuItem value="manually_resolved">Manually Resolved</MenuItem>
                    </Select>
                  </FormControl>
                )}

                <FormControl
                  size="small"
                  sx={pageFilterFormControlSx(210)}
                >
                  <InputLabel id="camera-label" shrink>
                    Select Camera Type
                  </InputLabel>

                  <Select
                    labelId="camera-label"
                    value={camera}
                    label="Select Camera Type"
                    onChange={(e) => {
                      clearNotificationAuditDeepLink();
                      const nextType = e.target.value;
                      setCamera(nextType);
                      if (cameraId) {
                        const selectedCam = assignedInOutCameras.find((item) => item.id === cameraId);
                        const nextTypeNorm = String(nextType || '').trim().toUpperCase();
                        if (selectedCam && nextTypeNorm && selectedCam.camera_type !== nextTypeNorm) {
                          setCameraId('');
                        }
                      }
                    }}
                    displayEmpty
                    renderValue={(v) => v ? (v === 'in' ? 'Camera: In' : 'Camera: Out') : 'All'}
                  >
                    <MenuItem value="">
                      All
                    </MenuItem>
                    <MenuItem value="in">Camera: In</MenuItem>
                    <MenuItem value="out">Camera: Out</MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={pageFilterFormControlSx(220)}
                >
                  <InputLabel id="camera-name-label" shrink>
                    Camera Label
                  </InputLabel>
                  <Select
                    labelId="camera-name-label"
                    value={cameraId}
                    label="Camera Label"
                    onOpen={() => { ensureAssignedInOutCamerasLoaded(); }}
                    onChange={(e) => {
                      clearNotificationAuditDeepLink();
                      setCameraId(e.target.value);
                    }}
                    displayEmpty
                    renderValue={(v) => {
                      if (!v) return 'All';
                      const selectedCam = assignedInOutCameras.find((item) => item.id === v);
                      if (!selectedCam) return 'All';
                      return selectedCam.label;
                    }}
                  >
                    <MenuItem value="">
                      All
                    </MenuItem>
                    {(camera
                      ? assignedInOutCameras.filter((item) => item.camera_type === String(camera).toUpperCase())
                      : assignedInOutCameras
                    ).map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.label} ({item.camera_type})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={pageFilterFormControlSx(150)}
                >
                  <InputLabel id="log-type-label" shrink>
                    Log Type
                  </InputLabel>

                  <Select
                    labelId="log-type-label"
                    value={logType}
                    label="Log Type"
                    onChange={(e) => {
                      clearNotificationAuditDeepLink();
                      setLogType(e.target.value);
                    }}
                    displayEmpty
                    renderValue={auditLogTypeLabel}
                  >
                    <MenuItem value="">
                      All
                    </MenuItem>
                    <MenuItem value="attendance">Employee</MenuItem>
                    <MenuItem value="vendor">Vendor</MenuItem>
                    <MenuItem value="visitor">Visitor</MenuItem>
                    <MenuItem value="intrusion">Intrusion</MenuItem>
                  </Select>
                </FormControl>

                <Box
                  onClick={handleOpenLogsDatePicker}
                  sx={pageFilterDatePickerSx(220)}
                >
                  <CalendarMonth fontSize="small" color="action" />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {isSameDay(logsDateRange[0].startDate, logsDateRange[0].endDate)
                      ? `${format(logsDateRange[0].startDate, 'MMM dd, yyyy')}${logsStartTime !== '00:00' || logsEndTime !== '23:59' ? ` • ${logsStartTime} - ${logsEndTime}` : ''}`
                      : `${format(logsDateRange[0].startDate, 'MMM dd')} - ${format(logsDateRange[0].endDate, 'MMM dd, yyyy')}${logsStartTime !== '00:00' || logsEndTime !== '23:59' ? ` • ${logsStartTime} - ${logsEndTime}` : ''}`}
                  </Typography>
                </Box>
                <Popover
                  open={logsDatePickerOpen}
                  anchorEl={logsDatePickerAnchor}
                  onClose={() => setLogsDatePickerAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{
                    paper: {
                      sx: { maxWidth: '100vw', overflow: 'auto' }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                    {/* Quick Select Presets */}
                    <Box sx={{
                      p: 2,
                      borderRight: { md: '1px solid' },
                      borderBottom: { xs: '1px solid', md: 'none' },
                      borderColor: 'divider',
                      minWidth: 140,
                    }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Quick Select
                      </Typography>
                      <Stack spacing={0.5}>
                        {[
                          { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
                          { label: 'Yesterday', getValue: () => ({ startDate: addDays(new Date(), -1), endDate: addDays(new Date(), -1) }) },
                          { label: 'Last 7 Days', getValue: () => ({ startDate: addDays(new Date(), -6), endDate: new Date() }) },
                          { label: 'Last 30 Days', getValue: () => ({ startDate: addDays(new Date(), -29), endDate: new Date() }) },
                          {
                            label: 'This Month', getValue: () => {
                              const now = new Date();
                              return { startDate: new Date(now.getFullYear(), now.getMonth(), 1), endDate: now };
                            }
                          },
                          {
                            label: 'Last Month', getValue: () => {
                              const now = new Date();
                              return { startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1), endDate: new Date(now.getFullYear(), now.getMonth(), 0) };
                            }
                          },
                        ].map((preset) => (
                          <Button
                            key={preset.label}
                            size="small"
                            variant="text"
                            onClick={() => {
                              const { startDate, endDate } = preset.getValue();
                              setLogsDateDraftRange([{ startDate, endDate, key: 'selection' }]);
                            }}
                            sx={{
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              px: 1.5,
                              py: 0.75,
                              color: 'text.primary',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                          >
                            {preset.label}
                          </Button>
                        ))}
                      </Stack>
                    </Box>

                    {/* Date Range Picker - Responsive */}
                    <Box sx={{
                      '& .rdrCalendarWrapper': {
                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                      },
                      '& .rdrMonth': {
                        width: { xs: '280px', sm: '300px' },
                      },
                    }}>
                      <DateRangePicker
                        ranges={logsDateDraftRange}
                        onChange={(ranges) => setLogsDateDraftRange([ranges.selection])}
                        moveRangeOnFirstSelection={false}
                        months={isMobile ? 1 : 2}
                        direction={isMobile ? 'vertical' : 'horizontal'}
                        rangeColors={[primary]}
                        staticRanges={[]}
                        inputRanges={[]}
                        maxDate={new Date()}
                      />
                      <Box sx={{ px: 2, pb: 2, pt: 1, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <TextField
                          label="Start Time"
                          type="time"
                          size="small"
                          value={logsStartTimeDraft}
                          onChange={(e) => setLogsStartTimeDraft(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 60 }}
                          fullWidth
                        />
                        <TextField
                          label="End Time"
                          type="time"
                          size="small"
                          value={logsEndTimeDraft}
                          onChange={(e) => setLogsEndTimeDraft(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ step: 60 }}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0 }}>
                        <Button
                          onClick={() => {
                            const today = new Date();
                            setLogsDateDraftRange([{ startDate: today, endDate: today, key: 'selection' }]);
                            setLogsStartTimeDraft('00:00');
                            setLogsEndTimeDraft('23:59');
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Reset
                        </Button>
                        <Button onClick={() => setLogsDatePickerAnchor(null)} variant="outlined" size="small">
                          Cancel
                        </Button>
                        <Button
                          onClick={handleApplyLogsDateFilters}
                          variant="contained"
                          size="small"
                        >
                          Apply
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Popover>
                <ClearAllFiltersButton
                  onClear={handleClearAllLogsFilters}
                  disabled={!hasActiveLogsFilters}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                />
              </Box>

              <Box sx={pageFiltersActionsSx}>
                <Tooltip title="Advanced filters for audit logs">
                  <Button
                    size="small"
                    variant={greaterThanTotalTimeMs != null ? 'contained' : 'outlined'}
                    startIcon={<FilterList />}
                    endIcon={<ArrowDropDown />}
                    onClick={handleOpenAdvancedFilters}
                    disabled={logsLoading || findPersonResultMode || unknownResultMode}
                    sx={{
                      ...pageFilterActionButtonSx,
                      width: { xs: '100%', lg: 'auto' },
                    }}
                  >
                    Advanced Filters
                  </Button>
                </Tooltip>
                <Popover
                  open={advancedFiltersOpen}
                  anchorEl={advancedFiltersAnchorEl}
                  onClose={handleCloseAdvancedFilters}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 0.75,
                        width: { xs: 'min(92vw, 360px)', sm: 360 },
                        borderRadius: 2,
                        boxShadow: '0 12px 30px rgba(15,23,42,0.12)',
                      },
                    },
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#40518A' }}>
                      Advanced Filters
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 1.5 }}>
                      Refine IN/OUT logs with performance and metadata filters.
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ display: 'grid', gap: 1.5 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Total Time
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Show logs whose end-to-end Total Time is greater than the value below.
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Minimum Total Time (ms)"
                          value={advancedTotalTimeInputMs}
                          onChange={(e) => setAdvancedTotalTimeInputMs(e.target.value.replace(/[^\d]/g, ''))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleApplyAdvancedTotalTimeFilter();
                            }
                          }}
                          inputProps={{ min: 1, step: 1 }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', mt: 2.5 }}>
                      {greaterThanTotalTimeMs != null ? (
                        <Button onClick={handleClearAdvancedTotalTimeFilter} color="inherit" size="small">
                          Clear
                        </Button>
                      ) : null}
                      <Box sx={{ flex: 1 }} />
                      <Button onClick={handleCloseAdvancedFilters} color="inherit" size="small">
                        Cancel
                      </Button>
                      <Button onClick={handleApplyAdvancedTotalTimeFilter} variant="contained" size="small">
                        Apply
                      </Button>
                    </Box>
                  </Box>
                </Popover>
                <Tooltip title="Upload image to find person in logs">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadFile />}
                    onClick={handleUploadMatchClick}
                    disabled={uploadMatchLoading || refreshingFindPerson || logsLoading || unknownMatchRunning}
                    sx={{
                      ...pageFilterActionButtonSx,
                      width: { xs: '100%', lg: 150 },
                      ...(uploadMatchLoading || unknownMatchRunning ? {
                        borderColor: primary,
                        color: primary,
                        animation: `${searchingBorderGlow} 1.4s ease-in-out infinite`,
                        '& .MuiButton-startIcon': { color: primary },
                        '&.Mui-disabled': {
                          borderColor: primary,
                          color: primary,
                        }
                      } : {})
                    }}
                  >
                    {uploadMatchLoading ? 'Searching...' : 'Find Person'}
                  </Button>
                </Tooltip>
                <Tooltip title="Export">
                  <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<FileDownload />}
                      endIcon={<ArrowDropDown />}
                      onClick={openAuditExportMenu}
                      disabled={exportLoading || logsLoading}
                      sx={{
                        ...pageFilterActionButtonSx,
                        width: { xs: '100%', lg: 'auto' },
                      }}
                    >
                      {exportLoading ? 'Exporting...' : 'Export'}
                    </Button>
                    <Menu
                      anchorEl={auditExportMenuAnchor}
                      open={auditExportMenuOpen}
                      onClose={closeAuditExportMenu}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <MenuItem onClick={() => handleChooseAuditExportFormat('csv')}>Export in CSV</MenuItem>
                      <MenuItem onClick={() => handleChooseAuditExportFormat('xlsx')}>Export in XLSX</MenuItem>
                    </Menu>
                  </Box>
                </Tooltip>
              </Box>
            </Stack>
          </Box>

          {status === 'possible_match' && !findPersonResultMode && !unknownResultMode && showVisionReviewUI && (
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 2,
                flexWrap: 'wrap',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 2,
                mb: 2,
                px: 1.5,
                py: 1,
                width: '100%',
                backgroundColor: 'background.paper',
                boxShadow: (theme) => theme.shadows[1],
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={possibleMatchSelectAllChecked}
                    onChange={(e) => handlePossibleMatchSelectAllChange(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Select All</Typography>}
                sx={{ m: 0 }}
              />
              {visionReviewRunning && (
                <Chip
                  size="small"
                  icon={<CircularProgress size={14} />}
                  label={`${visionReviewIsBatch ? 'AI Batch' : 'AI Matching'} ${visionReviewProgress.processed} / ${visionReviewProgress.total || '…'}`}
                  color="primary"
                  variant="outlined"
                />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { xs: 0, md: 'auto' }, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={visionReviewRunning ? <CircularProgress size={16} color="inherit" /> : <SmartToy />}
                  onClick={() => handleStartBulkVisionReview({ selectAllFiltered: false })}
                  disabled={visionReviewRunning || selectedPossibleMatchLogs.size === 0}
                  sx={{ textTransform: 'none' }}
                >
                  {visionReviewRunning
                    ? 'Matching…'
                    : selectedPossibleMatchLogs.size >= 2
                      ? `AI Batch Matching (${selectedPossibleMatchLogs.size})`
                      : `AI Matching (${selectedPossibleMatchLogs.size})`}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SmartToy />}
                  onClick={() => handleStartBulkVisionReview({ selectAllFiltered: true })}
                  disabled={visionReviewRunning}
                  sx={{
                    textTransform: 'none',
                    color: (theme) => theme.palette.warning.main,
                    borderColor: (theme) => theme.palette.warning.main,
                    '&:hover': {
                      borderColor: (theme) => theme.palette.warning.main,
                      bgcolor: (theme) => theme.palette.warning.light + '22'
                    },
                  }}

                >
                  AI Batch Matching All (filtered)
                </Button>
              </Box>
            </Box>
          )}

          {/* Find Person Results Section */}
          {findPersonResultMode && (
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 2,
                mb: 2,
                flexWrap: 'wrap',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 2,
                px: 1.5,
                py: 1,
                width: '100%',
                backgroundColor: 'background.paper',
                boxShadow: (theme) => theme.shadows[1],
              }}
            >
              <Box sx={{ width: { xs: '100%', sm: 280 }, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Similarity Threshold: {parseFloat(findPersonSimilarityThreshold || 0).toFixed(2)}
                </Typography>
                <Slider
                  value={parseFloat(findPersonSimilarityThreshold || 0.3)}
                  onChange={(e, newValue) => setFindPersonSimilarityThreshold(String(newValue))}
                  min={0}
                  max={1}
                  step={0.01}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 0.25, label: '0.25' },
                    { value: 0.5, label: '0.5' },
                    { value: 0.75, label: '0.75' },
                    { value: 1, label: '1' },
                  ]}
                  sx={{ width: '100%' }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flex: { xs: '1 1 100%', md: '1 1 360px' },
                  minWidth: 0,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  px: 1.25,
                  py: 0,
                  backgroundColor: 'grey.50',
                  height: 44,
                  overflow: 'hidden',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0, flexWrap: 'nowrap' }}>
                  <Chip
                    icon={<Person fontSize="small" />}
                    color="default"
                    variant="outlined"
                    size="small"
                    label={matchedPersonName || matchedPersonIdFilter || 'Unmatched person'}
                    sx={{
                      maxWidth: { xs: '100%', sm: 320 },
                      '& .MuiChip-label': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
                    }}
                  />
                  <Chip
                    color="success"
                    variant="filled"
                    size="small"
                    label={`Matched ${findPersonSummary.known_count}`}
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    color="warning"
                    variant="filled"
                    size="small"
                    label={`Possible Match ${findPersonSummary.possible_match_count}`}
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    color="error"
                    variant="filled"
                    size="small"
                    label={`Unmatched ${findPersonSummary.unknown_count}`}
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAllChecked}
                    onChange={(e) => handleSelectAllChange(e.target.checked)}
                    size="small"
                  />
                }
                label={<Tooltip title="Select all unmatched logs to match"><Typography variant="body2" sx={{ fontWeight: 500 }}>Select All</Typography></Tooltip>}
                sx={{
                  m: 0,
                  px: 1,
                  py: 0.5,
                  height: 44,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  boxSizing: 'border-box',
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  ml: { xs: 0, md: 'auto' },
                  height: 44,
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={handleReMatch}
                  disabled={rematchLoading || selectedLogs.size === 0 || !matchedPersonIdFilter}
                  sx={{ textTransform: 'none', minWidth: 132, px: 1.5, height: 44 }}
                >
                  {rematchLoading ? 'Matching...' : `Match (${selectedLogs.size})`}
                </Button>
                <Tooltip title="Clear find person result">
                  <IconButton
                    size="small"
                    onClick={clearUploadedMatchFilter}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      color: 'text.secondary',
                      backgroundColor: 'grey.50',
                      '&:hover': {
                        color: 'error.main',
                        borderColor: 'error.light',
                        backgroundColor: 'rgba(211, 47, 47, 0.08)',
                      },
                      width: 44,
                      height: 44,
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {(logsLoading || (uploadMatchLoading && findPersonResultMode) || (refreshingFindPerson && findPersonResultMode)) ? (
            /* Logs Loading Skeleton */
            <Box>
              {/* Date header skeleton */}
              <Box sx={{ width: 180, height: 28, borderRadius: 1, mb: 2, ...shimmerStyles }} />
              {/* Tiles skeleton - responsive grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, 1fr)',      // 2 columns on mobile
                    sm: 'repeat(3, 1fr)',      // 3 columns on small tablets
                    md: 'repeat(4, 1fr)',      // 4 columns on tablets
                    lg: 'repeat(5, 1fr)',      // 5 columns on desktop
                    xl: 'repeat(6, 1fr)',      // 6 columns on large screens
                  },
                  gap: 2,
                }}
              >
                {Array.from({ length: 12 }).map((_, idx) => (
                  <LogsTileSkeleton key={`logs-skel-${idx}`} />
                ))}
              </Box>
            </Box>
          ) : (!logs || logs.length === 0) ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, background: '#fafafa' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>No audit activity found</Typography>
              <Typography variant="body2" color="text.secondary">
                Adjust filters or widen the day range to see results.
              </Typography>
            </Paper>
          ) : (
            Object.entries(groupLogsByDate(logs)).map(([date, items]) => {
              // Backend already returns bucketized top logs; build bucket structures for UI rendering
              const trackBuckets = items.map((log) => ({
                bucketKey: log.bucket_key || getTrackBucketKey(log),
                representative: log,
                logs: (Array.isArray(log.bucket_logs) && log.bucket_logs.length > 0) ? log.bucket_logs : [log],
                stack_size: log.stack_size || (Array.isArray(log.bucket_logs) ? log.bucket_logs.length : 1),
              }));
              return (
                <Box key={date} sx={{ mb: 4 }}>
                  {/* Date header */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 1, color: '#333' }}
                  >
                    {date}
                  </Typography>

                  {/* Cards - Responsive photo gallery grid */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(2, 1fr)',      // 2 columns on mobile
                        sm: 'repeat(3, 1fr)',      // 3 columns on small tablets
                        md: 'repeat(4, 1fr)',      // 4 columns on tablets
                        lg: 'repeat(5, 1fr)',      // 5 columns on desktop
                        xl: 'repeat(6, 1fr)',      // 6 columns on large screens
                      },
                      gap: 2,
                    }}
                  >
                    {trackBuckets.map((bucket) => {
                      const hydratedRepresentative = hydrateRepresentativeLog(bucket.representative || bucket.logs[0], bucket.logs);
                      const attendanceMarkedLog = pickAttendanceMarkedLogForDisplay(hydratedRepresentative, bucket.logs);
                      // Preserve any bucket/top-level fields (e.g. bucket_key/bucket_logs) while switching
                      // to the attendance-marked frame for display.
                      const topLog =
                        attendanceMarkedLog && attendanceMarkedLog !== hydratedRepresentative
                          ? { ...hydratedRepresentative, ...attendanceMarkedLog }
                          : hydratedRepresentative;
                      const logWithStack = {
                        ...topLog,
                        stack_size: bucket.stack_size || bucket.logs.length,
                      };
                      const capturedTs = getCapturedTimestamp(topLog);
                      const requestSentTs = getRequestSentTimestamp(topLog);
                      const apiSubmitTs = getApiSubmitTimestamp(topLog);
                      const apiReceivedTs = getApiReceivedTimestamp(topLog);
                      const key = `${date}-${bucket.bucketKey}`;
                      const stackImages = buildPrioritizedStackImages(bucket.logs, topLog, {});

                      const showPresent =
                        !topLog?.manual_attendance &&
                        (topLog?.confidence_level === 'no_match' ||
                          (!topLog?.success && !topLog?.recognized_person_id));
                      const showBucketManualMark = shouldShowBucketManualMarkIcon(topLog, bucket.logs);
                      const bucketAttendanceRef = resolveBucketAttendanceRef(topLog, bucket.logs);
                      const isMatchedTile = Boolean(topLog?.success) && topLog?.confidence_level !== 'possible_match';
                      const canRevokeAttendance =
                        isMatchedTile &&
                        bucketAttendanceRef.hasAttendance &&
                        !bucketAttendanceRef.revoked;
                      const revokeLoadingKey =
                        bucketAttendanceRef.sourceAuditId || topLog.audit_id || bucketAttendanceRef.attendanceId;

                      // Check if log is unmatched (only show checkbox for unmatched logs)
                      const isUnmatched = topLog?.confidence_level === 'no_match' ||
                        !topLog?.success ||
                        !topLog?.recognized_person_id;
                      const showPossibleMatchCheckbox =
                        showVisionReviewUI &&
                        status === 'possible_match' &&
                        !findPersonResultMode &&
                        !unknownResultMode &&
                        topLog.confidence_level === 'possible_match' &&
                        topLog.audit_id;
                      const showCheckbox =
                        (findPersonResultMode && topLog.audit_id && isUnmatched) ||
                        showPossibleMatchCheckbox;

                      return (
                        <AuditTile
                          key={key}
                          log={logWithStack}
                          lazyLoad
                          stackImages={stackImages}
                          bucketLogs={bucket.logs}
                          capturedTs={capturedTs}
                          apiReceivedTs={apiReceivedTs}
                          onClick={() =>
                            openTimingDialog({
                              log: {
                                ...logWithStack,
                                capturedTs,
                                requestSentTs,
                                apiSubmitTs,
                                apiReceivedTs,
                              },
                              stack: stackImages,
                            })
                          }
                          onTrainClick={
                            shouldShowTrainButton(topLog)
                              ? (e) => handleTrainClick(e, topLog.audit_id)
                              : null
                          }
                          onMatchClick={() => handleMatchAudit(topLog.audit_id)}
                          onRetryMatchClick={(e) => handleOpenRetryMatchDialog(e, topLog, topLog.audit_id ? peekAuditImage(topLog.audit_id) : null, stackImages)}
                          onPresentClick={
                            showPresent
                              ? (e) => {
                                e.stopPropagation();
                                handleOpenPresent(topLog);
                              }
                              : null
                          }
                          onBucketManualMarkClick={
                            showBucketManualMark
                              ? (e) => {
                                e.stopPropagation();
                                handleOpenBucketManualMark(topLog, bucket.logs, stackImages);
                              }
                              : null
                          }
                          bucketManualMarkLoading={
                            showBucketManualMark &&
                            bucketManualMarkSubmitting &&
                            bucketManualMarkDialog.topLog?.audit_id === topLog.audit_id
                          }
                          onRevokeClick={
                            canRevokeAttendance
                              ? (e) => {
                                e.stopPropagation();
                                openRevokeConfirmDialog(topLog, bucket.logs);
                              }
                              : null
                          }
                          revokeLoading={Boolean(revokeLoadingKey && revokingAuditIds.has(revokeLoadingKey))}
                          presentLoading={
                            showPresent && presentSubmitting && presentAuditId === topLog.audit_id
                          }
                          onCheckboxChange={
                            showCheckbox
                              ? (showPossibleMatchCheckbox
                                ? handlePossibleMatchCheckboxChange
                                : handleLogCheckboxChange)
                              : null
                          }
                          isChecked={
                            showCheckbox
                              ? (showPossibleMatchCheckbox
                                ? selectedPossibleMatchLogs.has(topLog.audit_id)
                                : selectedLogs.has(topLog.audit_id))
                              : false
                          }
                          onSearchClick={(log, image) => handleOpenFindPersonSearchIconAuditLogs(log, image, stackImages)}
                          findPersonResultMode={findPersonResultMode}
                          showVisionReviewUI={showVisionReviewUI}
                          onPossibleMatchClick={
                            topLog.confidence_level === 'possible_match'
                              ? () => openPossibleMatchModal(topLog, topLog.audit_id ? peekAuditImage(topLog.audit_id) : null)
                              : null
                          }
                        />
                      );
                    })}
                  </Box>
                </Box>
              );
            })
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Showing {pagination.total === 0 ? 0 : pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} results
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">Tiles per page:</Typography>
                <Select
                  size="small"
                  value={pagination.limit}
                  onChange={(e) => loadLogs(e.target.value, 0)}
                  sx={{ minWidth: 90 }}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={75}>75</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </Stack>
            </Stack>
            <Pagination
              count={Math.max(1, Math.ceil((pagination.total || 0) / pagination.limit))}
              page={pagination.current_page}
              onChange={handlePageChange}
              showFirstButton
              showLastButton
            />
          </Box>
        </Box>
      }

      <AuditFindPersonDialogs
        imageSourceDialogOpen={imageSourceDialogOpen}
        handleCloseImageSourceDialog={handleCloseImageSourceDialog}
        findPersonSimilarityThreshold={findPersonSimilarityThreshold}
        setFindPersonSimilarityThreshold={setFindPersonSimilarityThreshold}
        isCameraOn={isCameraOn}
        previewImageDataUrl={previewImageDataUrl}
        uploadMatchInputRef={uploadMatchInputRef}
        handleUploadMatchChange={handleUploadMatchChange}
        primary={primary}
        startCamera={startCamera}
        stopCamera={stopCamera}
        capturePhotoFromCamera={capturePhotoFromCamera}
        cameraVideoRef={cameraVideoRef}
        handleFindPersonFromPreview={handleFindPersonFromPreview}
        uploadMatchLoading={uploadMatchLoading}
        unknownConfirmOpen={unknownConfirmOpen}
        setUnknownConfirmOpen={setUnknownConfirmOpen}
        handleStartUnknownSearch={handleStartUnknownSearch}
        unknownMatchRunning={unknownMatchRunning}
      />

      <AuditDialogs
        findPersonSearchIconAuditLogsModalOpen={findPersonSearchIconAuditLogsModalOpen}
        handleCloseFindPersonSearchIconAuditLogs={handleCloseFindPersonSearchIconAuditLogs}
        handleSearchFindPersonSearchIconAuditLogs={handleSearchFindPersonSearchIconAuditLogs}
        findPersonSearchIconAuditLogsLoading={findPersonSearchIconAuditLogsLoading}
        findPersonSearchIconAuditLogsSelectedImage={findPersonSearchIconAuditLogsSelectedImage}
        primary={primary}
        findPersonSearchIconAuditLogsStackImages={findPersonSearchIconAuditLogsStackImages}
        findPersonSearchIconAuditLogsSelectedAuditId={findPersonSearchIconAuditLogsSelectedAuditId}
        getCapturedTimestamp={getCapturedTimestamp}
        setFindPersonSearchIconAuditLogsSelectedImage={setFindPersonSearchIconAuditLogsSelectedImage}
        setFindPersonSearchIconAuditLogsSelectedAuditId={setFindPersonSearchIconAuditLogsSelectedAuditId}
        findPersonSearchIconAuditLogsThreshold={findPersonSearchIconAuditLogsThreshold}
        setFindPersonSearchIconAuditLogsThreshold={setFindPersonSearchIconAuditLogsThreshold}
        attendanceExportDialogOpen={attendanceExportDialogOpen}
        setAttendanceExportDialogOpen={setAttendanceExportDialogOpen}
        handleExportAttendance={handleExportAttendance}
        exportAttendanceLoading={exportAttendanceLoading}
        attendanceExportSelectedFields={attendanceExportSelectedFields}
        attendanceExportFileName={attendanceExportFileName}
        setAttendanceExportFileName={setAttendanceExportFileName}
        setAttendanceExportDatePickerAnchor={setAttendanceExportDatePickerAnchor}
        attendanceExportDateRange={attendanceExportDateRange}
        attendanceExportStartTime={attendanceExportStartTime}
        attendanceExportEndTime={attendanceExportEndTime}
        attendanceExportDatePickerOpen={attendanceExportDatePickerOpen}
        attendanceExportDatePickerAnchor={attendanceExportDatePickerAnchor}
        setAttendanceExportDateRange={setAttendanceExportDateRange}
        isMobile={isMobile}
        setAttendanceExportStartTime={setAttendanceExportStartTime}
        setAttendanceExportEndTime={setAttendanceExportEndTime}
        buildExportDateTimeRange={buildExportDateTimeRange}
        show={show}
        availableNames={availableNames}
        attendanceExportPersonName={attendanceExportPersonName}
        attendanceExportAllEmployeesPresence={attendanceExportAllEmployeesPresence}
        setAttendanceExportPersonName={setAttendanceExportPersonName}
        setAttendanceExportAllEmployeesPresence={setAttendanceExportAllEmployeesPresence}
        setAttendanceExportSelectedFields={setAttendanceExportSelectedFields}
        attendanceExportAction={attendanceExportAction}
        setAttendanceExportAction={setAttendanceExportAction}
        attendanceExportPremisesFilter={attendanceExportPremisesFilter}
        setAttendanceExportPremisesFilter={setAttendanceExportPremisesFilter}
        attendanceExportLateFilter={attendanceExportLateFilter}
        setAttendanceExportLateFilter={setAttendanceExportLateFilter}
        attendanceExportVisibleFields={attendanceExportVisibleFields}
        moveSelectedField={moveSelectedField}
        toggleAttendanceExportField={toggleAttendanceExportField}
        snackbar={snackbar}
        setSnackbar={setSnackbar}
        timingDialog={timingDialog}
        closeTimingDialog={closeTimingDialog}
        timingDialogResultMeta={timingDialogResultMeta}
        hasTimingDialogTrackAttendance={hasTimingDialogTrackAttendance}
        possibleMatchTrainActiveRef={possibleMatchTrainActiveRef}
        setTrainAuditId={setTrainAuditId}
        setTrainMenuAnchor={setTrainMenuAnchor}
        timingDialogPossibleMatchSelectedId={timingDialogPossibleMatchSelectedId}
        timingDialogPossibleMatchManualCompanyId={timingDialogPossibleMatchManualCompanyId}
        setTimingDialogPossibleMatchManualCompanyId={setTimingDialogPossibleMatchManualCompanyId}
        timingDialogPossibleMatchMarkSubmitting={timingDialogPossibleMatchMarkSubmitting}
        employees={employees}
        sortedEmployees={sortedEmployees}
        openConfirmMarkAttendance={openConfirmMarkAttendance}
        modalFrames={modalFrames}
        imageCache={{}}
        ANONYMOUS_IMAGE={ANONYMOUS_IMAGE}
        setTimingDialogPossibleMatchSelectedId={setTimingDialogPossibleMatchSelectedId}
        timingDialogPossibleMatchCandidateImages={timingDialogPossibleMatchCandidateImages}
        onTimingDialogRetryMatchClick={(e) => {
          if (!timingDialog.log?.audit_id) return;
          handleOpenRetryMatchDialog(
            e,
            timingDialog.log,
            timingDialog.log.audit_id ? peekAuditImage(timingDialog.log.audit_id) : null,
            modalFrames
          );
        }}
        onTimingDialogRefreshTopMatchersClick={() => {
          if (!timingDialog.log?.audit_id) return;
          openRefreshTopMatchersConfirmDialog(timingDialog.log, { source: 'timeline' });
        }}
        refreshTopMatchersLoading={refreshTopMatchersLoading}
        onTimingDialogRevokeClick={(e) => {
          e.stopPropagation();
          if (!timingDialog.log) return;
          openRevokeConfirmDialog(timingDialog.log, timingDialogBucketLogs);
        }}
        canTimingDialogRevokeAttendance={canTimingDialogRevokeAttendance}
        timingDialogRevokeLoading={timingDialogRevokeLoading}
        addToEmployeeDialog={addToEmployeeDialog}
        setAddToEmployeeDialog={setAddToEmployeeDialog}
        handleAddToEmployee={handleAddToEmployee}
        operationLoading={operationLoading}
        selectedPersonId={selectedPersonId}
        companyOptions={companyOptions}
        employeeCompanyId={employeeCompanyId}
        setEmployeeCompanyId={setEmployeeCompanyId}
        setSelectedPersonId={setSelectedPersonId}
        setSelectedName={setSelectedName}
        setSelectedEmployeeCode={setSelectedEmployeeCode}
        setSelectedCompanyId={setSelectedCompanyId}
        setSelectedBranchId={setSelectedBranchId}
        OTHER_COMPANY_OPTION_VALUE={OTHER_COMPANY_OPTION_VALUE}
        employeesLoading={employeesLoading}
        presentDialogOpen={presentDialogOpen}
        handleClosePresentDialog={handleClosePresentDialog}
        handlePresentSubmit={handlePresentSubmit}
        presentSubmitting={presentSubmitting}
        presentPersonId={presentPersonId}
        setPresentPersonId={setPresentPersonId}
        setPresentName={setPresentName}
        markAttendanceDialog={markAttendanceDialog}
        handleCloseMarkAttendance={handleCloseMarkAttendance}
        handleMarkAttendanceSubmit={handleMarkAttendanceSubmit}
        markAttendanceSubmitting={markAttendanceSubmitting}
        markAttendancePersonId={markAttendancePersonId}
        markAttendanceCheckIn={markAttendanceCheckIn}
        markAttendanceCheckOut={markAttendanceCheckOut}
        markAttendanceRemark={markAttendanceRemark}
        markAttendanceCompanyId={markAttendanceCompanyId}
        setMarkAttendanceCompanyId={setMarkAttendanceCompanyId}
        setMarkAttendancePersonId={setMarkAttendancePersonId}
        setMarkAttendancePersonName={setMarkAttendancePersonName}
        loadEmployees={loadEmployees}
        calendarType={calendarType}
        setCalendarType={setCalendarType}
        setSelectedAttendanceDate={setSelectedAttendanceDate}
        setAttendanceCalendarOpen={setAttendanceCalendarOpen}
        setMarkAttendanceRemark={setMarkAttendanceRemark}
        attendanceCalendarOpen={attendanceCalendarOpen}
        selectedAttendanceDate={selectedAttendanceDate}
        setMarkAttendanceCheckIn={setMarkAttendanceCheckIn}
        setMarkAttendanceCheckOut={setMarkAttendanceCheckOut}
        possibleMatchModal={possibleMatchModal}
        closePossibleMatchModal={closePossibleMatchModal}
        setPossibleMatchSelectedId={setPossibleMatchSelectedId}
        possibleMatchSelectedId={possibleMatchSelectedId}
        possibleMatchManualCompanyId={possibleMatchManualCompanyId}
        setPossibleMatchManualCompanyId={setPossibleMatchManualCompanyId}
        possibleMatchMarkSubmitting={possibleMatchMarkSubmitting}
        possibleMatchCandidateImages={possibleMatchCandidateImages}
        confirmMarkAttendanceOpen={confirmMarkAttendanceOpen}
        closeConfirmMarkAttendance={closeConfirmMarkAttendance}
        handleConfirmMarkAttendanceSubmit={handleConfirmMarkAttendanceSubmit}
        confirmMarkAttendanceSource={confirmMarkAttendanceSource}
        confirmMarkAttendancePersonName={confirmMarkAttendancePersonName}
        trainMenuAnchor={trainMenuAnchor}
        handleTrainClose={handleTrainClose}
        handleOpenAddToEmployee={handleOpenAddToEmployee}
        handleOpenCreateEmployee={handleOpenCreateEmployee}
        handleOpenCreateEmployeeProfile={handleOpenCreateEmployeeProfile}
        handleOpenCreateVendor={handleOpenCreateVendor}
        handleOpenCreateIntrusion={handleOpenCreateIntrusion}
        handleOpenCreateVisitor={handleOpenCreateVisitor}
        vendorManagementEnabled={vendorManagementEnabled}
        visitorManagementEnabled={visitorManagementEnabled}
        createProfileTypeDialogOpen={createProfileTypeDialogOpen}
        setCreateProfileTypeDialogOpen={setCreateProfileTypeDialogOpen}
        previewOpen={previewOpen}
        closePreview={closePreview}
        previewUrl={previewUrl}
        bucketManualMarkDialog={bucketManualMarkDialog}
        handleCloseBucketManualMark={handleCloseBucketManualMark}
        handleBucketManualMarkRequestConfirm={handleBucketManualMarkRequestConfirm}
        handleBucketManualMarkSubmit={handleBucketManualMarkSubmit}
        bucketManualMarkSubmitting={bucketManualMarkSubmitting}
        bucketManualMarkRemark={bucketManualMarkRemark}
        setBucketManualMarkRemark={setBucketManualMarkRemark}
        bucketManualMarkPersonId={bucketManualMarkPersonId}
        bucketManualMarkPersonName={bucketManualMarkPersonName}
        bucketManualMarkEmployeeCode={bucketManualMarkEmployeeCode}
        bucketManualMarkSelectedAuditId={bucketManualMarkSelectedAuditId}
        setBucketManualMarkSelectedAuditId={setBucketManualMarkSelectedAuditId}
        onVisionReview={showVisionReviewUI ? handleVisionReviewForLog : null}
        visionReviewLoading={visionReviewLoading}
        showVisionReviewUI={showVisionReviewUI}
      />

      <Dialog
        open={revokeConfirmDialog.open}
        onClose={closeRevokeConfirmDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            overflow: 'hidden',
            maxWidth: 600,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.5, pt: 2.5, px: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: 'rgba(239, 68, 68, 0.12)',
                color: '#dc2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BlockIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Revoke Attendance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Attendance will be revoked; you can mark again or re-run AI matching when needed.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, px: 3 }}>
          <Box
            sx={{
              borderRadius: 1.5,
              p: 2,
              bgcolor: '#fff7ed',
              border: '1px solid #fed7aa',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: '#9a3412', mb: 0.75 }}>
              Are you sure you want to revoke attendance?
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#7c2d12', mb: 1 }}>
              {revokeConfirmDialog.userName || 'Unknown'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#9a3412' }}>
              After revoking, you can mark attendance again or re-run AI matching on this audit when applicable.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
          <Button onClick={closeRevokeConfirmDialog}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmRevokeAttendance} sx={{ fontWeight: 700 }}>
            Revoke Attendance
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={refreshTopMatchersConfirmDialog.open}
        onClose={closeRefreshTopMatchersConfirmDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            overflow: 'hidden',
            maxWidth: 600,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.5, pt: 2.5, px: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: 'rgba(15, 118, 110, 0.12)',
                color: '#0f766e',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <RefreshIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Refresh Top Matchers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Improves ranking when the correct person is lower in the list or missing.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, px: 3 }}>
          <Box
            sx={{
              borderRadius: 1.5,
              p: 2,
              bgcolor: '#f0fdfa',
              border: '1px solid #99f6e4',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: '#115e59', mb: 0.75 }}>
              Refresh candidate ranking?
            </Typography>
            <Typography variant="body2" sx={{ color: '#0f766e' }}>
              This re-ranks current candidates and expands search if needed to put the best visual match first. Capture image and audit details stay unchanged.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
          <Button onClick={closeRefreshTopMatchersConfirmDialog} disabled={refreshTopMatchersLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmRefreshTopMatchers}
            disabled={refreshTopMatchersLoading}
            sx={{
              fontWeight: 700,
              bgcolor: '#0f766e',
              '&:hover': { bgcolor: '#0d9488' },
            }}
          >
            Refresh
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={retryMatchDialogOpen}
        onClose={handleRetryMatchDialogClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: {
              xs: 'calc(100% - 24px)',
              sm: retryMatchStackImages.length > 3 ? 700 : retryMatchStackImages.length > 1 ? 560 : 460,
            },
            maxWidth: 'unset',
            m: { xs: 1.5, sm: 2 },
          }
        }}
      >
        <DialogTitle>Retry Match</DialogTitle>
        <DialogContent
          dividers
          ref={retryMatchContentRef}
          sx={{
            maxHeight: { xs: '78vh', sm: '72vh' },
            overflowY: 'auto',
          }}
        >
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Set similarity threshold for this audit tile rematch.
            </Typography>
            {retryMatchStackImages.length > 1 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Select Image ({retryMatchStackImages.length} available)
                </Typography>
                {retryMatchImageLoading && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Loading images...
                  </Typography>
                )}
                <Box
                  onWheel={(e) => {
                    e.preventDefault();
                    e.currentTarget.scrollTop += e.deltaY * 2.2;
                  }}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 190px))',
                    justifyContent: 'start',
                    gap: { xs: 1.5, sm: 2 },
                    maxHeight: { xs: '48vh', sm: '360px' },
                    overflowY: 'auto',
                    p: 0.5,
                  }}
                >
                  {retryMatchStackImages.map((item, idx) => {
                    const isSelected = String(retryMatchSelectedAuditId || '') === String(item.uniqueId || '');
                    const capturedTs = getCapturedTimestamp(item.log);
                    return (
                      <Card
                        key={item.uniqueId || item.log?.audit_id || idx}
                        sx={{
                          position: 'relative',
                          cursor: 'pointer',
                          border: isSelected ? `3px solid ${primary}` : '1px solid',
                          borderColor: isSelected ? primary : 'divider',
                          '&:hover': {
                            boxShadow: 3,
                            borderColor: primary,
                          },
                          transition: 'all 0.2s',
                        }}
                        onClick={() => {
                          setRetryMatchSelectedAuditId(item.uniqueId);
                          setRetryMatchAudit(item.log);
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            paddingTop: '96%',
                            bgcolor: '#f5f5f5',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            component="img"
                            src={item.image || ANONYMOUS_IMAGE}
                            alt="Retry match candidate"
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          {isSelected && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: primary,
                                color: '#fff',
                                borderRadius: '50%',
                                width: 28,
                                height: 28,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                              }}
                            >
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '50%',
                                  bgcolor: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: primary,
                                  }}
                                />
                              </Box>
                            </Box>
                          )}
                          {item.isMain && (
                            <Chip
                              label="Main"
                              size="small"
                              sx={{
                                position: 'absolute',
                                bottom: 4,
                                left: 4,
                                bgcolor: 'rgba(0, 0, 0, 0.7)',
                                color: '#fff',
                                fontSize: '0.65rem',
                                height: 18,
                              }}
                            />
                          )}
                        </Box>
                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                          <Typography variant="caption" noWrap sx={{ fontSize: '0.72rem' }}>
                            {capturedTs?.iso ? formatDateWithSeconds(capturedTs.iso) : '-'}
                          </Typography>
                          {item.log?.audit_id && (
                            <Typography variant="caption" display="block" noWrap sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                              ID: {item.log.audit_id}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              </Box>
            )}
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: { xs: 'block', sm: 'flex' }, alignItems: 'flex-start', gap: { xs: 0.75, sm: 2 } }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    minWidth: 'fit-content',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    lineHeight: { xs: 'normal', sm: '40px' },
                  }}
                >
                  Similarity Threshold: {parseFloat(retryMatchThreshold || 0).toFixed(2)}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0, maxWidth: { xs: 320, sm: 430 }, mt: { xs: 0.25, sm: 0.5 } }}>
                  <Slider
                    key={`retry-threshold-${retryMatchDialogOpen ? 'open' : 'closed'}-${retryMatchSelectedAuditId || 'none'}-${retryMatchThreshold}`}
                    defaultValue={parseFloat(retryMatchThreshold || 0.6)}
                    onChange={(e, newValue) => {
                      retryMatchThresholdRef.current = Number(newValue);
                    }}
                    onChangeCommitted={(e, newValue) => {
                      const v = Number(newValue);
                      retryMatchThresholdRef.current = v;
                      setRetryMatchThreshold(String(v));
                    }}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => Number(v).toFixed(2)}
                    min={0}
                    max={1}
                    step={0.01}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 0.25, label: '0.25' },
                      { value: 0.5, label: '0.5' },
                      { value: 0.75, label: '0.75' },
                      { value: 1, label: '1' },
                    ]}
                    sx={{
                      width: '100%',
                      '& .MuiSlider-track': {
                        height: 6,
                        marginTop: '-4px',
                      },
                      '& .MuiSlider-rail': {
                        height: 6,
                        marginTop: '-4px',
                      },
                      '& .MuiSlider-thumb': {
                        width: 14,
                        height: 14,
                        marginTop: '-5px',
                        border: '2px solid white', // optional - better look ke liye
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      },
                      '& .MuiSlider-markLabel': {
                        fontSize: '0.65rem',
                        top: 20,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Divider />

            <Box
              ref={retryMatchResultRef}
              sx={{
                minHeight: (retryMatchResultLoading || retryMatchResultData)
                  ? { xs: 320, sm: 340 }
                  : 'auto',
              }}
            >
              {retryMatchResultLoading ? (
                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Matched Result</Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
                      gap: { xs: 2, sm: 3 },
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">Audit Capture</Typography>
                      <Skeleton variant="rounded" sx={{ mt: 0.5, width: '100%', aspectRatio: '1 / 1', borderRadius: 1 }} />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        fontSize: { xs: '1.4rem', sm: '1.8rem' },
                        fontWeight: 700,
                        transform: { xs: 'rotate(90deg)', sm: 'none' },
                      }}
                    >
                      →
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Matched Employee Profile</Typography>
                      <Skeleton variant="rounded" sx={{ mt: 0.5, width: '100%', aspectRatio: '1 / 1', borderRadius: 1 }} />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                    <Skeleton variant="text" width="88%" />
                    <Skeleton variant="text" width="72%" />
                    <Skeleton variant="text" width="64%" />
                    <Skeleton variant="text" width="70%" />
                  </Box>
                  <Stack spacing={1.5} sx={{ py: 0.5, alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={26} />
                    <Typography variant="body2" color="text.secondary">Preparing match result...</Typography>
                  </Stack>
                </Stack>
              ) : retryMatchResultData ? (
                (() => {
                  const rawScore = Number(retryMatchResultData?.result?.confidence_score);
                  const rawThreshold = Number(retryMatchResultData?.threshold);
                  const score = Number.isFinite(rawScore) ? Math.max(0, Math.min(1, rawScore)) : 0;
                  const threshold = Number.isFinite(rawThreshold) ? Math.max(0, Math.min(1, rawThreshold)) : 0;
                  return (
                    <Stack spacing={2}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Matched Result</Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
                          gap: { xs: 2, sm: 3 },
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">Audit Capture</Typography>
                          <Box sx={{ mt: 0.5, borderRadius: 1, overflow: 'hidden', bgcolor: '#f5f5f5', aspectRatio: '1 / 1' }}>
                            <Box
                              component="img"
                              src={retryMatchResultData?.auditImage || ANONYMOUS_IMAGE}
                              alt="Audit capture"
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'text.secondary',
                            fontSize: { xs: '1.4rem', sm: '1.8rem' },
                            fontWeight: 700,
                            transform: { xs: 'rotate(90deg)', sm: 'none' },
                          }}
                        >
                          →
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Matched Employee Profile</Typography>
                          <Box sx={{ mt: 0.5, borderRadius: 1, overflow: 'hidden', bgcolor: '#f5f5f5', aspectRatio: '1 / 1' }}>
                            <Box
                              component="img"
                              src={retryMatchResultData?.employeeImage || ANONYMOUS_IMAGE}
                              alt="Employee profile"
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                        <Typography variant="body2"><strong>Name:</strong> {retryMatchResultData?.result?.recognized_name || 'Unknown'}</Typography>
                        <Typography variant="body2"><strong>Person ID:</strong> {retryMatchResultData?.result?.recognized_person_id || '-'}</Typography>
                        <Typography variant="body2"><strong>Score:</strong> {score.toFixed(2)}</Typography>
                        <Typography variant="body2"><strong>Threshold:</strong> {threshold.toFixed(2)}</Typography>
                      </Box>
                    </Stack>
                  );
                })()
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ py: 1 }}>
                  Match result will appear here after clicking Match.
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={handleRetryMatchDialogClose}
            disabled={retryMatchAttendanceSubmitting || retryMatchResultLoading}
          >
            Close
          </Button>
          <Button
            onClick={handleRetryMatchContinue}
            disabled={retryMatchAttendanceSubmitting || retryMatchResultLoading}
          >
            {retryMatchResultData ? 'Rematch' : 'Match'}
          </Button>
          <Button
            variant="contained"
            onClick={handleRetryMatchApply}
            disabled={!retryMatchResultData || retryMatchAttendanceSubmitting || retryMatchResultLoading}
          >
            {retryMatchAttendanceSubmitting ? 'Updating…' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

const getResultMeta = (log) => {
  if (!log) {
    return { label: 'Unknown', color: 'default' };
  }
  if (log.success) {
    return { label: 'Matched', color: 'success' };
  }
  if (log.confidence_level === 'possible_match') {
    return { label: 'Possible Match', color: 'warning' };
  }
  if (log.confidence_level === 'no_match') {
    return { label: 'Unmatched', color: 'error' };
  }
  return { label: 'Failed', color: 'error' };
};

// Helper function to check if Train button should be shown
// Only show for unmatched and failed recognition
const shouldShowTrainButton = (log) => {
  if (!log) return false;
  // If already trained, don't show train button (show Match button instead)
  if (log.is_trained) return false;
  // Show train button if not successful (either unmatched or failed)
  return !log.success;
};

const AuditStackLayerImage = ({ auditId, enabled, alt, sx }) => {
  const { url } = useAuditImage(auditId, { enabled: enabled && Boolean(auditId) });
  const src = url === undefined ? ANONYMOUS_IMAGE : (url || ANONYMOUS_IMAGE);
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={sx}
    />
  );
};

const AuditTile = ({ log, image, capturedTs, onClick, onTrainClick, onMatchClick, onRetryMatchClick, onPresentClick, presentLoading, onBucketManualMarkClick, bucketManualMarkLoading = false, stackImages = [], bucketLogs = [], onCheckboxChange, isChecked, selectionVariant = 'checkbox', onPossibleMatchClick, onSearchClick, findPersonResultMode = false, showVisionReviewUI = false, onRevokeClick, revokeLoading = false, disableRecordingPlayback = false, timeOnly = false, disableEmployeeLink = false, hidePersonName = false, hideTrackId = false, hideFrameBadge = false, hideIntrusionBadge = false, statusLabel = null, statusChipSx = null, lazyLoad = false }) => {
  const resultMeta = getResultMeta(log);
  const showConfidenceScore = !!log?.confidence_score
    && Boolean(log?.recognized_person_id)
    && !['no_match', 'no_face', 'error'].includes(String(log?.confidence_level || '').toLowerCase());
  const displayStatusLabel = statusLabel || resultMeta.label;
  const usesCustomStatusChip = Boolean(statusLabel || statusChipSx);
  const cameraLabel = (log.camera || '').toUpperCase() || '—';
  const physicalCameraLabel = getPhysicalCameraLabel(log);
  const hasInOutDirection = cameraLabel === 'IN' || cameraLabel === 'OUT';
  const directionChipLabel = physicalCameraLabel
    ? (hasInOutDirection ? `${cameraLabel}: ${physicalCameraLabel}` : physicalCameraLabel)
    : cameraLabel;
  const formattedTime = (() => {
    if (!capturedTs?.iso) return '-';
    if (!timeOnly) return formatDateWithSeconds(capturedTs.iso);
    const rawIso = String(capturedTs.iso).trim();
    const hasTZ = /([+-]\d{2}:?\d{2}|Z)$/i.test(rawIso);
    const dt = new Date(hasTZ ? rawIso : `${rawIso}Z`);
    if (Number.isNaN(dt.getTime())) return '-';
    return format(dt, 'hh:mm a');
  })();
  const cameraMode = (log.camera || '').toString().toLowerCase();
  const isValidityExpired = String(log?.validity_status || '').toLowerCase() === 'expired';
  const presentAction = cameraMode === 'out' ? 'check_out' : 'check_in';
  const presentLabel = presentAction === 'check_out' ? 'Check-Out' : 'Check-In';
  const borderColor = isValidityExpired
    ? '#d32f2f'
    : cameraMode === 'in'
      ? '#16a34a'
      : cameraMode === 'out'
        ? '#facc15'
        : 'transparent';
  const stackLayers = (stackImages || []).slice(0, MAX_STACK_PREVIEW);
  const showStack = stackLayers.length > 1;
  const stackRenderOrder = showStack ? [...stackLayers].reverse() : stackLayers;
  const totalCards = Math.max(1, log.stack_size || 1);
  const visibleCards = Math.max(1, stackLayers.length);
  const frameBadgeLabel =
    totalCards <= visibleCards ? `${totalCards}` : `${visibleCards}+`;
  const hasTrainOrMatchButton = ((log.is_trained && !log.success) || onTrainClick);
  // Show chip if any log in this bucket has send_attendance marked true.
  const hasBucketAttendance =
    bucketLogs.some((bucketLog) => isAttendanceSent(bucketLog?.send_attendance)) ||
    isAttendanceSent(log?.send_attendance);

  const bucketAttendanceRef = resolveBucketAttendanceRef(log, bucketLogs);
  const isAttendanceRevoked = bucketAttendanceRef.revoked;
  const revokedByName = [log, ...(Array.isArray(bucketLogs) ? bucketLogs : [])]
    .filter(Boolean)
    .map((item) => getAttendanceRefFromLog(item))
    .find((ref) => ref.revoked)?.revokedByName ?? null;

  const recordingReadyFromApi =
    isAuditRecordingReady(log) ||
    (Array.isArray(bucketLogs) && bucketLogs.some((b) => isAuditRecordingReady(b)));
  const showRecordingPlay =
    Boolean(log?.audit_id) && !disableRecordingPlayback && recordingReadyFromApi;
  const handleTileClick = useCallback(
    (e) => {
      if (isRecordingPlayInteraction(e)) {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        return;
      }
      onClick?.(e);
    },
    [onClick]
  );

  const useLazyImage = lazyLoad && image === undefined && Boolean(log?.audit_id);
  const { ref: lazyImageRef, url: lazyUrl, inView: lazyInView } = useAuditImage(log?.audit_id, {
    enabled: useLazyImage,
  });
  const displayImage = useLazyImage ? lazyUrl : image;
  const stackLoadEnabled = lazyLoad ? lazyInView : true;

  return (
    <>
      <Card
        sx={{
          width: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          cursor: 'pointer',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: isAttendanceRevoked ? 'rgba(71, 85, 105, 0.75)' : 'rgba(0, 0, 0, 0.61)',
          color: '#fff',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.29)',
          borderTop: `3px solid ${borderColor}`,
          opacity: isAttendanceRevoked ? 0.72 : 1,
          filter: isAttendanceRevoked ? 'grayscale(0.85)' : 'none',
          '&:hover .train-button': {

            opacity: 1,
          },
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(15,23,42,0.2)',
            transform: 'translateY(-2px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        }}
        onClick={handleTileClick}
      >
        {/* Image - Square aspect ratio */}
        <Box
          ref={useLazyImage ? lazyImageRef : undefined}
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: '100%', // 1:1 aspect ratio (square)
            background: '#0f172a',
            overflow: 'hidden',
          }}
        >
          {displayImage === undefined ? (
            <Box
              component="img"
              src={ANONYMOUS_IMAGE}
              alt="Loading..."
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0.7,
              }}
            />
          ) : showStack ? (
            <Box sx={{ position: 'absolute', inset: 0 }}>
              <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                {stackRenderOrder.map((layer, idx) => {
                  const depth = stackRenderOrder.length - 1 - idx;
                  const offset = depth * 10;
                  const layerImage = lazyLoad ? undefined : layer.image;
                  const layerKey = layer.log.audit_id
                    ? `${layer.log.audit_id}-${idx}`
                    : `${layer.log.track_id || 'track'}-${idx}`;
                  return (
                    <Box
                      key={layerKey}
                      sx={{
                        position: 'absolute',
                        top: offset,
                        left: offset,
                        right: offset,
                        bottom: offset,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.08)',
                        boxShadow: '0 10px 30px rgba(15,23,42,0.55)',
                        background: '#0f172a',
                        zIndex: idx + 1,
                      }}
                    >
                      {lazyLoad && layer.log.audit_id ? (
                        <AuditStackLayerImage
                          auditId={layer.log.audit_id}
                          enabled={stackLoadEnabled}
                          alt={layer.log.recognized_name || 'Recognition frame'}
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <Box
                          component="img"
                          src={layerImage || ANONYMOUS_IMAGE}
                          alt={layer.log.recognized_name || 'Recognition frame'}
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : (
            <Box
              component="img"
              src={displayImage || ANONYMOUS_IMAGE}
              alt={log.recognized_name || 'Recognition frame'}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          )}

          <Box
            sx={{
              position: 'absolute',
              top: 0,
              zIndex: 21,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
              width: '100%',
              padding: '8px'
            }}
          >
            <Box >
              {onCheckboxChange && (
                <Box
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.62)',
                    borderRadius: 4,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    transform: 'scale(1.1)',
                    height: 32,
                    width: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '8px'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {selectionVariant === 'radio' ? (
                    <Radio
                      checked={isChecked || false}
                      onChange={(e) => {
                        e.stopPropagation();
                        onCheckboxChange(log.audit_id, e.target.checked);
                      }}
                      sx={{
                        color: '#fff',
                        '&.Mui-checked': {
                          color: '#40518A',
                        },
                        padding: 0,
                        '& .MuiSvgIcon-root': {
                          fontSize: 20,
                        },
                      }}
                    />
                  ) : (
                    <Checkbox
                      checked={isChecked || false}
                      onChange={(e) => {
                        e.stopPropagation();
                        onCheckboxChange(log.audit_id, e.target.checked);
                      }}
                      sx={{
                        color: '#fff',
                        '&.Mui-checked': {
                          color: '#40518A',
                        },
                        padding: 0,
                        '& .MuiSvgIcon-root': {
                          fontSize: 20,
                        },
                      }}
                    />
                  )}
                </Box>
              )}
              {(onPresentClick || onBucketManualMarkClick) && (
                <Box
                  sx={{
                    zIndex: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  {/* Login / Logout */}
                  {onPresentClick && !findPersonResultMode && (
                    <Tooltip title={presentLabel} arrow placement="bottom">
                      <IconButton
                        size="small"
                        disabled={presentLoading || bucketManualMarkLoading}
                        sx={{
                          bgcolor: 'rgba(229, 229, 229, 0.95)',
                          color: presentAction === 'check_out' ? '#f59e0b' : '#10b981',
                          width: 32,
                          height: 32,
                          transform: 'scale(1.1)',
                          borderRadius: 4,
                          '&:hover': {
                            bgcolor: 'rgba(229, 229, 229, 0.95)',
                            transform: 'scale(1.1)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            color: presentAction === 'check_out' ? '#d97706' : '#059669',
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onPresentClick(e);
                        }}
                      >
                        {presentAction === 'check_out' ? <Logout sx={{ fontSize: 16 }} /> : <Login sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>
                  )}
                  {onBucketManualMarkClick && (
                    <Tooltip title="Mark attendance manually" arrow placement="bottom">
                      <IconButton
                        size="small"
                        disabled={presentLoading || bucketManualMarkLoading}
                        sx={{
                          bgcolor: 'rgba(229, 229, 229, 0.95)',
                          color: '#40518A',
                          width: 32,
                          height: 32,
                          transform: 'scale(1.1)',
                          borderRadius: 4,
                          '&:hover': {
                            bgcolor: 'rgba(229, 229, 229, 0.95)',
                            transform: 'scale(1.1)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            color: '#2f3d6b',
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onBucketManualMarkClick(e);
                        }}
                      >
                        {bucketManualMarkLoading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <HowToRegIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Box>

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>

              <AuditRecordingPlayButton
                log={log}
                enabled={true}
                placement="bottom"
              />

              {/* Train / Match Button */}
              {hasTrainOrMatchButton && (
                <Box
                  sx={{
                    display: 'flex',
                    height: 32,
                    alignItems: 'center',
                  }}
                >
                  {log.is_trained && !log.success ? (
                    <Tooltip title="Match" arrow placement="bottom">
                      <IconButton
                        size="small"
                        disabled={presentLoading}
                        sx={{
                          bgcolor: 'rgba(229, 229, 229, 0.95)',
                          color: "primary",
                          width: 32,
                          height: 32,
                          transform: 'scale(1.1)',
                          borderRadius: 4,
                          '&:hover': {
                            bgcolor: 'rgba(229, 229, 229, 0.95)',
                            transform: 'scale(1.1)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            color: "primary"
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onMatchClick) onMatchClick(e);
                        }}
                      >
                        {<RefreshIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Train" arrow placement="bottom">
                      <IconButton
                        size="small"
                        disabled={presentLoading}
                        sx={{
                          bgcolor: 'rgba(229, 229, 229, 0.95)',
                          color: 'rgba(230, 90, 90, 0.95)',
                          width: 32,
                          height: 32,
                          transform: 'scale(1.1)',
                          borderRadius: 4,
                          '&:hover': {
                            bgcolor: 'rgba(229, 229, 229, 0.95)',
                            transform: 'scale(1.1)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            color: 'rgba(218, 90, 90, 0.95)'
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onTrainClick) onTrainClick(e);
                        }}
                      >
                        {<Psychology sx={{ fontSize: 20 }} />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}

              <CircularProgressIcon enable={showConfidenceScore} confidence_score={log?.confidence_score} />

            </Box>

          </Box>

          {/* Tags bottom-left */}
          <Stack
            direction="row"
            spacing={0.5}
            flexWrap='wrap'
            useFlexGap
            sx={{
              position: 'absolute',
              bottom: 4,
              left: 4,
              right: 4,
              zIndex: 10,
              rowGap: '0.3rem',
            }}
          >
            <Tooltip title={directionChipLabel} arrow placement="top">
              <Chip
                size="small"
                label={directionChipLabel}
                variant="filled"
                sx={{
                  fontSize: { xs: '0.6rem', sm: '0.65rem' },
                  height: { xs: 18, sm: 20 },
                  maxWidth: physicalCameraLabel ? 180 : undefined,
                  bgcolor:
                    cameraLabel === 'IN'
                      ? '#dcfce7'   // light green
                      : cameraLabel === 'OUT'
                        ? '#fee2e2'   // light red
                        : '#e5e7eb',  // fallback gray
                  color:
                    cameraLabel === 'IN'
                      ? '#166534'
                      : cameraLabel === 'OUT'
                        ? '#991b1b'
                        : '#374151',
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  '& .MuiChip-label': {
                    px: 0.75,
                    ...(physicalCameraLabel ? {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'block',
                    } : {}),
                  },
                }}
              />
            </Tooltip>
            <Chip
              size="small"
              label={displayStatusLabel}
              color={
                usesCustomStatusChip
                  ? 'default'
                  : (resultMeta.color === 'default' ? 'default' : resultMeta.color)
              }
              variant={
                usesCustomStatusChip
                  ? 'filled'
                  : (resultMeta.color === 'default' ? 'outlined' : 'filled')
              }
              onClick={onPossibleMatchClick && resultMeta.label === 'Possible Match' ? (e) => {
                e.stopPropagation();
                onPossibleMatchClick();
              } : undefined}
              sx={{
                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                height: { xs: 18, sm: 20 },
                '& .MuiChip-label': { px: 0.75 },
                cursor: onPossibleMatchClick && resultMeta.label === 'Possible Match' ? 'pointer' : 'default',
                ...(statusChipSx || {}),
              }}
            />
            {Boolean(log?.is_violation) && (
              <Tooltip title="Violation" arrow placement="top">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 18, sm: 20 },
                    height: { xs: 18, sm: 20 },
                    borderRadius: '4px',
                    bgcolor: '#fee2e2',
                    color: '#b91c1c',
                    p: 0.25,
                    cursor: 'pointer',
                  }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                </Box>
              </Tooltip>
            )}
            {/* Search Icon for All Logs - before Track ID (hidden when Find Person results are displayed) */}
            {onSearchClick && !findPersonResultMode && (
              <Tooltip title="Find Person based on this Image" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSearchClick(log, image);
                  }}
                  sx={{
                    backgroundColor: 'rgba(64, 81, 138, 0.9)',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(64, 81, 138, 1)',
                    },
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    padding: 0,
                  }}
                >
                  <Search sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
            {hasBucketAttendance && (
              <Chip size="small" label="Attendance" color="success" variant="filled" sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' }, height: { xs: 18, sm: 20 } }} />
            )}
            {isValidityExpired && (
              <Chip size="small" label="Validity Expired" color="error" variant="filled" sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' }, height: { xs: 18, sm: 20 } }} />
            )}
            {log.is_intrusion && !hideIntrusionBadge && (
              <Tooltip
                title="Intrusion"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#374151', // Dark gray background
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      padding: '6px 12px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    }
                  },
                  arrow: {
                    sx: {
                      color: '#374151', // Dark gray arrow to match background
                    }
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 18, sm: 20 },
                    height: { xs: 18, sm: 20 },
                    borderRadius: '4px',
                    bgcolor: 'warning.main', // Warning color from theme
                    color: 'white',
                    p: 0.25,
                    cursor: 'pointer',
                  }}
                >
                  <BlockIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                </Box>
              </Tooltip>
            )}
            {logShowsVendorBadge(log, bucketLogs) && (
              <Tooltip title="Vendor" arrow placement="top">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 18, sm: 20 },
                    height: { xs: 18, sm: 20 },
                    borderRadius: '4px',
                    bgcolor: 'success.main',
                    color: 'white',
                    p: 0.25,
                  }}
                >
                  <AssignmentIndIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                </Box>
              </Tooltip>
            )}
            {logShowsVisitorBadge(log, bucketLogs) && (
              <Tooltip title="Visitor" arrow placement="top">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 18, sm: 20 },
                    height: { xs: 18, sm: 20 },
                    borderRadius: '4px',
                    bgcolor: 'success.main',
                    color: 'white',
                    p: 0.25,
                  }}
                >
                  <BadgeIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                </Box>
              </Tooltip>
            )}
          </Stack>
        </Box>

        {/* Bottom info bar */}
        <Box
          sx={{
            px: 1.5,
            py: 1,
            color: 'white',
            overflow: 'hidden',
          }}
        >
          {(!hidePersonName || !hideFrameBadge) && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.15 }}>
              {!hidePersonName ? (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.8rem', sm: '0.85rem' },
                    lineHeight: 1.3,
                  }}
                >
                  <EmployeeNameHyperlink
                    text={log.recognized_name || 'Unknown person'}
                    employees={[buildPersonLinkMeta(log, { routeSource: 'event' })]}
                    disabled={disableEmployeeLink || Boolean(log?.is_intrusion)}
                    style={{ display: 'inline' }}
                  />
                </Typography>
              ) : (
                <Box sx={{ flex: 1, minWidth: 0 }} />
              )}
              {!hideFrameBadge && (
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    border: '1px solid rgba(255,255,255,0.4)',
                    background: 'rgba(15, 23, 42, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                    {frameBadgeLabel}
                  </Typography>
                </Box>
              )}

            </Box>
          )}
          {getAttendanceMarkedByLabel(log) && (
            <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }} noWrap>
              {getAttendanceMarkedByLabel(log)}
            </Typography>
          )}
          {log.manual_attendance?.remark && (
            <Typography variant="caption" sx={{ opacity: 0.85, fontStyle: 'italic', display: 'block' }} noWrap>
              {`Remark: ${log.manual_attendance.remark}`}
            </Typography>
          )}
          {isAttendanceRevoked && (
            <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', color: '#fde68a' }} noWrap>
              {getAttendanceRevokedReopenHint(log) || `Revoked${revokedByName ? ` by ${revokedByName}` : ''}`}
            </Typography>
          )}
          {showVisionReviewUI && getAiReviewChipLabel(log) && (
            <Chip
              size="small"
              icon={<SmartToy sx={{ fontSize: '0.85rem !important', color: '#fff !important' }} />}
              label={getAiReviewChipLabel(log)}
              color={getVisionReviewFromLog(log)?.error ? 'warning' : 'default'}
              sx={{
                mt: 0.5,
                height: 20,
                fontSize: '0.65rem',
                maxWidth: '100%',
                bgcolor: getVisionReviewFromLog(log)?.error ? undefined : 'rgba(64, 81, 138, 0.85)',
                color: getVisionReviewFromLog(log)?.error ? undefined : '#fff',
              }}
            />

          )}
          {showVisionReviewUI && (() => {
            const executor = getVisionReviewExecutorInfo(log);
            if (!executor) return null;
            return (
              <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.1 }}>
                <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>
                  {`Triggered By: ${executor.triggeredByUserName}`}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
                  {`Mode: ${executor.automationModeLabel}`}
                </Typography>
              </Box>
            );
          })()}
          {log.is_trained && log.trained_by_name && (
            <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
              {`trained by ${log.trained_by_name}`}
            </Typography>
          )}
          {log.confidence_level === 'possible_match' &&
            !hasBucketAttendance &&
            !hasAttendanceMarkedBy(log) && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 600, display: 'block', mb: 0.25 }}>
                  Top match:
                </Typography>
                {Array.isArray(log.request_metadata?.top_candidates) && log.request_metadata.top_candidates.length > 0 ? (
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.85,
                      fontSize: { xs: '0.6rem', sm: '0.65rem' },
                      display: 'block',
                      lineHeight: 1.4,
                    }}
                    noWrap
                  >
                    {log.request_metadata.top_candidates[0].name || 'Unknown'} ({Number(log.request_metadata.top_candidates[0].score).toFixed(2)})
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ opacity: 0.75, fontSize: '0.65rem' }}>—</Typography>
                )}
              </Box>
            )}
          {log.unmatch_to_match_by && (
            <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
              {`Rematched by ${log.unmatch_to_match_by}`}
            </Typography>
          )}
          {!hideTrackId && log.track_id != null && (
            <Typography
              variant="caption"
              sx={{
                color: '#fff',
                fontWeight: 600,
                fontSize: { xs: '0.55rem', sm: '0.6rem' },
                display: 'block',
              }}
              noWrap
            >
              {`Track: ${log.track_id}`}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{
              opacity: 0.8,
              fontSize: { xs: '0.65rem', sm: '0.7rem' },
              display: 'block',
            }}
            noWrap
          >
            {formattedTime}
          </Typography>
        </Box>
      </Card>
    </>
  );
};
const formatShortDateLabel = (iso) => {
  if (!iso) return 'No activity';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Invalid date';
  return date.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const formatEventTime = (iso) => {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatEventDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

// Tooltip that only shows when text is truncated with ellipsis
const EllipsisTooltip = ({ text, children }) => {
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const element = textRef.current.querySelector('*') || textRef.current;
        setIsOverflowing(element.scrollWidth > element.clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [text]);

  return (
    <Tooltip title={isOverflowing ? text : ''} arrow placement="top" disableHoverListener={!isOverflowing}>
      <Box ref={textRef} sx={{ minWidth: 0 }}>
        {children}
      </Box>
    </Tooltip>
  );
};

export { AttendanceGridLayout, AuditTile, getResultMeta };

export default Audit;
